/**
 * Landing page configs.
 *
 * Each entry powers one /<slug> route. Every page renders the same
 * LandingPage template with the data below. Content is buyer-focused,
 * fact-based, and references only credentials we actually hold (FSSAI,
 * APEDA, Spices Board, ISO 22000, IEC). No fake awards, no
 * unverified rankings.
 *
 * Add a new page: drop a new entry, register a route in App.js, add
 * the URL to sitemap.xml and the build-seo-html.js script.
 */

const SITE = "https://www.viloraimpex.com";
const ORG_REF = `${SITE}/#organization`;

/** Common process steps reused across all pages (slight rewording per page). */
const PROCESS_DEFAULT = [
  {
    label: "Inquiry",
    body: "You send the spec, destination port, packing and quantity. We answer the same day in our timezone with price and timeline.",
  },
  {
    label: "Sample",
    body: "Sortex-cleaned, lab-tested samples couriered. Your approval is locked before any commit.",
  },
  {
    label: "Origin loading",
    body: "Hand-graded at the warehouse. Photographed, weighed, sealed before stuffing at Mundra or Kandla.",
  },
  {
    label: "Documentation",
    body: "Phyto, FSSAI, APEDA, COO, Bill of Lading — couriered within 48 hours of sail.",
  },
  {
    label: "Delivery",
    body: "Live-tracked to port of arrival. We stay on the line until the buyer signs off.",
  },
];

/** Trust points common to every page. */
const WHY_DEFAULT = [
  "FSSAI licensed, APEDA registered, Spices Board of India accredited, ISO 22000",
  "Hand-graded at origin; sortex-cleaned to buyer specification",
  "Documentation packet (phyto, FSSAI, COO, BL) within 48 hours of sail",
  "Live container tracking to port of arrival — every shipment, every time",
  "Direct from origin desks in Gujarat, Punjab, Andhra Pradesh, Maharashtra, Karnataka",
  "Payment terms supported: 30% advance + 70% against BL scan, or irrevocable L/C at sight",
];

const PORTS_DEFAULT = "Mundra and Kandla (Gujarat) loading; 12+ destination ports across the Gulf, Middle East, East Africa, Maldives and Southeast Asia (Jebel Ali, Dammam, Doha, Salalah, Sohar, Aqaba, Beirut, Mombasa, Dar es Salaam, Malé, Singapore, Port Klang).";

/** Countries served — used by enriched Service.areaServed schema. */
const AREA_SERVED = [
  "United Arab Emirates", "Saudi Arabia", "Qatar", "Oman", "Kuwait",
  "Bahrain", "Jordan", "Lebanon", "Egypt", "Kenya", "Tanzania",
  "Maldives", "Singapore", "Malaysia", "Indonesia",
];

const FOUNDER_REF = `${SITE}/founder/darshit-anadkat#person`;

const LANDINGS = {
  "rice-exporter": {
    slug: "rice-exporter-from-india",
    folio: "№ 07",
    sectionLabel: "RICE EXPORT",
    seoTitle: "Rice Exporter from India | Basmati & Non-Basmati",
    seoDescription:
      "Vilora Impex exports basmati, non-basmati, parboiled and sortex-cleaned rice from Gujarat with FSSAI/APEDA documentation.",
    h1: "Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Long-grain Indian rice for bulk export from Vilora Impex",
    eyebrow: "Bulk rice supply for global buyers",
    standfirst:
      "Vilora Impex is an Indian trading house and a registered rice exporter from India. We supply premium long-grain basmati, non-basmati, sortex-cleaned and parboiled rice in bulk to wholesale buyers, importers and distributors across the Gulf, Middle East, East Africa and Southeast Asia.",
    introH2: "Bulk rice supply for global buyers",
    introBody:
      "Our rice desk sources directly from milling partners in Punjab, Andhra Pradesh and Gujarat. Every lot is hand-graded, sortex-cleaned to your specification (5%–25% broken), and packed to buyer choice — 5/10/25/50 kg PP, BOPP, jute or private-label sacks. Shipments stuff at Mundra and Kandla and depart with full FSSAI, APEDA, phytosanitary and certificate-of-origin paperwork within 48 hours of sail.",
    productsH2: "Types of rice we export",
    products: [
      { name: "1121 Basmati Rice", spec: "Sella (parboiled), Steam, Raw • long grain 8.30 mm+ • aged 12 months min" },
      { name: "1509 Basmati Rice", spec: "Short-duration premium basmati • retail and HoReCa grade" },
      { name: "Sona Masuri Rice", spec: "Medium-grain non-basmati • low starch • popular in South Asia + Gulf" },
      { name: "IR64 Rice", spec: "Long-grain non-basmati • parboiled or raw • 5%–25% broken to buyer spec" },
      { name: "Idli Rice (parboiled)", spec: "Short-grain, fermentation-grade staple" },
      { name: "Pusa Basmati", spec: "Long-grain aromatic basmati • available on contract" },
    ],
    packagingH2: "Packaging, MOQ and shipment",
    packagingBody:
      "Standard MOQ is one 20ft FCL container — approximately 25 metric tonnes of rice. We accept LCL consolidated shipments on a case-by-case basis for new-buyer trial orders. Packaging options include 5 kg, 10 kg, 25 kg and 50 kg in PP, BOPP printed, jute, or buyer-branded private label. Incoterms supported: FOB Mundra/Kandla, CIF and CFR.",
    portsH2: "Ports and destinations",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why choose Vilora Impex",
    why: WHY_DEFAULT,
    faqsH2: "Rice export FAQs",
    faqs: [
      {
        q: "Do you export rice from India?",
        a: "Yes. Vilora Impex is a registered Indian rice exporter (IEC code from DGFT, FSSAI licence, APEDA registration) based in Rajkot, Gujarat. We ship rice from Mundra and Kandla ports to buyers across the Gulf, Middle East, East Africa, Maldives and Southeast Asia.",
      },
      {
        q: "What types of rice do you supply for export?",
        a: "1121 Basmati (Sella, Steam, Raw), 1509 Basmati, Pusa Basmati, Sona Masuri, IR64 (parboiled and raw) and idli rice. All lots are sortex-cleaned and graded 5%–25% broken to buyer specification.",
      },
      {
        q: "What is the minimum order quantity for rice export?",
        a: "Standard MOQ is one 20ft FCL container — approximately 25 metric tonnes. We accept LCL consolidated shipments for new-buyer trial orders.",
      },
      {
        q: "Do you provide private-label rice packaging?",
        a: "Yes. We offer private-label and contract-manufacturing packing in 5/10/25/50 kg PP, BOPP printed, jute and retail cartons. Artwork can be supplied by the buyer or co-developed with our packaging desk.",
      },
      {
        q: "Which countries do you export rice to?",
        a: "12+ destination ports — UAE (Jebel Ali), Saudi Arabia (Dammam), Qatar (Doha), Oman (Salalah, Sohar), Kuwait, Bahrain, Jordan (Aqaba), Lebanon (Beirut), Egypt, Kenya (Mombasa), Tanzania (Dar es Salaam), Maldives (Malé), Singapore, Malaysia (Port Klang).",
      },
      {
        q: "Do you handle export documentation?",
        a: "Yes. Every shipment ships with phytosanitary certificate, FSSAI, APEDA, certificate of origin, Bill of Lading and lab/COA — couriered within 48 hours of sail. Buyers receive live container tracking until sign-off at port of arrival.",
      },
    ],
    related: ["basmati", "non-basmati", "indian-agro"],
    productSchemaName: "Indian Rice for Export",
    productSchemaDescription:
      "Premium long-grain basmati, non-basmati and parboiled rice from Gujarat, India. FSSAI and APEDA registered exporter.",
  },

  basmati: {
    slug: "basmati-rice-exporter-from-india",
    folio: "№ 07·A",
    sectionLabel: "BASMATI EXPORT",
    seoTitle: "Basmati Rice Exporter from India | 1121 Sella, Steam & Raw",
    seoDescription:
      "1121, 1509 and Pusa basmati rice from India: Sella, Steam and Raw, sortex-cleaned, aged and packed to buyer specification.",
    h1: "Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Premium long-grain basmati rice exporter from India",
    eyebrow: "1121 · 1509 · Pusa · Sella · Steam · Raw",
    standfirst:
      "Vilora Impex supplies premium Indian basmati rice — 1121, 1509 and Pusa varieties — in Sella (parboiled), Steam and Raw grades. Every lot is long-grain (8.30 mm+), aged a minimum of 12 months, and sortex-cleaned to international buyer specification.",
    introH2: "Premium basmati for international buyers",
    introBody:
      "Basmati is our flagship division. We work directly with mills across Haryana, Punjab and Uttar Pradesh — the traditional basmati belt — and consolidate at our Gujarat desk for export. Buyers in the Gulf, the Middle East, and increasingly Africa rely on us for consistent grain length, low broken percentage, controlled moisture, and on-time documentation.",
    productsH2: "Basmati varieties we export",
    products: [
      { name: "1121 Basmati — Sella (Golden Parboiled)", spec: "Average grain length 8.40 mm+ • moisture 12% max" },
      { name: "1121 Basmati — Steam", spec: "Steam-processed for translucent appearance and longer shelf life" },
      { name: "1121 Basmati — Raw", spec: "Premium raw basmati • aged minimum 12 months" },
      { name: "1509 Basmati", spec: "Short-duration premium basmati • cost-effective retail and HoReCa grade" },
      { name: "Pusa Basmati", spec: "Long-grain aromatic basmati on contract" },
      { name: "Traditional Basmati", spec: "Heritage varieties on bulk contract" },
    ],
    packagingH2: "Packaging, MOQ and shipment",
    packagingBody:
      "MOQ: one 20ft FCL container (~25 MT). Packing: 5 / 10 / 25 / 50 kg PP, BOPP printed, jute, or buyer-branded private label. Broken percentage: 1% / 2% / 5% / 11% / 15% to buyer spec. Incoterms: FOB Mundra/Kandla, CIF, CFR. Payment: 30% advance + 70% against BL scan, or irrevocable L/C at sight.",
    portsH2: "Ports and destinations",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why source basmati from Vilora Impex",
    why: [
      "Consistent grain length 8.30 mm+ verified per lot",
      "Minimum 12 months aged — better aroma, lower breakage",
      "Sortex-cleaned to 1%–15% broken to your spec",
      "FSSAI, APEDA, Spices Board and ISO 22000",
      "Documentation packet within 48 hours of sail",
      "Live container tracking to port of arrival",
    ],
    faqsH2: "Basmati export FAQs",
    faqs: [
      {
        q: "Are you a 1121 basmati rice exporter from India?",
        a: "Yes. 1121 Basmati (Sella, Steam, Raw) is our largest basmati export volume. Average grain length 8.40 mm+, aged minimum 12 months, sortex-cleaned to 1%–15% broken on buyer specification.",
      },
      {
        q: "What is the difference between Sella, Steam and Raw basmati?",
        a: "Sella (golden parboiled) is steamed before milling, giving longer cooked-grain length and a golden colour. Steam is white-translucent. Raw is the natural milled grain — fluffiest cooked profile but most fragile in transit.",
      },
      {
        q: "What is your MOQ for basmati export?",
        a: "One 20ft FCL container (~25 MT). LCL accepted for trial orders.",
      },
      {
        q: "Do you do private-label basmati packing?",
        a: "Yes. 5/10/25/50 kg BOPP printed, jute, retail cartons. Artwork from buyer or co-developed.",
      },
      {
        q: "How is the basmati aged?",
        a: "Minimum 12 months aged in controlled silos at mill before milling and export. Aging improves aroma, reduces stickiness when cooked, and lowers breakage in transit.",
      },
      {
        q: "Which Indian ports does Vilora Impex ship basmati from?",
        a: "Mundra and Kandla ports in Gujarat. Both are deep-water container ports with direct service to Jebel Ali, Dammam, Doha, Salalah, Aqaba, Mombasa, Malé, Singapore and Port Klang.",
      },
    ],
    related: ["rice-exporter", "non-basmati", "indian-agro"],
    productSchemaName: "Indian Basmati Rice (1121, 1509, Pusa)",
    productSchemaDescription:
      "Premium Indian basmati rice for export — 1121, 1509 and Pusa varieties in Sella, Steam and Raw. Long-grain 8.30 mm+, aged 12 months minimum.",
  },

  "non-basmati": {
    slug: "non-basmati-rice-exporter-india",
    folio: "№ 07·B",
    sectionLabel: "NON-BASMATI EXPORT",
    seoTitle: "Non-Basmati Rice Exporter India | Sona Masuri & IR64",
    seoDescription:
      "Non-basmati rice exporter from India — Sona Masuri, IR64 (parboiled and raw), idli rice. Sortex-cleaned, packed 5–50 kg. FSSAI & APEDA registered. Bulk export.",
    h1: "Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Bulk Indian non-basmati rice supplier for export",
    eyebrow: "Sona Masuri · IR64 · Idli Rice · Parboiled · Raw",
    standfirst:
      "Vilora Impex exports the full range of Indian non-basmati rice — Sona Masuri, IR64 (parboiled and raw), idli rice and short-grain staples — in bulk to international wholesale buyers. Sortex-cleaned, graded to buyer specification, packed for the long haul.",
    introH2: "Non-basmati rice from India in bulk",
    introBody:
      "Non-basmati covers the majority of staple rice volumes shipped out of India. Our desk consolidates Sona Masuri from Andhra Pradesh and Karnataka, IR64 from Punjab and Andhra Pradesh, and idli rice from Tamil Nadu. We handle both parboiled and raw, in standard 5%–25% broken grades, with packing from 5 kg retail bags up to 50 kg jute sacks for institutional buyers.",
    productsH2: "Non-basmati varieties we export",
    products: [
      { name: "Sona Masuri Rice", spec: "Medium-grain, low starch • origin: Andhra Pradesh, Karnataka" },
      { name: "IR64 — Parboiled", spec: "Long-grain non-basmati • 5%–25% broken to buyer spec" },
      { name: "IR64 — Raw (White)", spec: "Long-grain non-basmati • polished, sortex-cleaned" },
      { name: "Idli Rice", spec: "Short-grain parboiled • fermentation staple" },
      { name: "PR11 / PR14 Rice", spec: "Long-grain non-basmati on contract" },
      { name: "Short-Grain Staple Rice", spec: "Various varieties on bulk contract" },
    ],
    packagingH2: "Packaging, MOQ and shipment",
    packagingBody:
      "MOQ: one 20ft FCL container (~25 MT). Packing options: 5/10/25/50 kg PP, BOPP printed, jute, or buyer-branded private label. Broken percentage 5%–25% to buyer spec. Sortex-cleaned. Incoterms: FOB Mundra/Kandla, CIF, CFR. Payment: 30% advance + 70% against BL, or irrevocable L/C at sight.",
    portsH2: "Ports and destinations",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why source non-basmati rice from Vilora Impex",
    why: [
      "Sortex-cleaned to buyer-specified broken percentage",
      "Direct from origin mills in Andhra Pradesh, Karnataka, Punjab and Tamil Nadu",
      "FSSAI, APEDA, ISO 22000 — registry numbers shared on signed request",
      "5/10/25/50 kg packing, jute or BOPP, private-label supported",
      "Documentation within 48 hours of sail",
      "Live container tracking to port of arrival",
    ],
    faqsH2: "Non-basmati rice export FAQs",
    faqs: [
      {
        q: "Are you a bulk non-basmati rice exporter from India?",
        a: "Yes. We export Sona Masuri, IR64 (parboiled and raw), idli rice and PR11/PR14 varieties in bulk to international wholesale buyers, with sortex cleaning and buyer-specified broken grading.",
      },
      {
        q: "What is the difference between parboiled IR64 and raw IR64?",
        a: "Parboiled IR64 is partially boiled in the husk, then dried and milled — this hardens the grain and improves shelf life and nutritional retention. Raw IR64 is the natural milled grain, polished and sortex-cleaned for retail and HoReCa.",
      },
      {
        q: "Can you supply Sona Masuri in bulk for retail packing?",
        a: "Yes. Sona Masuri is available 5/10/25 kg PP or BOPP printed for retail packing, and 25/50 kg jute for wholesale distributors. Private label supported.",
      },
      {
        q: "What is the broken percentage for non-basmati rice?",
        a: "Standard grades are 5%, 10%, 15% and 25% broken. We sortex-clean to your specification and verify on lab COA before shipment.",
      },
      {
        q: "What is the MOQ?",
        a: "One 20ft FCL container (~25 MT of rice). LCL accepted for trial orders.",
      },
      {
        q: "Where do you source non-basmati rice from?",
        a: "Sona Masuri from Andhra Pradesh and Karnataka; IR64 from Punjab and Andhra Pradesh; idli rice from Tamil Nadu. All consolidated at our Gujarat desk for export.",
      },
    ],
    related: ["rice-exporter", "basmati", "indian-agro"],
    productSchemaName: "Indian Non-Basmati Rice (Sona Masuri, IR64, Idli)",
    productSchemaDescription:
      "Indian non-basmati rice for export — Sona Masuri, IR64 parboiled and raw, idli rice. Sortex-cleaned, packed 5–50 kg.",
  },

  pulses: {
    slug: "pulses-supplier-from-india",
    folio: "№ 08",
    sectionLabel: "PULSES EXPORT",
    seoTitle: "Pulses Supplier from India | Toor, Chana, Moong Exporter",
    seoDescription:
      "Indian pulses supplier for toor dal, chana dal, moong dal, masoor and chickpeas. Machine-cleaned, calibrated and packed for bulk export.",
    h1: "Pulses Supplier from India",
    image: "/og.jpg",
    imageAlt: "Vilora Impex Indian pulses supplier and agro exporter",
    eyebrow: "Toor · Chana · Moong · Masoor · Chickpeas",
    standfirst:
      "Vilora Impex is an Indian pulses exporter supplying toor dal (arhar), chana dal, moong dal, masoor and chickpeas in bulk to wholesale buyers, importers and distributors. Machine-cleaned, calibrated, and packed for the long haul.",
    introH2: "Bulk Indian pulses for international buyers",
    introBody:
      "Pulses are a core part of our agro division. We source whole and split dals from Maharashtra, Madhya Pradesh and Karnataka, machine-clean to remove broken and stone, calibrate counts for retail readiness, and pack to buyer specification in 25/50 kg PP, BOPP or jute. Standard MOQ is one 20ft FCL container.",
    productsH2: "Pulses we export",
    products: [
      { name: "Toor Dal (Arhar)", spec: "Whole and split • machine-cleaned" },
      { name: "Chana Dal (Chickpea split)", spec: "Polished and unpolished • multiple counts" },
      { name: "Kabuli Chana / Chickpeas (Whole)", spec: "Counts 40/42, 44/46, 56/58, 60/62 per ounce" },
      { name: "Moong Dal (Green gram split)", spec: "Whole, split, polished" },
      { name: "Urad Dal (Black gram)", spec: "Whole, split, hulled" },
      { name: "Masoor Dal (Red lentil)", spec: "Whole, split, polished" },
    ],
    packagingH2: "Packaging, MOQ and shipment",
    packagingBody:
      "MOQ: one 20ft FCL container (approx 18–22 MT depending on pulse). Packing: 25/50 kg PP, BOPP printed, jute, or buyer-branded private label. Incoterms: FOB Mundra/Kandla, CIF, CFR. Payment: 30% advance + 70% against BL, or irrevocable L/C at sight.",
    portsH2: "Ports and destinations",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why source pulses from Vilora Impex",
    why: [
      "Direct from origin mills — Maharashtra, Madhya Pradesh, Karnataka",
      "Machine-cleaned and calibrated to buyer count specification",
      "FSSAI, APEDA, ISO 22000 — registry numbers on signed request",
      "Polished and unpolished grades available",
      "Documentation within 48 hours of sail",
      "Live container tracking to port of arrival",
    ],
    faqsH2: "Pulses export FAQs",
    faqs: [
      {
        q: "Are you an Indian pulses exporter?",
        a: "Yes. Vilora Impex exports toor (arhar), chana, moong, urad and masoor dals — whole and split — in bulk to international buyers. FSSAI, APEDA and ISO 22000 registered.",
      },
      {
        q: "What is the MOQ for pulses export?",
        a: "One 20ft FCL container (approximately 18–22 MT depending on density of the pulse).",
      },
      {
        q: "Do you supply whole chickpeas?",
        a: "Yes. Kabuli chana in counts 40/42, 44/46, 56/58 and 60/62 per ounce, machine-cleaned, packed 25 or 50 kg.",
      },
      {
        q: "Can you supply polished and unpolished dal?",
        a: "Yes. Both grades available for toor, chana, moong, urad and masoor on buyer specification.",
      },
      {
        q: "Do you offer private-label packing for pulses?",
        a: "Yes. 25 and 50 kg BOPP printed and jute sacks with buyer artwork.",
      },
      {
        q: "Which countries do you ship pulses to?",
        a: "Gulf, Middle East, East Africa, Maldives and Southeast Asia — 12+ destination ports including Jebel Ali, Dammam, Mombasa and Singapore.",
      },
    ],
    related: ["rice-exporter", "indian-agro", "spices"],
    productSchemaName: "Indian Pulses (Toor, Chana, Moong, Masoor)",
    productSchemaDescription:
      "Indian pulses for export — toor (arhar), chana, moong, urad, masoor and whole chickpeas. Machine-cleaned, calibrated counts.",
  },

  spices: {
    slug: "spices-exporter-from-india",
    folio: "№ 09",
    sectionLabel: "SPICES EXPORT",
    seoTitle: "Spices Exporter from India | Turmeric, Cumin, Chilli & More",
    seoDescription:
      "Indian spices exporter for turmeric, cumin, red chilli, coriander, cardamom and garam masala. Single-origin, tested and packed for bulk buyers.",
    h1: "Spices Exporter from India",
    image: "/hero-spices.jpg",
    imageAlt: "Indian spices including turmeric, cumin and red chilli for export",
    eyebrow: "Single-origin · Whole and ground · Steam-sterilised",
    standfirst:
      "Vilora Impex is an Indian spices exporter supplying single-origin whole and ground spices — turmeric, cumin, chilli, coriander, fennel, cardamom and bespoke masala blends — to wholesale buyers and distributors worldwide. Steam-sterilised, oil-content and ASTA-colour tested per lot.",
    introH2: "Bulk Indian spices for global buyers",
    introBody:
      "Our spice desk sits in Rajkot with direct sourcing across Gujarat, Andhra Pradesh, Tamil Nadu, Rajasthan, Karnataka and Madhya Pradesh. Every lot is cleaned, graded, oil-content and colour tested, and packed in vacuum-sealed liners or breathable jute. We hold Spices Board of India accreditation and operate under ISO 22000 food-safety standards.",
    productsH2: "Spices we export",
    products: [
      { name: "Turmeric (Haldi)", spec: "Whole and powder • curcumin 3% / 5% / 7%+ on contract" },
      { name: "Cumin Seeds (Jeera)", spec: "Singapore (99.5%) and Europe (99%) grades" },
      { name: "Red Chilli", spec: "Whole and ground • ASTA 90–220 colour units" },
      { name: "Coriander Seeds (Dhania)", spec: "Eagle / Parrot grade • whole and split" },
      { name: "Fennel (Saunf)", spec: "Europe Special, Singapore, Standard grades" },
      { name: "Cardamom (Elaichi)", spec: "Green and black • 6/7/8 mm bold" },
      { name: "Black Pepper", spec: "Tellicherry, Malabar Garbled, MG-1" },
      { name: "Garam Masala", spec: "Bespoke blends and standard formulas" },
    ],
    packagingH2: "Packaging, MOQ and shipment",
    packagingBody:
      "MOQ: one 20ft FCL container (typically 14–22 MT depending on the spice density). Packing options: 25/50 kg PP, jute or food-grade vacuum-sealed liners. Bulk drums, retail pouches and BOPP printed bags supported. Steam sterilisation, fumigation and aflatoxin testing on request. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Ports and destinations",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why source spices from Vilora Impex",
    why: [
      "Single-origin sourcing across six Indian states",
      "Spices Board of India accredited",
      "Steam-sterilised, oil-content and ASTA-colour tested per lot",
      "FSSAI, APEDA, ISO 22000",
      "Vacuum-sealed liners for aroma retention in transit",
      "Bespoke masala blends developed with buyer's R&D",
    ],
    faqsH2: "Spices export FAQs",
    faqs: [
      {
        q: "Are you an Indian spices exporter?",
        a: "Yes. Vilora Impex is Spices Board of India accredited and exports single-origin whole and ground spices — turmeric, cumin, chilli, coriander, fennel, cardamom, garam masala — to wholesale buyers worldwide.",
      },
      {
        q: "Do you supply organic spices?",
        a: "Organic-certified lots are available on contract through our Vilora Organic Essential mill. Certificates accompany the shipment.",
      },
      {
        q: "What is the MOQ for spice export?",
        a: "One 20ft FCL container (typically 14–22 MT depending on bulk density of the spice).",
      },
      {
        q: "Do you steam-sterilise spices?",
        a: "Yes. Steam sterilisation, fumigation and aflatoxin testing on request, with COA accompanying the shipment.",
      },
      {
        q: "Can you do bespoke masala blends?",
        a: "Yes. We develop garam masala and other blends with buyer's R&D team, with formulation locked and labelled by lot.",
      },
      {
        q: "How is colour and pungency tested for chilli and turmeric?",
        a: "Red chilli is ASTA-colour tested (90–220 units to buyer spec). Turmeric is curcumin-content tested (3% / 5% / 7%+ grades). Both reports accompany every shipment.",
      },
    ],
    related: ["dry-fruits", "indian-agro", "pulses"],
    productSchemaName: "Indian Spices (Turmeric, Cumin, Chilli, Coriander)",
    productSchemaDescription:
      "Single-origin Indian spices for export — turmeric, cumin, chilli, coriander, cardamom, fennel, black pepper, garam masala. Steam-sterilised.",
  },

  "dry-fruits": {
    slug: "dry-fruits-exporter-from-india",
    folio: "№ 10",
    sectionLabel: "DRY FRUIT EXPORT",
    seoTitle: "Dry Fruits Exporter India | Cashew, Almond & Raisin",
    seoDescription:
      "Indian dry fruits exporter for cashew W180-W400, almond, raisin, fig and dates with aflatoxin testing and long-haul packing.",
    h1: "Dry Fruits Exporter from India",
    image: "/products/dry-fruits-premium.jpg",
    imageAlt: "Premium dry fruits and nuts supplier from India",
    eyebrow: "Cashew · Almond · Raisin · Fig · Pulses · Nuts",
    standfirst:
      "Vilora Impex is an Indian dry fruits exporter supplying cashew (W180–W400), almond, raisin, fig and select dates in bulk to wholesale buyers, importers and distributors. Aflatoxin-tested, vacuum or nitrogen-flushed packing, full COA on every lot.",
    introH2: "Premium Indian dry fruits in bulk",
    introBody:
      "Our dry-fruit desk runs out of our Vilora Organic Essential mill in Gujarat. We source cashew kernels from Mangalore and Goa, almonds and raisins from contracted growers, and figs from Maharashtra. Every lot is hand-graded, calibrated by count, aflatoxin-tested, and vacuum- or nitrogen-flushed for long-haul shipping.",
    productsH2: "Dry fruits we export",
    products: [
      { name: "Cashew Kernels W180", spec: "Premium large white kernels • aflatoxin tested" },
      { name: "Cashew W210, W240, W320, W400", spec: "Full grade range • calibrated kernel counts" },
      { name: "Almonds (whole, split)", spec: "American Nonpareil and Indian varieties on contract" },
      { name: "Raisins", spec: "Golden, green and black • Sultana and Thompson seedless" },
      { name: "Figs (Anjeer)", spec: "Maharashtra origin • premium grade" },
      { name: "Pulses (Toor / Chana / Moong)", spec: "Cross-listed with our pulses desk" },
    ],
    packagingH2: "Packaging, MOQ and shipment",
    packagingBody:
      "MOQ: one 20ft FCL container (typically 18–22 MT for dry fruits). Packing: 10/25 kg vacuum-sealed liners in cartons, or nitrogen-flushed tins for premium retail. Bulk jute or BOPP for wholesale. Incoterms: FOB Mundra/Kandla, CIF, CFR. Payment: 30% advance + 70% against BL, or irrevocable L/C at sight.",
    portsH2: "Ports and destinations",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why source dry fruits from Vilora Impex",
    why: [
      "Aflatoxin-tested on every lot, with COA accompanying shipment",
      "Vacuum-sealed and nitrogen-flushed packing for long-haul integrity",
      "Hand-graded and calibrated kernel counts",
      "FSSAI, APEDA, ISO 22000",
      "Sourcing partnerships with Mangalore, Goa, Maharashtra growers",
      "Documentation within 48 hours of sail",
    ],
    faqsH2: "Dry fruit export FAQs",
    faqs: [
      {
        q: "Are you a dry fruits exporter from India?",
        a: "Yes. Vilora Impex exports cashew (W180–W400), almond, raisin, fig and select dates in bulk to wholesale buyers worldwide. FSSAI, APEDA and ISO 22000 registered.",
      },
      {
        q: "Are dry fruits aflatoxin-tested?",
        a: "Yes. Every lot is aflatoxin-tested at an accredited lab and COA accompanies the shipment.",
      },
      {
        q: "What cashew grades do you supply?",
        a: "Full white-kernel range — W180 (largest), W210, W240, W320 and W400. Scorched and split grades available on contract.",
      },
      {
        q: "What is the MOQ for dry fruit export?",
        a: "One 20ft FCL container (18–22 MT for dry fruits, depending on the product mix).",
      },
      {
        q: "How is dry fruit packed for long-haul shipping?",
        a: "Vacuum-sealed liners in cartons (10/25 kg) or nitrogen-flushed tins for premium retail. Bulk jute or BOPP for wholesale and distributor buyers.",
      },
      {
        q: "Where do you source dry fruits from?",
        a: "Cashew from Mangalore and Goa, almonds and raisins from contracted growers, figs from Maharashtra. Consolidation at our Vilora Organic Essential mill in Gujarat.",
      },
    ],
    related: ["spices", "indian-agro", "fresh-produce"],
    productSchemaName: "Indian Dry Fruits (Cashew, Almond, Raisin, Fig)",
    productSchemaDescription:
      "Indian dry fruits for export — cashew W180 to W400, almond, raisin, fig and dates. Aflatoxin-tested, vacuum or nitrogen-flushed packing.",
  },

  "fresh-produce": {
    slug: "fresh-produce-exporter-from-india",
    folio: "№ 11",
    sectionLabel: "FRESH PRODUCE EXPORT",
    seoTitle: "Fresh Produce Exporter India | Mango, Onion & Pomegranate",
    seoDescription:
      "Indian fresh produce exporter for mangoes, onion, pomegranate and banana with APEDA documentation, grading and cold-chain reefer planning.",
    h1: "Fresh Produce Exporter from India",
    image: "/products/fresh-mangoes.jpg",
    imageAlt: "Fresh Indian mangoes selected for cold-chain export",
    eyebrow: "Mango · Onion · Pomegranate · Banana · Cold-chain reefer",
    standfirst:
      "Vilora Impex exports fresh Indian produce on cold-chain reefer — Kesar, Alphonso and Banganapalli mangoes, onions, pomegranate, banana and seasonal fruit — to wholesale buyers, importers and distributors. Pre-cooled at origin, graded for export, shipped from Mundra and Kandla.",
    introH2: "Cold-chain fresh produce for international buyers",
    introBody:
      "Fresh produce is our most time-sensitive division. Mangoes are pre-cooled at the farm and reefer-loaded for arrival within shelf-life. Onions are sized and bagged at origin in Maharashtra and Gujarat. Pomegranate from Maharashtra and banana from Tamil Nadu are inspected, graded and packed for export. APEDA registered.",
    productsH2: "Fresh produce we export",
    products: [
      { name: "Kesar Mango", spec: "Gujarat origin • premium aromatic mango • cold-chain reefer" },
      { name: "Alphonso Mango", spec: "Maharashtra (Ratnagiri, Devgad) origin" },
      { name: "Banganapalli Mango", spec: "Andhra Pradesh origin • large yellow mango" },
      { name: "Onion", spec: "Red and rose onion • Nashik origin • 25/50 kg mesh bags" },
      { name: "Pomegranate", spec: "Maharashtra origin • premium Bhagwa variety" },
      { name: "Banana", spec: "Cavendish • Tamil Nadu origin • cold-chain" },
      { name: "Seasonal Fruit", spec: "Grape, watermelon, papaya on contract" },
    ],
    packagingH2: "Packaging, MOQ and shipment",
    packagingBody:
      "MOQ: one 20ft or 40ft reefer container per consignment depending on commodity. Packing: corrugated cartons sized 3.5/4/5 kg for mango; mesh bags for onion; CFB cartons for pomegranate and banana. Temperature and humidity controlled. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Ports and destinations",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why source fresh produce from Vilora Impex",
    why: [
      "Pre-cooled at origin to lock in shelf-life",
      "Reefer-planned for direct routes from Mundra",
      "APEDA registered exporter",
      "Grading and sizing to buyer specification",
      "Live container temperature and humidity tracking",
      "Documentation within 48 hours of sail",
    ],
    faqsH2: "Fresh produce export FAQs",
    faqs: [
      {
        q: "Are you a fresh produce exporter from India?",
        a: "Yes. Vilora Impex exports mango (Kesar, Alphonso, Banganapalli), onion, pomegranate, banana and seasonal fruit on cold-chain reefer to international wholesale buyers. APEDA registered.",
      },
      {
        q: "What mango varieties do you export?",
        a: "Kesar (Gujarat), Alphonso (Ratnagiri / Devgad), Banganapalli (Andhra Pradesh) and seasonal Indian varieties on contract.",
      },
      {
        q: "How is mango packed and shipped?",
        a: "Pre-cooled at origin, packed in corrugated cartons (3.5/4/5 kg), reefer-loaded at controlled temperature, shipped from Mundra to Gulf and Middle East with direct service.",
      },
      {
        q: "Do you export onion in bulk?",
        a: "Yes. Red and rose onion from Nashik in 25 and 50 kg mesh bags, sized to buyer specification.",
      },
      {
        q: "What is the MOQ for reefer fresh produce?",
        a: "One 20ft or 40ft reefer container per commodity. Mixed-commodity reefers on case-by-case approval.",
      },
      {
        q: "What is your cold-chain protocol?",
        a: "Pre-cool at origin within 24 hours of harvest, temperature-control through stuffing, reefer monitored at 8°C–13°C depending on the commodity, with live container tracking and temperature logs to buyer.",
      },
    ],
    related: ["mango", "dry-fruits", "indian-agro"],
    productSchemaName: "Indian Fresh Produce (Mango, Onion, Pomegranate, Banana)",
    productSchemaDescription:
      "Indian fresh produce on cold-chain reefer: Kesar, Alphonso and Banganapalli mangoes; onion, pomegranate, banana. Pre-cooled, graded for export.",
  },

  mango: {
    slug: "mango-exporter-from-india",
    folio: "№ 11·A",
    sectionLabel: "MANGO EXPORT",
    seoTitle: "Mango Exporter from India | Kesar, Alphonso & Banganapalli",
    seoDescription:
      "Indian mango exporter for Kesar, Alphonso and Banganapalli mangoes. APEDA registered, pre-cooled, graded, packed in cartons and shipped by cold-chain reefer.",
    h1: "Mango Exporter from India",
    image: "/products/fresh-mangoes.jpg",
    imageAlt: "Premium Indian mangoes packed for cold-chain export",
    eyebrow: "Kesar · Alphonso · Banganapalli · Cold-chain reefer",
    standfirst:
      "Vilora Impex exports premium Indian mangoes in season, including Kesar from Gujarat, Alphonso from Maharashtra and Banganapalli from Andhra Pradesh. Fruit is harvested at buyer-specified maturity, pre-cooled at origin, graded by size and colour, packed in corrugated cartons and shipped by cold-chain reefer.",
    introH2: "Indian mangoes for importers and wholesale buyers",
    introBody:
      "Mango export needs tight timing. Our fresh-produce desk coordinates farm pickup, maturity sorting, pre-cooling, carton packing, documentation and reefer loading so buyers receive fruit inside the agreed shelf-life window. We serve importers, distributors, supermarket suppliers and food-service buyers looking for reliable Indian mango supply from Gujarat and Maharashtra.",
    productsH2: "Mango varieties we export",
    products: [
      { name: "Kesar Mango", spec: "Gujarat origin • aromatic pulp • popular in Gulf and Middle East markets" },
      { name: "Alphonso Mango", spec: "Ratnagiri and Devgad origin • premium table mango • seasonal export" },
      { name: "Banganapalli Mango", spec: "Andhra Pradesh origin • large yellow mango • retail carton grade" },
      { name: "Totapuri Mango", spec: "Processing and pulp buyers on contract" },
      { name: "Rajapuri Mango", spec: "Seasonal Gujarat variety on request" },
      { name: "Mango pulp and puree", spec: "Available through approved processing partners on contract" },
    ],
    packagingH2: "Packaging, MOQ and shipment",
    packagingBody:
      "MOQ is one 20ft or 40ft reefer container depending on destination, variety and season. Standard mango packing is 3.5 kg, 4 kg or 5 kg corrugated cartons with ventilation, size grading and buyer label support. Temperature, humidity and ventilation settings are planned per variety and transit time. Incoterms supported: FOB Mundra/Kandla, CIF and CFR.",
    portsH2: "Ports and destinations",
    portsBody:
      "Mangoes are consolidated in Gujarat and shipped through Mundra or Kandla with cold-chain planning for Gulf, Middle East, Maldives and selected Southeast Asia destinations. Air freight can be discussed for urgent premium consignments.",
    process: [
      {
        label: "Season planning",
        body: "You share variety, carton size, destination, volume and delivery window. We confirm seasonal availability and harvest plan.",
      },
      {
        label: "Harvest and grading",
        body: "Fruit is harvested at agreed maturity, sorted for size, colour, external defects and buyer grade.",
      },
      {
        label: "Pre-cooling",
        body: "Mangoes are pre-cooled at origin before packing to protect shelf life during long-haul transit.",
      },
      {
        label: "Packing and inspection",
        body: "Cartons are packed, labelled, photographed and checked against buyer specification before reefer stuffing.",
      },
      {
        label: "Documentation and delivery",
        body: "APEDA, phytosanitary, certificate of origin, invoice, packing list and BL are prepared with live reefer tracking to arrival.",
      },
    ],
    whyH2: "Why source mangoes from Vilora Impex",
    why: [
      "Kesar, Alphonso and Banganapalli mango sourcing across Gujarat, Maharashtra and Andhra Pradesh",
      "APEDA registered fresh produce export desk",
      "Pre-cooling, grading and buyer-specific carton packing",
      "Cold-chain reefer planning from origin to port of arrival",
      "Live container tracking and temperature logs on request",
      "Same-day export desk response during mango season",
    ],
    faqsH2: "Mango export FAQs",
    faqs: [
      {
        q: "Are you a mango exporter from India?",
        a: "Yes. Vilora Impex exports Indian mangoes including Kesar, Alphonso and Banganapalli to international wholesale buyers, importers and distributors. Shipments are planned through our APEDA registered fresh-produce desk.",
      },
      {
        q: "Which mango varieties do you supply?",
        a: "Kesar mango from Gujarat, Alphonso mango from Ratnagiri and Devgad, Banganapalli mango from Andhra Pradesh, plus Totapuri and Rajapuri on contract during season.",
      },
      {
        q: "How do you pack mangoes for export?",
        a: "Mangoes are size-graded, pre-cooled and packed in ventilated corrugated cartons, usually 3.5 kg, 4 kg or 5 kg. Buyer labels and retail carton designs are supported.",
      },
      {
        q: "What is the MOQ for mango export?",
        a: "Standard MOQ is one 20ft or 40ft reefer container depending on route, variety and season. Smaller air-freight consignments can be discussed for premium urgent shipments.",
      },
      {
        q: "Do you provide mango export documentation?",
        a: "Yes. We support APEDA documentation, phytosanitary certificate, certificate of origin, invoice, packing list and Bill of Lading. Destination-specific documents are confirmed before order acceptance.",
      },
      {
        q: "Which countries do you export mangoes to?",
        a: "Primary buyer regions include the Gulf, Middle East, Maldives and selected Southeast Asia destinations, depending on seasonal route availability and import rules.",
      },
    ],
    related: ["fresh-produce", "indian-agro", "dry-fruits"],
    productSchemaName: "Indian Mangoes (Kesar, Alphonso, Banganapalli)",
    productSchemaDescription:
      "Premium Indian mangoes for export: Kesar, Alphonso and Banganapalli. APEDA registered, pre-cooled, graded and shipped by cold-chain reefer.",
  },

  "indian-agro": {
    slug: "indian-agro-products-exporter",
    folio: "№ 12",
    sectionLabel: "INDIAN AGRO PRODUCTS",
    seoTitle: "Indian Agro Products Exporter | Rice, Spices & Pulses",
    seoDescription:
      "Indian agro products exporter for rice, pulses, spices, dry fruits and fresh produce from one Gujarat trading house with bulk shipment support.",
    h1: "Indian Agro Products Exporter",
    image: "/og.jpg",
    imageAlt: "Vilora Impex Indian agro products exporter",
    eyebrow: "Rice · Pulses · Spices · Dry fruits · Fresh produce — one ledger",
    standfirst:
      "Vilora Impex is an Indian agro products exporter consolidating rice, pulses, spices, dry fruit and fresh produce under one Gujarat-based trading house. Wholesale buyers, importers, distributors and food-service operators source the full agro basket from a single ledger.",
    introH2: "One trading house, the full Indian agro basket",
    introBody:
      "Most Indian export houses specialise in one commodity. Vilora Impex runs five desks — rice, pulses, spices, dry fruit and fresh produce — each led by people who know origin, grading and the destination port. Buyers consolidate orders, paperwork and shipping under one supplier, with one ledger.",
    productsH2: "Categories we export",
    products: [
      { name: "Rice & Grain", spec: "1121 / 1509 basmati, Sona Masuri, IR64, idli rice, parboiled and raw" },
      { name: "Pulses", spec: "Toor, chana, moong, urad, masoor — whole and split, machine-cleaned" },
      { name: "Spices & Masala", spec: "Turmeric, cumin, chilli, coriander, cardamom, garam masala blends" },
      { name: "Dry Fruit & Nuts", spec: "Cashew W180–W400, almond, raisin, fig, dates" },
      { name: "Fresh Produce", spec: "Kesar, Alphonso, Banganapalli mango, onion, pomegranate, banana — cold-chain" },
    ],
    packagingH2: "Packaging, MOQ and shipment",
    packagingBody:
      "MOQ: one 20ft FCL container per commodity (14–25 MT depending on the product). Cross-category consolidation possible on approval. Packing options span 5 kg retail to 50 kg jute, plus private-label and bespoke artwork. Incoterms: FOB Mundra/Kandla, CIF, CFR. Payment: 30% advance + 70% against BL, or irrevocable L/C at sight.",
    portsH2: "Ports and destinations",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why consolidate with Vilora Impex",
    why: [
      "Five export desks under one ledger — fewer suppliers, fewer documents",
      "FSSAI, APEDA, Spices Board of India, ISO 22000 across the full agro basket",
      "Cross-category container consolidation on approval",
      "Documentation packet within 48 hours of sail",
      "Live container tracking to port of arrival",
      "Same family, same scale, same discipline — fifteen years of agro trade",
    ],
    faqsH2: "Indian agro products FAQs",
    faqs: [
      {
        q: "Are you an Indian agro products exporter?",
        a: "Yes. Vilora Impex exports the full Indian agro basket — rice, pulses, spices, dry fruit and fresh produce — under one trading house registered in Rajkot, Gujarat. FSSAI, APEDA, Spices Board and ISO 22000.",
      },
      {
        q: "Can I consolidate multiple commodities in one container?",
        a: "Yes, with prior approval. We routinely run mixed-category 20ft and 40ft containers for distributors who want a single shipment of rice, spice, pulses and dry fruit. Each line gets its own COA and documentation.",
      },
      {
        q: "Do you handle private-label across categories?",
        a: "Yes. Private-label rice, spices, pulses and dry fruit available with buyer artwork. Packaging desk co-develops if needed.",
      },
      {
        q: "What are the standard payment terms?",
        a: "30% advance + 70% against scan copy of Bill of Lading, or irrevocable Letter of Credit at sight. Accepted in USD, AED, EUR.",
      },
      {
        q: "Where is Vilora Impex based?",
        a: "Headquartered in Rajkot, Gujarat, India. Origin sourcing covers Gujarat, Punjab, Andhra Pradesh, Maharashtra, Karnataka, Kashmir and Rajasthan, with consolidation at Mundra and Kandla for export.",
      },
      {
        q: "How quickly do you reply to inquiries?",
        a: "Same day in our timezone (IST). Email exports@viloraimpex.com with your spec, quantity, packing preference and destination port.",
      },
    ],
    related: ["rice-exporter", "spices", "mango"],
    productSchemaName: "Indian Agro Products — Rice, Pulses, Spices, Dry Fruit, Fresh Produce",
    productSchemaDescription:
      "Indian agro products exported under one Gujarat-based trading house — rice, pulses, spices, dry fruit and fresh produce. FSSAI, APEDA, Spices Board, ISO 22000.",
  },

  "premium-rice": {
    slug: "premium-rice-supplier-india",
    folio: "№ 16",
    sectionLabel: "PREMIUM RICE",
    seoTitle: "Premium Rice Supplier from India",
    seoDescription:
      "Premium rice supplier from India — long-grain 1121 basmati, 1509 basmati, Sona Masuri, sortex-cleaned. FSSAI/APEDA registered exporter, FOB Mundra/Kandla.",
    h1: "Premium Rice Supplier from India",
    image: "/hero-rice.jpg",
    imageAlt: "Premium long-grain Indian rice — Vilora Impex export desk, Gujarat",
    eyebrow: "Premium-grade rice for international buyers",
    standfirst:
      "Vilora Impex is a premium rice supplier from India, specialising in long-grain basmati and high-grade non-basmati lots for wholesale importers, distributors, HoReCa chains and private-label brands. Every shipment is hand-graded at origin, sortex-cleaned to buyer specification, and dispatched from Mundra or Kandla with full FSSAI, APEDA, phytosanitary and certificate-of-origin documentation.",
    introH2: "What 'premium' means at Vilora Impex",
    introBody:
      "Premium isn't a marketing word in our ledger — it's a specification. Grain length 8.30 mm+ for 1121 basmati, aged 12 months minimum, broken below 2%, foreign matter below 0.05%, moisture under 12.5%, sortex-cleaned in two passes. Every lot ships with an independent lab COA so the importer never opens a container guessing. We supply only the top three milling tiers and decline anything below.",
    productsH2: "Premium rice varieties we export",
    products: [
      { name: "1121 Basmati — Sella / Steam / Raw", spec: "Premium long-grain 8.30 mm+ • aged 12 months • <2% broken • two-pass sortex" },
      { name: "1509 Basmati Rice", spec: "Short-duration premium basmati • HoReCa and retail grade • aged 6+ months" },
      { name: "Pusa Basmati", spec: "Long-grain aromatic basmati • contract supply for private-label buyers" },
      { name: "Sortex-Cleaned Sona Masuri", spec: "Medium-grain non-basmati • low starch • premium polish" },
      { name: "Premium IR64 Parboiled", spec: "Long-grain non-basmati • 5% broken max • bulk and retail packs" },
      { name: "Premium Basmati Blend", spec: "Custom 1121/1509 blend on contract, locked to buyer cuttest" },
    ],
    packagingH2: "Packaging, MOQ and incoterms",
    packagingBody:
      "Standard MOQ is one 20ft FCL container (~25 metric tonnes). Packaging available in 5/10/25/50 kg PP, BOPP printed, jute, or buyer-branded private label. Incoterms supported: FOB Mundra/Kandla, CIF and CFR to destination port. LCL consolidated shipments accepted for new-buyer trial orders.",
    portsH2: "Ports and destinations",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for premium rice",
    why: WHY_DEFAULT,
    faqsH2: "Premium rice supply FAQs",
    faqs: [
      {
        q: "What makes Vilora Impex a premium rice supplier from India?",
        a: "We supply only the top three milling tiers, accept lots with broken below 2%, sortex-clean in two passes, and ship every order with an independent lab COA. Buyers receive cuttest samples and approve before any commit.",
      },
      {
        q: "Do you supply premium basmati and non-basmati in the same shipment?",
        a: "Yes. We routinely consolidate mixed-variety containers (e.g., 1121 basmati + Sona Masuri + IR64) for distributors who serve multi-cuisine retail markets. Each variety ships in its own lot ID with its own COA.",
      },
      {
        q: "What is the minimum order quantity for premium rice?",
        a: "One 20ft FCL — approximately 25 metric tonnes. Trial LCL shipments of 5–10 MT are accepted for first-time importers under L/C or 100% advance terms.",
      },
      {
        q: "Can you supply premium rice under our private brand?",
        a: "Yes. We offer private-label and contract-pack programs in 5/10/25/50 kg in PP, BOPP printed, jute or retail cartons. Buyers either supply artwork or co-develop with our packaging desk.",
      },
      {
        q: "How do you guarantee the rice is genuinely premium?",
        a: "Independent lab COA, photographic evidence of hand-grading, weighbridge slips, sortex pass logs and pre-stuffing photos are couriered to the buyer with the documentation packet — every shipment, every time.",
      },
      {
        q: "What payment terms do you accept for premium rice exports?",
        a: "30% advance + 70% against scanned Bill of Lading, or irrevocable Letter of Credit at sight from a reputable bank. For new buyers on first shipments, 100% advance against a sample-approved PI is standard.",
      },
    ],
    related: ["1121-basmati", "1509-basmati", "sortex-rice", "wholesale-rice"],
    productSchemaName: "Premium Indian Rice for Export",
    productSchemaDescription:
      "Premium long-grain Indian rice — 1121 basmati, 1509 basmati, Sona Masuri and sortex-cleaned non-basmati — supplied to international buyers under FSSAI and APEDA registration.",
  },

  "1121-basmati": {
    slug: "1121-basmati-rice-exporter",
    folio: "№ 17",
    sectionLabel: "1121 BASMATI",
    seoTitle: "1121 Basmati Rice Exporter from India",
    seoDescription:
      "1121 basmati rice exporter from India — Sella, Steam and Raw varieties, 8.30 mm+ long grain, aged 12 months minimum. FSSAI/APEDA registered.",
    h1: "1121 Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "1121 Sella basmati rice long grain export — Vilora Impex, Gujarat",
    eyebrow: "1121 Sella, Steam and Raw basmati for global buyers",
    standfirst:
      "Vilora Impex is a registered Indian exporter of 1121 basmati rice — the long-grain, aromatic, low-glycemic premium variety preferred by Middle East, Gulf and Southeast Asia importers. We supply 1121 Sella (parboiled golden), Steam and Raw in retail and HoReCa grades, aged 12 months minimum, with grain length 8.30 mm+ guaranteed.",
    introH2: "About 1121 basmati rice",
    introBody:
      "1121 basmati is the longest-grain commercial basmati cultivar grown in India and Pakistan — uncooked grain length 8.30 mm with average cooked length exceeding 18 mm. Aged at least 12 months for aroma development, sortex-cleaned in two passes for purity below 2% broken. The Sella (parboiled) variant has the iconic golden colour; Steam is creamy-white from gentle steam processing; Raw is the natural milled grain.",
    productsH2: "1121 basmati grades we export",
    products: [
      { name: "1121 Sella Basmati", spec: "Parboiled golden long grain • 8.30 mm+ • aged 12 months • <2% broken" },
      { name: "1121 Steam Basmati", spec: "Steam-processed creamy white • 8.30 mm+ • premium HoReCa grade" },
      { name: "1121 Raw Basmati", spec: "Natural milled white • 8.30 mm+ • aromatic premium grade" },
      { name: "1121 Golden Sella", spec: "Deep-golden parboiled • premium aged stock for Gulf retail" },
      { name: "1121 Tibar (Broken)", spec: "Sortex-cleaned brokens for affordability segments" },
      { name: "Custom 1121 Specification", spec: "Cuttest-locked grain length, broken %, moisture, polish to buyer spec" },
    ],
    packagingH2: "1121 basmati packaging, MOQ and shipment",
    packagingBody:
      "Standard MOQ is one 20ft FCL container (~25 metric tonnes). Packaging options: 5/10/25/50 kg PP, BOPP printed, jute, cotton, or buyer-branded private label. Common Gulf retail packs are 5 kg and 10 kg in BOPP-printed laminate; HoReCa orders typically ship in 25 kg PP. Incoterms: FOB Mundra/Kandla, CIF and CFR.",
    portsH2: "Where we ship 1121 basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for 1121 basmati",
    why: WHY_DEFAULT,
    faqsH2: "1121 basmati export FAQs",
    faqs: [
      {
        q: "What is 1121 basmati rice?",
        a: "1121 basmati is the longest-grain commercial basmati cultivar — uncooked grain length 8.30 mm with cooked grain exceeding 18 mm. It's aromatic, low-glycemic, ages well, and is the premium choice for biryani, pilaf and Gulf retail.",
      },
      {
        q: "What's the difference between 1121 Sella, Steam and Raw?",
        a: "Sella is parboiled — grain is steamed in the husk before milling, giving the iconic golden colour and harder texture. Steam is gently steam-processed creamy-white. Raw is the natural milled white grain. All three are 1121 cultivar, same grain length, different cooking behaviour.",
      },
      {
        q: "What is the minimum order quantity for 1121 basmati export?",
        a: "One 20ft FCL — approximately 25 metric tonnes. We accept LCL consolidated shipments of 5–10 MT for new-buyer trial orders.",
      },
      {
        q: "Do you provide pre-shipment samples of 1121 basmati?",
        a: "Yes. Sortex-cleaned, lab-tested 250 g samples are couriered before any commit. Buyer approval of the cuttest is locked into the PI before we book the container.",
      },
      {
        q: "Which countries import 1121 basmati from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Jordan, Lebanon, Egypt, Kenya, Tanzania, Maldives, Singapore and Malaysia. Gulf retail is the largest segment; HoReCa and biryani-chain supply is growing in Southeast Asia.",
      },
      {
        q: "How do you guarantee 1121 grain length and ageing?",
        a: "Every lot ships with photographic grain-length proofs (calipered against a 10 mm rule), an independent lab COA stating moisture, broken percentage and grain dimensions, plus a sworn ageing declaration from the milling partner.",
      },
    ],
    related: ["basmati", "1509-basmati", "premium-rice", "rice-exporter"],
    productSchemaName: "1121 Basmati Rice — Sella, Steam, Raw",
    productSchemaDescription:
      "Premium 1121 basmati rice from India — Sella (parboiled), Steam and Raw long grain 8.30 mm+. Aged 12 months minimum. FSSAI and APEDA registered exporter.",
  },

  "1509-basmati": {
    slug: "1509-basmati-rice-exporter",
    folio: "№ 18",
    sectionLabel: "1509 BASMATI",
    seoTitle: "1509 Basmati Rice Exporter from India",
    seoDescription:
      "1509 basmati rice exporter from India — short-duration premium basmati, Sella and Steam grades. FSSAI/APEDA registered. FOB Mundra/Kandla.",
    h1: "1509 Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "1509 basmati rice long grain export — Vilora Impex, Gujarat",
    eyebrow: "1509 basmati — the younger sibling of 1121",
    standfirst:
      "Vilora Impex is a registered Indian exporter of 1509 basmati rice — a short-duration premium basmati cultivar with grain length 8.0–8.4 mm, fragrant, low-broken and increasingly preferred by HoReCa, biryani-chain and value-retail buyers across the Gulf and Middle East. We supply Sella (parboiled), Steam and Raw grades with full FSSAI, APEDA and phytosanitary documentation.",
    introH2: "About 1509 basmati rice",
    introBody:
      "1509 is a short-duration basmati variety developed for higher yield while preserving the aromatic, long-grain character of the basmati family. Grain length 8.0–8.4 mm (slightly shorter than 1121), excellent aroma, lower price point than 1121 — which makes it the value-premium choice for HoReCa chains, retail private label and growing markets where 1121 is at the top of the price range. Often blended with 1121 for retail packs.",
    productsH2: "1509 basmati grades we export",
    products: [
      { name: "1509 Sella Basmati", spec: "Parboiled golden • 8.0–8.4 mm grain • premium retail and HoReCa" },
      { name: "1509 Steam Basmati", spec: "Steam-processed creamy white • HoReCa biryani-chain grade" },
      { name: "1509 Raw Basmati", spec: "Natural milled white • aromatic value-premium grade" },
      { name: "1509 Golden Sella", spec: "Deep-golden parboiled • value-tier Gulf retail" },
      { name: "1509/1121 Blend", spec: "Custom blend ratios to lock retail price points" },
      { name: "Custom 1509 Specification", spec: "Cuttest-locked grain length, broken %, moisture, polish to buyer spec" },
    ],
    packagingH2: "1509 basmati packaging, MOQ and shipment",
    packagingBody:
      "Standard MOQ is one 20ft FCL container (~25 metric tonnes). Packaging: 5/10/25/50 kg PP, BOPP printed, jute, or private-label retail packs. HoReCa buyers commonly order 25 kg PP at FCL volumes. Incoterms: FOB Mundra/Kandla, CIF and CFR. LCL trial shipments accepted under L/C or 100% advance.",
    portsH2: "Where we ship 1509 basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for 1509 basmati",
    why: WHY_DEFAULT,
    faqsH2: "1509 basmati export FAQs",
    faqs: [
      {
        q: "What is 1509 basmati rice?",
        a: "1509 is a short-duration premium basmati cultivar — grain length 8.0–8.4 mm, fragrant, lower-yield-risk than 1121 and priced below it. Increasingly preferred by HoReCa, biryani-chain and value-retail buyers.",
      },
      {
        q: "How does 1509 differ from 1121 basmati?",
        a: "1121 is the longest commercial basmati at 8.30 mm+ and commands a premium. 1509 is slightly shorter (8.0–8.4 mm), matures faster on the farm, and trades at a lower price point — making it the value-premium choice without leaving the basmati family.",
      },
      {
        q: "What's the minimum order quantity for 1509 basmati export?",
        a: "One 20ft FCL — approximately 25 metric tonnes. LCL trial shipments of 5–10 MT are accepted for new-buyer first orders under L/C or 100% advance.",
      },
      {
        q: "Can you blend 1509 with 1121 in the same shipment?",
        a: "Yes. Custom 1121/1509 blends are common for retail brands locking specific price points. Blend ratios are locked in the PI and certified on the COA.",
      },
      {
        q: "Which countries import 1509 basmati from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Jordan, Lebanon, Egypt, Kenya, Tanzania, Maldives, Singapore and Malaysia. HoReCa biryani chains and value-retail brands are the fastest-growing segments.",
      },
      {
        q: "Do you provide private-label packing for 1509 basmati?",
        a: "Yes. Private-label and contract-pack programs in 5/10/25/50 kg PP, BOPP printed, jute or retail cartons. Buyers either supply artwork or co-develop with our packaging desk.",
      },
    ],
    related: ["basmati", "1121-basmati", "premium-rice", "rice-exporter"],
    productSchemaName: "1509 Basmati Rice — Sella, Steam, Raw",
    productSchemaDescription:
      "Premium 1509 basmati rice from India — short-duration aromatic long grain 8.0–8.4 mm in Sella, Steam and Raw grades. FSSAI and APEDA registered exporter.",
  },

  "sortex-rice": {
    slug: "sortex-cleaned-rice-exporter",
    folio: "№ 19",
    sectionLabel: "SORTEX RICE",
    seoTitle: "Sortex-Cleaned Rice Exporter from India",
    seoDescription:
      "Sortex-cleaned rice exporter from India — two-pass colour-sorted basmati and non-basmati for international buyers. FSSAI/APEDA registered.",
    h1: "Sortex-Cleaned Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Sortex-cleaned long-grain Indian rice export — Vilora Impex",
    eyebrow: "Two-pass sortex purity for serious importers",
    standfirst:
      "Vilora Impex exports sortex-cleaned rice from India — two-pass colour-sorted basmati and non-basmati lots that meet the stringent purity specifications demanded by Gulf retail chains, HoReCa kitchens and private-label brands. Every container ships with the sortex pass log and independent lab COA so the buyer never opens to surprises.",
    introH2: "What 'sortex-cleaned' means in our spec",
    introBody:
      "Sortex is the industry-standard colour-sorting technology that removes discoloured grains, foreign matter, paddy and broken pieces from the milled lot using high-speed optical cameras. A single pass is the bare minimum; we run two passes on every premium export lot to push the purity below 0.05% foreign matter and broken below buyer-specified percentage. The sortex pass log is photographed at the mill and included in the documentation packet.",
    productsH2: "Sortex-cleaned rice grades we export",
    products: [
      { name: "Sortex 1121 Basmati Sella", spec: "Two-pass sortex • <2% broken • aged 12 months • 8.30 mm+" },
      { name: "Sortex 1509 Basmati", spec: "Two-pass sortex • HoReCa-grade premium basmati" },
      { name: "Sortex Sona Masuri", spec: "Two-pass sortex • medium-grain non-basmati • premium polish" },
      { name: "Sortex IR64 Parboiled", spec: "Two-pass sortex • long-grain non-basmati • 5% broken max" },
      { name: "Sortex Idli Rice", spec: "Two-pass sortex • short-grain parboiled fermentation grade" },
      { name: "Custom Sortex Specification", spec: "Three-pass option for ultra-premium private-label buyers" },
    ],
    packagingH2: "Packaging, MOQ and shipment",
    packagingBody:
      "Standard MOQ is one 20ft FCL container (~25 metric tonnes). Packaging in 5/10/25/50 kg PP, BOPP printed, jute, or private-label retail packs. Sortex pass logs and independent lab COA are couriered with the documentation packet within 48 hours of sail. Incoterms: FOB Mundra/Kandla, CIF and CFR.",
    portsH2: "Where we ship sortex-cleaned rice",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why buyers specify sortex from Vilora Impex",
    why: WHY_DEFAULT,
    faqsH2: "Sortex rice export FAQs",
    faqs: [
      {
        q: "What is sortex-cleaned rice?",
        a: "Sortex-cleaned rice has been processed through a colour-sorting machine that uses high-speed optical cameras to eject discoloured grains, foreign matter, paddy and broken pieces from the lot. Standard for premium export-grade rice.",
      },
      {
        q: "Why do you run two sortex passes?",
        a: "A single pass meets most spec sheets but two passes push the purity well below 0.05% foreign matter and tighten the broken percentage to <2% on premium lots. The second pass is what separates retail-shelf-ready stock from generic bulk supply.",
      },
      {
        q: "Do you provide the sortex pass log to buyers?",
        a: "Yes. The sortex pass log is photographed at the milling partner and included in the documentation packet alongside FSSAI, APEDA, phyto, COO and Bill of Lading. Buyers always know how many passes their lot received.",
      },
      {
        q: "What is the minimum order quantity for sortex-cleaned rice?",
        a: "One 20ft FCL — approximately 25 metric tonnes. LCL trial shipments of 5–10 MT accepted for first-time importers.",
      },
      {
        q: "Can sortex-cleaned rice be supplied under private label?",
        a: "Yes. Private-label and contract-pack programs in 5/10/25/50 kg are available. Many Gulf retail brands specify sortex-cleaned for their premium-tier own brand.",
      },
      {
        q: "Which varieties are available sortex-cleaned?",
        a: "1121 basmati (Sella, Steam, Raw), 1509 basmati, Sona Masuri, IR64 parboiled, idli rice and custom blends. All on contract or trade-pack basis.",
      },
    ],
    related: ["premium-rice", "rice-exporter", "1121-basmati", "non-basmati"],
    productSchemaName: "Sortex-Cleaned Indian Rice for Export",
    productSchemaDescription:
      "Two-pass sortex colour-sorted Indian rice — basmati and non-basmati varieties for premium retail and HoReCa buyers. FSSAI and APEDA registered exporter.",
  },

  "wholesale-rice": {
    slug: "wholesale-rice-supplier-from-india",
    folio: "№ 20",
    sectionLabel: "WHOLESALE RICE",
    seoTitle: "Wholesale Rice Supplier from India",
    seoDescription:
      "Wholesale rice supplier from India — bulk basmati and non-basmati for distributors, HoReCa and private-label buyers. FCL/LCL, FOB Mundra/Kandla.",
    h1: "Wholesale Rice Supplier from India",
    image: "/hero-rice.jpg",
    imageAlt: "Wholesale Indian rice bulk export bags — Vilora Impex, Gujarat",
    eyebrow: "Bulk rice supply, FCL and LCL, end-to-end",
    standfirst:
      "Vilora Impex is a wholesale rice supplier from India serving distributors, importers, HoReCa chains and private-label brands across the Gulf, Middle East, East Africa and Southeast Asia. We supply 1121 basmati, 1509 basmati, Sona Masuri, IR64 and sortex-cleaned premium lots in FCL and LCL volumes, with FOB Mundra/Kandla pricing, full documentation packet, and live container tracking until port of arrival sign-off.",
    introH2: "Wholesale rice supply — how we serve volume buyers",
    introBody:
      "Wholesale at Vilora Impex means commodity-grade pricing with premium-grade quality control. We aggregate from milling partners in Punjab, Andhra Pradesh, Gujarat and Maharashtra, hand-grade and sortex-clean every lot to buyer specification, and ship under our FSSAI / APEDA / Spices Board / ISO 22000 credentials. Distributors get one accountable export desk, one documentation pipeline and one tracking dashboard — not three layers of broker hand-offs.",
    productsH2: "Wholesale rice varieties available",
    products: [
      { name: "Bulk 1121 Basmati", spec: "Sella / Steam / Raw • FCL volumes • aged 12 months • <2% broken" },
      { name: "Bulk 1509 Basmati", spec: "Sella / Steam / Raw • HoReCa and retail volumes" },
      { name: "Bulk Sona Masuri", spec: "Medium-grain non-basmati • bulk distributor volumes" },
      { name: "Bulk IR64 Parboiled", spec: "5%–25% broken to spec • FCL and LCL volumes" },
      { name: "Bulk Idli Rice", spec: "Short-grain parboiled • South Asia and Gulf distributor demand" },
      { name: "Custom Wholesale Blend", spec: "Buyer-specified 1121/1509/Sona/IR64 blend on contract" },
    ],
    packagingH2: "Wholesale packaging, MOQ and incoterms",
    packagingBody:
      "Standard MOQ for FCL is one 20ft container (~25 metric tonnes). LCL consolidated trial orders accepted from 5 metric tonnes for new buyers. Packaging in 25/50 kg PP and jute for wholesale; 5/10 kg BOPP printed for distributor-onto-retail flow. Incoterms supported: FOB Mundra/Kandla, CIF and CFR to destination port. Payment terms: 30% advance + 70% against BL scan, or irrevocable L/C at sight.",
    portsH2: "Wholesale rice ports and destinations",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why distributors choose Vilora Impex as wholesale supplier",
    why: WHY_DEFAULT,
    faqsH2: "Wholesale rice supply FAQs",
    faqs: [
      {
        q: "Are you a wholesale rice supplier from India?",
        a: "Yes. Vilora Impex serves wholesale distributors, importers, HoReCa chains and private-label brands across 12+ destination ports. FCL and LCL volumes both supported, FOB Mundra/Kandla.",
      },
      {
        q: "What is the minimum order quantity for wholesale rice?",
        a: "Standard FCL MOQ is one 20ft container (~25 MT). LCL trial shipments accepted from 5 MT for first-time importers under L/C or 100% advance terms.",
      },
      {
        q: "Do you offer wholesale pricing for repeat distributor buyers?",
        a: "Yes. Repeat-buyer pricing is structured around an annual indent — buyers commit to an annual volume and lock per-container pricing at the start of the year, with quarterly cuttest reviews. Spot orders are priced FOB at booking.",
      },
      {
        q: "Can you handle wholesale private-label rice packing?",
        a: "Yes. Private-label and contract-pack programs in 5/10/25/50 kg PP, BOPP printed, jute or retail cartons. Buyers either supply artwork or co-develop with our packaging desk. MOQ for private label is one FCL per SKU.",
      },
      {
        q: "Which payment terms apply to wholesale rice exports?",
        a: "30% advance + 70% against scanned Bill of Lading, or irrevocable Letter of Credit at sight from a reputable bank. New buyers: 100% advance on first shipment against a sample-approved Performa Invoice.",
      },
      {
        q: "What documentation accompanies wholesale rice shipments?",
        a: "Phytosanitary certificate, FSSAI, APEDA, certificate of origin, Bill of Lading, independent lab COA and sortex pass log — couriered within 48 hours of sail. Buyers receive live container tracking until port of arrival sign-off.",
      },
    ],
    related: ["rice-exporter", "premium-rice", "1121-basmati", "sortex-rice"],
    productSchemaName: "Wholesale Indian Rice for Export",
    productSchemaDescription:
      "Wholesale Indian rice supply — bulk basmati and non-basmati for distributors, importers and private-label buyers. FSSAI and APEDA registered exporter, FOB Mundra/Kandla.",
  },

  // ===== Wave 2: generic-search long-tails with enriched schema =====

  "1121-supplier": {
    slug: "1121-basmati-rice-supplier-from-india",
    folio: "№ 22",
    sectionLabel: "1121 BASMATI SUPPLIER",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1121 Basmati Rice Supplier from India",
    seoDescription:
      "1121 basmati rice supplier from India — Sella, Steam and Raw grades, 8.30 mm+ aged 12 months. FCL/LCL, FOB Mundra/Kandla. FSSAI/APEDA registered.",
    h1: "1121 Basmati Rice Supplier from India",
    image: "/hero-rice.jpg",
    imageAlt: "1121 basmati rice supplier — Vilora Impex export desk, Gujarat, India",
    eyebrow: "Direct-from-mill 1121 supply for global buyers",
    standfirst:
      "Vilora Impex is a direct-from-mill 1121 basmati rice supplier from India. We aggregate from premium milling partners in Punjab and Haryana, hand-grade at origin, two-pass sortex-clean, and dispatch under our FSSAI, APEDA, Spices Board and ISO 22000 credentials. Sella (parboiled), Steam and Raw grades; aged 12 months minimum; grain length 8.30 mm+ guaranteed; live container tracking to port of arrival.",
    introH2: "1121 basmati supply, end-to-end",
    introBody:
      "Most importers don't want a broker — they want a supplier accountable for the lot. Vilora Impex is one accountable export desk: we sign the documentation packet, photograph the grading, log the sortex passes, and put our name on the container. Buyers receive a 250 g lab-tested sample before commit, an independent COA with every shipment, and our direct line until sign-off at destination port.",
    productsH2: "1121 basmati supply grades",
    products: [
      { name: "1121 Sella Basmati", spec: "Parboiled golden long grain • 8.30 mm+ • aged 12 months • two-pass sortex" },
      { name: "1121 Steam Basmati", spec: "Steam-processed creamy white • 8.30 mm+ • HoReCa premium grade" },
      { name: "1121 Raw Basmati", spec: "Natural milled white • 8.30 mm+ • aromatic premium grade" },
      { name: "1121 Golden Sella", spec: "Deep-golden parboiled • premium aged stock for Gulf retail shelves" },
      { name: "1121 Creamy Sella", spec: "Lighter parboiled tone for value-retail private label" },
      { name: "Custom 1121 Cuttest", spec: "Buyer-locked grain length, broken %, moisture, polish, ageing" },
    ],
    packagingH2: "1121 supply packaging, MOQ and incoterms",
    packagingBody:
      "Standard MOQ is one 20ft FCL (~25 MT). LCL trial shipments accepted from 5 MT for first-time importers under L/C or 100% advance. Packaging in 5/10/25/50 kg PP, BOPP-printed, jute, cotton or buyer-branded private label. Incoterms supported: FOB Mundra/Kandla, CIF and CFR to destination. Payment terms: 30% advance + 70% against BL scan, or irrevocable L/C at sight.",
    portsH2: "Destinations we supply 1121 basmati to",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose us as 1121 basmati supplier",
    why: WHY_DEFAULT,
    faqsH2: "1121 basmati supplier FAQs",
    faqs: [
      {
        q: "Are you a direct 1121 basmati supplier or a broker?",
        a: "We are a direct supplier. Vilora Impex aggregates from premium milling partners in Punjab and Haryana, signs the documentation packet under our own export desk, and remains accountable through to port-of-arrival sign-off. No intermediary brokers.",
      },
      {
        q: "What is the MOQ for 1121 basmati supply from India?",
        a: "One 20ft FCL — approximately 25 metric tonnes. LCL consolidated shipments of 5–10 MT accepted for new-buyer trial orders under L/C or 100% advance.",
      },
      {
        q: "Do you supply 1121 basmati under private label?",
        a: "Yes. Private-label and contract-pack programs in 5/10/25/50 kg in PP, BOPP-printed, jute or retail cartons. Buyers supply artwork or co-develop with our packaging desk. MOQ for private label is one FCL per SKU.",
      },
      {
        q: "What documents accompany a 1121 basmati supply shipment?",
        a: "Phytosanitary certificate, FSSAI, APEDA, certificate of origin, Bill of Lading, independent lab COA, sortex pass log and photographic grain-length proofs — couriered within 48 hours of sail.",
      },
      {
        q: "How is your 1121 basmati priced relative to spot market?",
        a: "Repeat-buyer pricing is structured around an annual indent — buyers commit to a volume and lock per-container pricing for the year. Spot orders are priced FOB Mundra/Kandla at booking based on current crop and ageing inventory.",
      },
      {
        q: "Which destinations do you supply 1121 basmati to most?",
        a: "Gulf retail (UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain) is our largest segment, followed by HoReCa biryani chains across Southeast Asia (Singapore, Malaysia) and Maldives retail.",
      },
    ],
    related: ["1121-basmati", "1509-basmati", "premium-rice", "wholesale-rice"],
    productSchemaName: "1121 Basmati Rice Supply — Sella, Steam, Raw",
    productSchemaDescription:
      "Direct-from-mill 1121 basmati rice supply from India — Sella, Steam and Raw grades, 8.30 mm+ aged 12 months. Supplied to international buyers under FSSAI/APEDA registration.",
  },

  "bulk-non-basmati": {
    slug: "bulk-non-basmati-rice-exporter-india",
    folio: "№ 23",
    sectionLabel: "BULK NON-BASMATI",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "Bulk Non-Basmati Rice Exporter from India",
    seoDescription:
      "Bulk non-basmati rice exporter from India — Sona Masuri, IR64, Swarna, idli rice in parboiled and raw. FCL volumes, FOB Mundra/Kandla, FSSAI/APEDA.",
    h1: "Bulk Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Bulk non-basmati Indian rice export bags — Vilora Impex, Gujarat",
    eyebrow: "FCL non-basmati rice for volume buyers",
    standfirst:
      "Vilora Impex is a registered bulk non-basmati rice exporter from India serving volume importers, food-service distributors and government tender buyers across the Gulf, Middle East, East Africa and Southeast Asia. We supply Sona Masuri, IR64, Swarna and idli rice in FCL volumes — parboiled and raw, sortex-cleaned, with 5%–25% broken to buyer specification.",
    introH2: "Bulk non-basmati rice supply at scale",
    introBody:
      "Bulk non-basmati buyers don't need fancy storytelling — they need reliable lots, accurate cuttests, consistent moisture, dependable documentation and on-time sail. Vilora Impex sources directly from milling partners in Andhra Pradesh, Telangana, Karnataka and West Bengal, and dispatches under one accountable export desk. Container photographs, weighbridge slips and sortex logs ship with the documentation packet.",
    productsH2: "Bulk non-basmati varieties we export",
    products: [
      { name: "Sona Masuri Rice", spec: "Medium-grain non-basmati • low starch • bulk Gulf and South-Asia volumes" },
      { name: "IR64 Parboiled Rice", spec: "Long-grain non-basmati • 5%–25% broken to spec • FCL volumes" },
      { name: "IR64 Raw Rice", spec: "Natural milled long-grain non-basmati • value retail and bulk" },
      { name: "Swarna Rice", spec: "Medium-grain non-basmati • parboiled • staple-grade FCL supply" },
      { name: "Idli Rice (Parboiled)", spec: "Short-grain fermentation-grade • South-Asia and Gulf distributor volumes" },
      { name: "Broken Rice (Tukda)", spec: "Sortex-cleaned brokens for affordability and food-ingredient buyers" },
    ],
    packagingH2: "Bulk packaging, MOQ and incoterms",
    packagingBody:
      "Bulk FCL MOQ is one 20ft container (~25 MT). LCL trial shipments accepted from 5 MT for first-time importers. Packaging in 25/50 kg PP, jute or buyer-branded sacks. Incoterms: FOB Mundra/Kandla, CIF and CFR to destination. Payment terms: 30% advance + 70% against BL scan, or irrevocable L/C at sight from a reputed bank.",
    portsH2: "Where we ship bulk non-basmati rice",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why volume buyers choose Vilora Impex",
    why: WHY_DEFAULT,
    faqsH2: "Bulk non-basmati rice export FAQs",
    faqs: [
      {
        q: "What is the MOQ for bulk non-basmati rice export?",
        a: "One 20ft FCL container — approximately 25 metric tonnes. LCL consolidated shipments of 5–10 MT accepted for new-buyer trial orders.",
      },
      {
        q: "Which non-basmati varieties do you supply in bulk?",
        a: "Sona Masuri, IR64 (parboiled and raw), Swarna, idli rice and sortex-cleaned brokens. All available in 25/50 kg PP, jute or buyer-branded sacks with 5%–25% broken to specification.",
      },
      {
        q: "Do you handle government tender supply for bulk non-basmati rice?",
        a: "Yes. We have completed multiple tender supplies into food-aid and government procurement channels across East Africa and Southeast Asia. Documentation is structured to meet tender pre-qualification.",
      },
      {
        q: "What sets your bulk non-basmati rice quality apart?",
        a: "Direct-from-mill sourcing, two-pass sortex cleaning, independent lab COA per shipment, photographic evidence of grading and stuffing, and a documentation packet couriered within 48 hours of sail.",
      },
      {
        q: "Can bulk non-basmati be supplied with custom moisture and broken % spec?",
        a: "Yes. Cuttest spec — moisture, broken percentage, foreign matter, polish, ageing — is locked in the PI and certified on the COA. Buyer approval of sample is mandatory before any commit.",
      },
      {
        q: "Which countries do you supply bulk non-basmati rice to?",
        a: "Gulf (UAE, Saudi Arabia, Oman, Kuwait, Qatar, Bahrain), East Africa (Kenya, Tanzania, Mozambique), West Africa (via trans-shipment), Southeast Asia (Singapore, Malaysia, Indonesia), Maldives, Bangladesh and Sri Lanka.",
      },
    ],
    related: ["non-basmati", "wholesale-rice", "rice-exporter", "sortex-rice"],
    productSchemaName: "Bulk Indian Non-Basmati Rice for Export",
    productSchemaDescription:
      "Bulk non-basmati rice from India — Sona Masuri, IR64, Swarna and idli rice in FCL volumes for distributors, tender buyers and food-service importers. FSSAI/APEDA registered.",
  },

  "private-label-rice": {
    slug: "private-label-rice-exporter-india",
    folio: "№ 24",
    sectionLabel: "PRIVATE LABEL RICE",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "Private Label Rice Exporter from India",
    seoDescription:
      "Private label rice exporter from India — buyer-branded basmati and non-basmati in 5/10/25/50 kg packs. Artwork support, contract pack, FSSAI/APEDA registered.",
    h1: "Private Label Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Private label Indian rice retail packs — Vilora Impex, Gujarat",
    eyebrow: "Buyer-branded rice export for retailers and distributors",
    standfirst:
      "Vilora Impex is a private label rice exporter from India. We pack premium 1121 and 1509 basmati, Sona Masuri, IR64 and sortex-cleaned non-basmati under your brand in 5/10/25/50 kg BOPP-printed laminate, PP, jute or retail cartons. Artwork support, contract-pack scheduling, regulatory-compliant labelling for the Gulf, GCC and ASEAN markets.",
    introH2: "Contract-pack and private-label programs",
    introBody:
      "Your brand on a shelf in Dubai, Doha, Riyadh, Muscat or Kuala Lumpur — packed and shipped under our FSSAI, APEDA, Spices Board and ISO 22000 credentials. We offer two programs: contract-pack (you ship artwork, we pack and dispatch) and co-developed private label (our design partner co-builds the artwork to retail-channel spec). Both include regulatory labelling review for the destination market.",
    productsH2: "Private-label rice varieties we pack",
    products: [
      { name: "1121 Basmati Private Label", spec: "5/10 kg BOPP-printed laminate • Gulf retail premium tier" },
      { name: "1509 Basmati Private Label", spec: "5/10 kg BOPP • HoReCa biryani-chain co-pack" },
      { name: "Sona Masuri Private Label", spec: "5/25 kg PP • South-Asia diaspora retail packs" },
      { name: "IR64 Private Label", spec: "10/25 kg PP and jute • value-tier retail" },
      { name: "Custom Blend Private Label", spec: "Buyer-specified basmati/non-basmati blend ratios" },
      { name: "Bulk Catering Packs", spec: "25/50 kg under buyer brand for HoReCa and government supply" },
    ],
    packagingH2: "Private-label MOQ, artwork and lead time",
    packagingBody:
      "Private-label MOQ is one FCL per SKU (~25 MT). Artwork accepted as PDF, Adobe Illustrator or InDesign — die-line provided on PI. Standard lead time from artwork approval to container stuffing is 14–18 days plus steamer lay-time. Regulatory labelling review for destination market (GCC, ASEAN, East Africa) included at no charge. Incoterms: FOB Mundra/Kandla, CIF and CFR.",
    portsH2: "Where we ship private-label rice",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why retailers choose Vilora Impex for private-label rice",
    why: WHY_DEFAULT,
    faqsH2: "Private-label rice export FAQs",
    faqs: [
      {
        q: "What is the MOQ for private-label rice export?",
        a: "One 20ft FCL container (~25 metric tonnes) per SKU. Multiple SKUs can be consolidated into a single FCL if total volume reaches MOQ; pricing adjusts for additional changeover time.",
      },
      {
        q: "Do you provide artwork support for private-label rice packs?",
        a: "Yes. Buyers supply artwork as PDF, AI or InDesign with our die-line, or our design partner co-develops the artwork to retail-channel spec. Regulatory labelling review for the destination market is included.",
      },
      {
        q: "Which markets do you handle regulatory labelling for?",
        a: "GCC (SFDA, ESMA, MOIAT, Kuwait MOH labelling), ASEAN (Singapore SFA, Malaysia FSQD), East Africa (KEBS, TBS, EAC), Maldives and Sri Lanka. Other markets on request — we engage local compliance partners where needed.",
      },
      {
        q: "What is the lead time for a private-label rice shipment?",
        a: "14–18 days from artwork approval and PI signing to container stuffing at Mundra/Kandla, plus steamer lay-time (typically 7–14 days to Gulf, 18–28 days to Southeast Asia, 21–30 days to East Africa).",
      },
      {
        q: "Can you co-develop the brand and packaging from scratch?",
        a: "Yes. Our design partner has built retail private-label programs for distributors in UAE, Saudi Arabia, Kenya, Singapore and Maldives. Briefing call → moodboards → 2 design rounds → die-line + production-ready files. Charged on a per-SKU basis, refunded against first FCL.",
      },
      {
        q: "Who owns the rights to the artwork after we co-develop?",
        a: "You do. All copyrights and production files transfer to the buyer on first-FCL dispatch. We retain a non-promotional case-study right (no brand logos used externally without permission).",
      },
    ],
    related: ["rice-exporter", "premium-rice", "1121-basmati", "wholesale-rice"],
    productSchemaName: "Private Label Rice Export from India",
    productSchemaDescription:
      "Private-label rice export and contract-pack from India — buyer-branded basmati and non-basmati in 5/10/25/50 kg packs with artwork support and regulatory labelling review.",
  },

  "wholesale-pulses": {
    slug: "wholesale-pulses-supplier-from-india",
    folio: "№ 25",
    sectionLabel: "WHOLESALE PULSES",
    category: "Pulses",
    enrichedSchema: true,
    seoTitle: "Wholesale Pulses Supplier from India",
    seoDescription:
      "Wholesale pulses supplier from India — Toor (Arhar), Chana, Moong, Urad, Masoor dal in FCL volumes. Machine-cleaned, calibrated, FSSAI/APEDA registered.",
    h1: "Wholesale Pulses Supplier from India",
    image: "/hero-rice.jpg",
    imageAlt: "Wholesale Indian pulses export bags — Vilora Impex, Gujarat",
    eyebrow: "Bulk pulses supply for distributors and importers",
    standfirst:
      "Vilora Impex is a wholesale pulses supplier from India — Toor dal (Arhar), Chana (Bengal gram), Moong (green gram), Urad (black gram) and Masoor (red lentil) in FCL volumes. Sourced direct from agricultural belts in Madhya Pradesh, Maharashtra, Rajasthan and Karnataka. Machine-cleaned, calibrated, FSSAI licensed, APEDA registered.",
    introH2: "Bulk pulses supply, end-to-end",
    introBody:
      "Distributor-grade pulses need three things: consistent calibration, dependable cleaning, and reliable origin documentation. Vilora Impex aggregates from agricultural cooperatives and processing partners across India, runs each lot through machine cleaning and calibration to buyer-spec mesh size, and ships under our FSSAI and APEDA credentials with full phytosanitary documentation.",
    productsH2: "Wholesale pulses varieties we export",
    products: [
      { name: "Toor Dal (Arhar / Pigeon Pea)", spec: "Polished split • 99% pure • bulk FCL volumes" },
      { name: "Chana (Bengal Gram)", spec: "Whole and split (Chana Dal) • polished and unpolished" },
      { name: "Moong (Green Gram)", spec: "Whole and split (Moong Dal) • washed and unwashed" },
      { name: "Urad (Black Gram)", spec: "Whole, split (Urad Dal), gota • South-Asia and Gulf demand" },
      { name: "Masoor (Red Lentil)", spec: "Whole and split • machine-cleaned • bulk distributor volumes" },
      { name: "Kabuli Chana (Chickpea)", spec: "8mm and 9mm calibrated • premium retail and HoReCa" },
    ],
    packagingH2: "Wholesale pulses packaging and MOQ",
    packagingBody:
      "FCL MOQ is one 20ft container (~25 MT). LCL trial shipments accepted from 5 MT. Packaging in 25/50 kg PP, jute or buyer-branded sacks. 5/10 kg retail packs available under private label. Incoterms: FOB Mundra/Kandla, CIF and CFR. Payment terms: 30% advance + 70% against BL, or irrevocable L/C at sight.",
    portsH2: "Where we ship wholesale pulses",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why distributors choose Vilora Impex for wholesale pulses",
    why: WHY_DEFAULT,
    faqsH2: "Wholesale pulses supply FAQs",
    faqs: [
      {
        q: "What is the MOQ for wholesale pulses export from India?",
        a: "One 20ft FCL — approximately 25 metric tonnes. LCL consolidated shipments accepted from 5 MT for first-time importers under L/C or 100% advance.",
      },
      {
        q: "Which pulses do you supply in wholesale volumes?",
        a: "Toor dal (Arhar), Chana (Bengal gram), Moong (green gram), Urad (black gram), Masoor (red lentil) and Kabuli chana. Whole and split available, machine-cleaned and calibrated.",
      },
      {
        q: "How are pulses cleaned and calibrated for export?",
        a: "Each lot is run through cyclone destoners, magnetic separators, gravity tables and sieve calibration to mesh size. Output is sampled, lab-tested for moisture, broken percentage and foreign matter, and approved against the PI cuttest before stuffing.",
      },
      {
        q: "Can wholesale pulses be supplied under private label?",
        a: "Yes. Private-label and contract-pack programs in 5/10/25/50 kg in PP or BOPP-printed laminate. MOQ for private label is one FCL per SKU.",
      },
      {
        q: "What documentation accompanies a wholesale pulses shipment?",
        a: "Phytosanitary certificate, FSSAI, APEDA, certificate of origin, Bill of Lading, independent lab COA and machine-cleaning report — couriered within 48 hours of sail.",
      },
      {
        q: "Which countries do you export wholesale pulses to?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Kenya, Tanzania, Singapore, Malaysia, Maldives, Sri Lanka, Bangladesh and Egypt. Gulf and South-Asia diaspora retail are the largest demand pockets.",
      },
    ],
    related: ["pulses-supplier", "indian-agro", "rice-exporter"],
    productSchemaName: "Wholesale Indian Pulses for Export",
    productSchemaDescription:
      "Wholesale pulses supply from India — Toor, Chana, Moong, Urad and Masoor dal in FCL volumes for distributors, importers and retail private-label buyers.",
  },

  "turmeric-powder": {
    slug: "turmeric-powder-exporter-from-india",
    folio: "№ 26",
    sectionLabel: "TURMERIC POWDER",
    category: "Spices",
    enrichedSchema: true,
    seoTitle: "Turmeric Powder Exporter from India",
    seoDescription:
      "Turmeric powder exporter from India — high-curcumin Erode, Salem and Nizamabad varieties. Steam-sterilized, ASTA-tested. FSSAI/APEDA, Spices Board registered.",
    h1: "Turmeric Powder Exporter from India",
    image: "/hero-spices.jpg",
    imageAlt: "Turmeric powder export — Vilora Impex spice desk, Rajkot",
    eyebrow: "High-curcumin Indian turmeric for global buyers",
    standfirst:
      "Vilora Impex is a Spices Board of India accredited turmeric powder exporter, supplying high-curcumin Erode, Salem and Nizamabad varieties to importers, food processors, nutraceutical brands and retail private-label buyers across the Gulf, Middle East, Europe and Southeast Asia. Steam-sterilized, ASTA-tested, curcumin content certified.",
    introH2: "Indian turmeric powder — origin, variety and quality",
    introBody:
      "Indian turmeric isn't a single commodity — it's a family of distinct origins with specific curcumin profiles. Erode (Tamil Nadu) is the global benchmark for curcumin content (3.0%–5.5%). Salem and Nizamabad offer different colour and aroma profiles for retail. We source from named farms and cooperatives, steam-sterilize per FSSAI and importer-country protocol, and certify curcumin content lot-by-lot.",
    productsH2: "Turmeric powder grades we export",
    products: [
      { name: "Erode Manjal Turmeric Powder", spec: "Curcumin 3.0%–5.5% • premium high-colour Tamil Nadu origin" },
      { name: "Salem Turmeric Powder", spec: "Curcumin 2.5%–4.0% • balanced colour and aroma" },
      { name: "Nizamabad Turmeric Powder", spec: "Curcumin 2.0%–3.5% • value retail and HoReCa" },
      { name: "Organic Turmeric Powder", spec: "Certified organic • higher-curcumin lots on contract" },
      { name: "Steam-Sterilized Turmeric", spec: "EU and US-compliant sterilisation for nutraceutical buyers" },
      { name: "Custom Curcumin Spec", spec: "Buyer-locked curcumin %, mesh size and colour on contract" },
    ],
    packagingH2: "Turmeric powder packaging, MOQ and shipment",
    packagingBody:
      "FCL MOQ is one 20ft container (~16 MT of spice powder). LCL trial shipments from 1 MT accepted. Packaging in 25/50 kg multi-wall paper sacks with food-grade liner, 1 kg consumer packs, or buyer-branded private label. Steam-sterilization available for EU, US and Japan buyers. Incoterms: FOB Mundra/Kandla, CIF and CFR. Curcumin content and microbial COA included.",
    portsH2: "Where we ship turmeric powder",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for turmeric powder",
    why: WHY_DEFAULT,
    faqsH2: "Turmeric powder export FAQs",
    faqs: [
      {
        q: "What is the curcumin content of your turmeric powder?",
        a: "Erode 3.0%–5.5%, Salem 2.5%–4.0%, Nizamabad 2.0%–3.5%. Curcumin content is certified lot-by-lot by an independent NABL-accredited laboratory and included in the COA.",
      },
      {
        q: "Do you offer steam-sterilized turmeric powder for EU and US buyers?",
        a: "Yes. We work with FSSAI-licensed steam-sterilization partners using saturated-steam protocols compliant with EU 2073/2005 microbial limits and US FDA FSMA. Sterilization batch logs included.",
      },
      {
        q: "What is the MOQ for turmeric powder export from India?",
        a: "FCL MOQ is approximately 16 metric tonnes (spice powder loads lighter than rice). LCL trial shipments from 1 MT accepted for new buyers.",
      },
      {
        q: "Can turmeric powder be supplied to nutraceutical / pharma buyers?",
        a: "Yes. High-curcumin lots (4.5%+) are available on contract for nutraceutical buyers, with ICH-Q3D heavy-metal testing, pesticide-residue MRL screening and microbial-spec compliance documented per shipment.",
      },
      {
        q: "Do you supply turmeric powder under private label or whole turmeric fingers?",
        a: "Both. Private-label powder in 100g–1kg consumer packs and 25/50 kg bulk; whole turmeric finger and bulb on separate contract. Buyers either supply artwork or co-develop with our packaging desk.",
      },
      {
        q: "Which countries import turmeric powder from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Egypt, Kenya, Tanzania, Singapore, Malaysia, Maldives, Sri Lanka. Nutraceutical buyers in EU, UK, US and Japan on contract for steam-sterilized lots.",
      },
    ],
    related: ["spices", "cumin-seeds", "coriander-powder", "red-chilli"],
    productSchemaName: "Indian Turmeric Powder for Export",
    productSchemaDescription:
      "High-curcumin Indian turmeric powder — Erode, Salem and Nizamabad origins. Steam-sterilized, ASTA-tested, Spices Board of India accredited exporter.",
  },

  "cumin-seeds": {
    slug: "cumin-seeds-exporter-from-india",
    folio: "№ 27",
    sectionLabel: "CUMIN SEEDS",
    category: "Spices",
    enrichedSchema: true,
    seoTitle: "Cumin Seeds Exporter from India",
    seoDescription:
      "Cumin seeds exporter from India — Unjha-origin Singapore Cumin Type-99, machine-cleaned, oil content 2.5%+. Spices Board, FSSAI, APEDA registered.",
    h1: "Cumin Seeds Exporter from India",
    image: "/hero-spices.jpg",
    imageAlt: "Indian cumin (jeera) seeds for export — Vilora Impex spice desk",
    eyebrow: "Gujarat-origin cumin for global spice buyers",
    standfirst:
      "Vilora Impex is a Spices Board of India accredited cumin seeds (jeera) exporter, supplying Unjha-origin Singapore Cumin Type-99 to importers, food processors, retail private-label brands and HoReCa chains across the Gulf, Middle East, Europe and Southeast Asia. Machine-cleaned to 99.5% purity, oil content 2.5%+, foreign matter below 1%, moisture below 10%.",
    introH2: "Indian cumin — origin, grade and oil content",
    introBody:
      "Gujarat (Unjha mandi) is the world's largest cumin trading hub and the origin of Singapore Cumin — the export-grade benchmark variety. Vilora Impex sources directly from Unjha and surrounding districts, runs each lot through gravity cleaning and machine sorting, lab-tests for oil content (BSS sieve and oil-content COA), and dispatches under our Spices Board and APEDA credentials.",
    productsH2: "Cumin seed grades we export",
    products: [
      { name: "Singapore Cumin Type-99", spec: "Premium export-grade • 99.5% purity • oil content 2.5%+" },
      { name: "Machine-Cleaned Cumin", spec: "Standard export grade • 99% purity • oil 2.3%+" },
      { name: "Steam-Sterilized Cumin", spec: "EU and US-compliant sterilisation for retail brands" },
      { name: "Organic Cumin", spec: "Certified organic • premium European and US demand" },
      { name: "Ground Cumin Powder", spec: "Steam-sterilized cumin powder • 60-mesh, retail and HoReCa" },
      { name: "Custom Cumin Spec", spec: "Buyer-locked purity %, oil %, mesh size on contract" },
    ],
    packagingH2: "Cumin packaging, MOQ and shipment",
    packagingBody:
      "FCL MOQ is one 20ft container (~18 MT of cumin seed). LCL trial shipments from 1 MT accepted. Packaging in 25/50 kg multi-wall paper sacks with food-grade liner, jute, or buyer-branded private label. Steam-sterilization available. Incoterms: FOB Mundra/Kandla, CIF and CFR. Oil content COA and microbial spec included per shipment.",
    portsH2: "Where we ship cumin seeds",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for cumin seeds",
    why: WHY_DEFAULT,
    faqsH2: "Cumin seeds export FAQs",
    faqs: [
      {
        q: "What origin is your cumin seed sourced from?",
        a: "Unjha mandi in Gujarat — the world's largest cumin trading hub — and surrounding districts of Mehsana and Banaskantha. We source directly from named cooperatives and aggregators, not anonymous bulk markets.",
      },
      {
        q: "What is Singapore Cumin Type-99?",
        a: "Singapore Cumin Type-99 is the premium export-grade benchmark for Indian cumin: 99.5% purity, oil content 2.5%+, foreign matter below 1%, moisture below 10%. Named for the historical Singapore re-export trade.",
      },
      {
        q: "Do you offer steam-sterilized cumin for EU and US buyers?",
        a: "Yes. Saturated-steam sterilization compliant with EU 2073/2005 microbial limits and US FDA FSMA. Sterilization batch logs, microbial COA and aflatoxin-screening report included.",
      },
      {
        q: "What is the MOQ for cumin seeds export from India?",
        a: "FCL MOQ is approximately 18 metric tonnes. LCL trial shipments from 1 MT accepted for new buyers under L/C or 100% advance.",
      },
      {
        q: "Can cumin be supplied as whole seeds and ground powder in the same shipment?",
        a: "Yes. We consolidate whole-seed and ground-powder SKUs into a single FCL on contract, with separate lot IDs and COAs for each.",
      },
      {
        q: "Which countries import cumin seeds from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Egypt, Kenya, Tanzania, Singapore, Malaysia. Nutraceutical and retail buyers in EU, UK and US on contract for steam-sterilized lots.",
      },
    ],
    related: ["spices", "turmeric-powder", "coriander-powder", "fennel-seeds"],
    productSchemaName: "Indian Cumin Seeds (Jeera) for Export",
    productSchemaDescription:
      "Unjha-origin Singapore Cumin Type-99 — premium export-grade Indian cumin seeds with 99.5% purity and oil content 2.5%+. Spices Board accredited.",
  },

  "red-chilli": {
    slug: "red-chilli-exporter-from-india",
    folio: "№ 28",
    sectionLabel: "RED CHILLI",
    category: "Spices",
    enrichedSchema: true,
    seoTitle: "Red Chilli Exporter from India",
    seoDescription:
      "Red chilli exporter from India — Teja S17, Sannam S4, Byadgi, Guntur varieties. ASTA-colour graded, SHU certified, Spices Board / FSSAI / APEDA registered.",
    h1: "Red Chilli Exporter from India",
    image: "/hero-spices.jpg",
    imageAlt: "Indian red chilli for export — Vilora Impex spice desk",
    eyebrow: "High-colour, high-pungency Indian chilli",
    standfirst:
      "Vilora Impex is a Spices Board of India accredited red chilli exporter — Teja S17, Sannam S4, Byadgi and Guntur varieties, ASTA-colour graded and SHU (Scoville Heat Unit) certified. We supply whole, broken and powdered red chilli to importers, food processors, sauce manufacturers and retail private-label brands across the Gulf, Asia and Africa.",
    introH2: "Indian red chilli — variety, colour and pungency",
    introBody:
      "Indian red chilli is the world's largest export by volume, and variety choice drives price and use case. Teja S17 (Andhra Pradesh) offers high pungency (35,000–40,000 SHU) for sauce and pickle. Sannam S4 balances colour and heat for retail. Byadgi (Karnataka) is famed for deep-red colour with low pungency — premium for paprika replacement. Guntur is the bulk workhorse.",
    productsH2: "Red chilli varieties we export",
    products: [
      { name: "Teja S17 Red Chilli", spec: "High pungency 35,000–40,000 SHU • Andhra Pradesh origin" },
      { name: "Sannam S4 Red Chilli", spec: "Balanced ASTA 100+ colour and 20,000 SHU • retail and HoReCa" },
      { name: "Byadgi Red Chilli", spec: "Deep red ASTA 130+ • low SHU • paprika-grade premium" },
      { name: "Guntur Red Chilli", spec: "Bulk workhorse • mid pungency • value retail and food processing" },
      { name: "Stemless Red Chilli", spec: "Machine-stemmed • premium retail and grinding" },
      { name: "Red Chilli Powder", spec: "Steam-sterilized 60-mesh • ASTA-colour grade • retail packs" },
    ],
    packagingH2: "Red chilli packaging, MOQ and shipment",
    packagingBody:
      "FCL MOQ is one 20ft container (~14 MT for whole chilli, ~18 MT for powder). LCL trial shipments from 1 MT accepted. Packaging in 25/50 kg multi-wall paper or jute sacks, retail packs from 100g–1kg under private label. Incoterms: FOB Mundra/Kandla, CIF and CFR. ASTA-colour COA and SHU certificate per shipment.",
    portsH2: "Where we ship red chilli",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for red chilli",
    why: WHY_DEFAULT,
    faqsH2: "Red chilli export FAQs",
    faqs: [
      {
        q: "Which red chilli variety should I order — Teja, Sannam, Byadgi or Guntur?",
        a: "Sauce, pickle and spicy retail → Teja S17 (35–40k SHU). General retail and HoReCa → Sannam S4 (20k SHU, ASTA 100+). Premium paprika replacement → Byadgi (ASTA 130+, low SHU). Bulk food processing → Guntur. We help match variety to your destination market and end-use.",
      },
      {
        q: "Do you certify ASTA colour and SHU for red chilli exports?",
        a: "Yes. Every shipment ships with an ASTA-colour test report and SHU (Scoville Heat Unit) certificate from an independent NABL-accredited laboratory, alongside the standard documentation packet.",
      },
      {
        q: "Can red chilli be supplied steam-sterilized for EU and US buyers?",
        a: "Yes. Saturated-steam sterilization compliant with EU 2073/2005 microbial limits and US FDA FSMA. Particularly relevant for powdered red chilli destined for retail and food processing.",
      },
      {
        q: "What is the MOQ for red chilli export from India?",
        a: "FCL MOQ is approximately 14 MT for whole red chilli and 18 MT for powder. LCL trial shipments from 1 MT accepted for new buyers.",
      },
      {
        q: "Do you supply red chilli powder under private label?",
        a: "Yes. Steam-sterilized 60-mesh red chilli powder in 100g–1kg consumer packs and 25/50 kg bulk under buyer label. Artwork support included.",
      },
      {
        q: "Which countries import red chilli from Vilora Impex?",
        a: "Sri Lanka, Bangladesh, Malaysia, Indonesia, Singapore, UAE, Saudi Arabia, Egypt, Kenya, Tanzania, Mexico (Byadgi for paprika replacement) and EU buyers on contract for steam-sterilized lots.",
      },
    ],
    related: ["spices", "turmeric-powder", "cumin-seeds", "coriander-powder"],
    productSchemaName: "Indian Red Chilli for Export",
    productSchemaDescription:
      "Indian red chilli — Teja S17, Sannam S4, Byadgi and Guntur varieties. ASTA-colour and SHU certified. Spices Board of India accredited exporter.",
  },

  "coriander-powder": {
    slug: "coriander-powder-exporter-from-india",
    folio: "№ 29",
    sectionLabel: "CORIANDER",
    category: "Spices",
    enrichedSchema: true,
    seoTitle: "Coriander Powder & Seeds Exporter from India",
    seoDescription:
      "Coriander powder and seeds exporter from India — Eagle variety, machine-cleaned, oil content tested. Spices Board, FSSAI, APEDA registered.",
    h1: "Coriander Powder & Seeds Exporter from India",
    image: "/hero-spices.jpg",
    imageAlt: "Indian coriander (dhania) seeds and powder export — Vilora Impex",
    eyebrow: "Premium Rajasthan and Madhya Pradesh coriander",
    standfirst:
      "Vilora Impex is a Spices Board of India accredited coriander (dhania) exporter — Eagle variety from Rajasthan and Madhya Pradesh, machine-cleaned to 99% purity, oil content tested. We supply whole seeds, split, and steam-sterilized 60-mesh powder to importers, food processors and retail private-label buyers across the Gulf, Asia and Africa.",
    introH2: "Indian coriander — variety and quality",
    introBody:
      "Indian coriander is the global benchmark for aroma — Eagle variety from Rajasthan (Ramganj Mandi, Kota) and Madhya Pradesh leads on essential-oil content (0.4%–1.0%). Vilora Impex sources directly from named mandis, runs gravity-clean and sortex sorting, and certifies oil content lot-by-lot via NABL-accredited labs.",
    productsH2: "Coriander grades we export",
    products: [
      { name: "Eagle Whole Coriander Seeds", spec: "Premium aroma • oil content 0.4%–1.0% • Rajasthan origin" },
      { name: "Split Coriander", spec: "Machine-split • bulk food-processing grade" },
      { name: "Coriander Powder", spec: "Steam-sterilized 60-mesh • retail and HoReCa" },
      { name: "Roasted Coriander Powder", spec: "Custom roast for biryani, garam-masala and curry blends" },
      { name: "Organic Coriander", spec: "Certified organic • European retail demand" },
      { name: "Custom Coriander Spec", spec: "Buyer-locked oil %, purity %, mesh size on contract" },
    ],
    packagingH2: "Coriander packaging and MOQ",
    packagingBody:
      "FCL MOQ is one 20ft container (~18 MT). LCL trial shipments from 1 MT accepted. Packaging in 25/50 kg multi-wall paper or jute sacks, retail packs in 100g–1kg under private label. Steam-sterilization available for EU/US. Incoterms: FOB Mundra/Kandla, CIF and CFR.",
    portsH2: "Where we ship coriander",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for coriander",
    why: WHY_DEFAULT,
    faqsH2: "Coriander export FAQs",
    faqs: [
      {
        q: "What is the oil content of your coriander seeds?",
        a: "Eagle whole seeds — oil content 0.4%–1.0% by volatile-oil distillation (NABL-accredited lab). Specific spec locked in the PI cuttest.",
      },
      {
        q: "Do you supply whole coriander or only powder?",
        a: "Both. Whole seeds (Eagle variety), split coriander, and steam-sterilized 60-mesh powder. Custom roasted powder for biryani and garam-masala blends on contract.",
      },
      {
        q: "What is the MOQ for coriander export?",
        a: "FCL MOQ is approximately 18 MT. LCL trial shipments from 1 MT accepted for new buyers under L/C or 100% advance.",
      },
      {
        q: "Can coriander be supplied steam-sterilized for EU buyers?",
        a: "Yes. Saturated-steam sterilization compliant with EU 2073/2005 microbial limits. Microbial-spec COA and sterilization batch log included.",
      },
      {
        q: "Do you offer organic coriander?",
        a: "Yes — on contract. Certified organic whole seeds and powder from NPOP-certified farms in Rajasthan and Madhya Pradesh. NPOP and EU-equivalence documentation included.",
      },
      {
        q: "Which markets import coriander from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Egypt, Kenya, Tanzania, Singapore, Malaysia, Sri Lanka. Retail and HoReCa buyers in EU and UK on contract.",
      },
    ],
    related: ["spices", "turmeric-powder", "cumin-seeds", "red-chilli"],
    productSchemaName: "Indian Coriander Seeds and Powder for Export",
    productSchemaDescription:
      "Indian Eagle-variety coriander seeds and steam-sterilized coriander powder — Spices Board of India accredited exporter.",
  },

  "fennel-seeds": {
    slug: "fennel-seeds-exporter-from-india",
    folio: "№ 30",
    sectionLabel: "FENNEL SEEDS",
    category: "Spices",
    enrichedSchema: true,
    seoTitle: "Fennel Seeds Exporter from India",
    seoDescription:
      "Fennel seeds (saunf) exporter from India — premium green Gujarat-origin saunf, oil content tested. Spices Board, FSSAI, APEDA registered.",
    h1: "Fennel Seeds (Saunf) Exporter from India",
    image: "/hero-spices.jpg",
    imageAlt: "Indian fennel seeds (saunf) for export — Vilora Impex spice desk",
    eyebrow: "Premium Gujarat-origin saunf for global buyers",
    standfirst:
      "Vilora Impex is a Spices Board of India accredited fennel seeds (saunf) exporter — premium green Gujarat-origin fennel with oil content 1.0%+ and length 6–9 mm. We supply whole and split fennel, sortex-cleaned and machine-graded, to importers, mukhwas brands, food processors and retail private-label buyers across the Gulf and Asia.",
    introH2: "Indian fennel — origin and grade",
    introBody:
      "Gujarat (Unjha and surrounding districts) is the largest fennel-producing region in India and the origin of premium green saunf — preferred for mukhwas (after-meal mouth freshener), Indian sweets, biryani and global retail. Vilora Impex sources directly from Unjha, runs sortex cleaning and machine grading by length, and certifies oil content lot-by-lot.",
    productsH2: "Fennel seed grades we export",
    products: [
      { name: "Premium Green Saunf (6–9 mm)", spec: "Long green seed • oil 1.0%+ • mukhwas and retail premium" },
      { name: "Sortex-Cleaned Fennel", spec: "Two-pass sortex • 99.5% purity • food-processing grade" },
      { name: "Split Fennel", spec: "Machine-split • compact volume retail and ingredient buyers" },
      { name: "Polished Fennel", spec: "Polished grain for premium retail packs" },
      { name: "Roasted Fennel", spec: "Lightly roasted for mukhwas and confectionery" },
      { name: "Custom Fennel Spec", spec: "Buyer-locked length, oil %, polish on contract" },
    ],
    packagingH2: "Fennel packaging and MOQ",
    packagingBody:
      "FCL MOQ is one 20ft container (~15 MT). LCL trial shipments from 1 MT accepted. Packaging in 25/50 kg PP or jute sacks, retail in 100g–1kg under private label. Incoterms: FOB Mundra/Kandla, CIF and CFR.",
    portsH2: "Where we ship fennel seeds",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for fennel",
    why: WHY_DEFAULT,
    faqsH2: "Fennel seeds export FAQs",
    faqs: [
      {
        q: "What grade of fennel do you supply for mukhwas brands?",
        a: "Premium green saunf, length 6–9 mm, oil content 1.0%+, two-pass sortex cleaned. The benchmark spec for premium mukhwas private-label retail across the Gulf and Indian diaspora markets.",
      },
      {
        q: "Do you supply organic fennel?",
        a: "Yes — on contract. Certified organic green saunf from NPOP-certified farms with EU-equivalence documentation.",
      },
      {
        q: "What is the MOQ for fennel export?",
        a: "FCL MOQ is approximately 15 MT. LCL trial shipments from 1 MT accepted for new buyers.",
      },
      {
        q: "Can fennel be roasted before packing?",
        a: "Yes. Light-roast and dark-roast profiles available on contract for mukhwas and confectionery buyers. Roast level locked in the PI cuttest.",
      },
      {
        q: "Do you supply fennel under private label?",
        a: "Yes. Private-label and contract-pack programs in 100g–1kg consumer packs and 25/50 kg bulk. Artwork support included.",
      },
      {
        q: "Which countries import fennel from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Singapore, Malaysia, Maldives, Sri Lanka, Bangladesh and Egypt. Mukhwas private-label retail and Indian-diaspora supermarkets drive most volume.",
      },
    ],
    related: ["spices", "cumin-seeds", "coriander-powder"],
    productSchemaName: "Indian Fennel Seeds (Saunf) for Export",
    productSchemaDescription:
      "Premium green Gujarat-origin fennel seeds (saunf) for export — sortex-cleaned, machine-graded. Spices Board of India accredited exporter.",
  },

  "black-pepper": {
    slug: "black-pepper-exporter-from-india",
    folio: "№ 31",
    sectionLabel: "BLACK PEPPER",
    category: "Spices",
    enrichedSchema: true,
    seoTitle: "Black Pepper Exporter from India",
    seoDescription:
      "Black pepper exporter from India — Malabar Garbled Grade-1, Tellicherry, piperine-tested. Spices Board, FSSAI, APEDA registered.",
    h1: "Black Pepper Exporter from India",
    image: "/hero-spices.jpg",
    imageAlt: "Indian black pepper (kali mirch) export — Vilora Impex spice desk",
    eyebrow: "Malabar and Tellicherry black pepper for global buyers",
    standfirst:
      "Vilora Impex is a Spices Board of India accredited black pepper exporter — Malabar Garbled Grade-1 (MG-1) and Tellicherry Extra Bold (TGSEB), piperine-tested, bulk-density verified. We supply whole peppercorn and steam-sterilized ground black pepper to importers, food processors and retail private-label buyers across the Gulf, EU and Asia.",
    introH2: "Indian black pepper — grade and origin",
    introBody:
      "Indian black pepper has been the global benchmark for over two millennia. Modern export specifications centre on three signals: bulk density (>= 500 g/L for premium), piperine content (4.5%+ for export-grade), and bold-grain percentage. Vilora Impex sources from Kerala (Malabar coast) and certifies all three signals lot-by-lot.",
    productsH2: "Black pepper grades we export",
    products: [
      { name: "Malabar Garbled Grade-1 (MG-1)", spec: "Bulk density 500 g/L+ • piperine 4.5%+ • premium export" },
      { name: "Tellicherry Extra Bold (TGSEB)", spec: "Premium bold grain 4.25 mm+ • piperine 4.5%+ • super premium" },
      { name: "Malabar Garbled Grade-2 (MG-2)", spec: "Standard export grade • piperine 4.0%+ • value retail" },
      { name: "Steam-Sterilized Black Pepper", spec: "EU and US-compliant • whole and ground" },
      { name: "Ground Black Pepper", spec: "Steam-sterilized 60-mesh • retail and HoReCa" },
      { name: "Custom Pepper Spec", spec: "Buyer-locked piperine %, bulk density, mesh size on contract" },
    ],
    packagingH2: "Black pepper packaging and MOQ",
    packagingBody:
      "FCL MOQ is one 20ft container (~17 MT). LCL trial shipments from 500 kg accepted (black pepper is high-value, smaller trial sizes common). Packaging in 25/50 kg multi-wall paper sacks with food-grade liner, retail in 50g–500g packs under private label. Incoterms: FOB Mundra/Kandla, CIF and CFR.",
    portsH2: "Where we ship black pepper",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for black pepper",
    why: WHY_DEFAULT,
    faqsH2: "Black pepper export FAQs",
    faqs: [
      {
        q: "What is the difference between MG-1 and Tellicherry black pepper?",
        a: "Both are premium export grades from Kerala. MG-1 (Malabar Garbled Grade-1) is the standard premium spec — bulk density 500 g/L+, piperine 4.5%+. Tellicherry Extra Bold (TGSEB) is super-premium with bold grain 4.25 mm+ — historically reserved for the European spice trade.",
      },
      {
        q: "Do you certify piperine content?",
        a: "Yes. Piperine content (responsible for pungency and aroma) is certified lot-by-lot by an independent NABL-accredited laboratory via HPLC. Standard export grade is 4.0%+; premium is 4.5%+.",
      },
      {
        q: "Can black pepper be supplied steam-sterilized?",
        a: "Yes. Whole peppercorn and ground black pepper both available steam-sterilized for EU (2073/2005) and US FDA FSMA compliance. Sterilization batch log included.",
      },
      {
        q: "What is the MOQ for black pepper export from India?",
        a: "FCL MOQ is approximately 17 MT. Black pepper is high-value, so LCL trial shipments from 500 kg accepted for new buyers under L/C or 100% advance.",
      },
      {
        q: "Do you supply organic black pepper?",
        a: "Yes — on contract. Certified organic Malabar pepper from NPOP-certified farms with EU-equivalence documentation. Premium pricing applies.",
      },
      {
        q: "Which countries import black pepper from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Singapore, Malaysia, Maldives, Sri Lanka. EU retail and US food-processing buyers on contract for steam-sterilized lots.",
      },
    ],
    related: ["spices", "cumin-seeds", "turmeric-powder", "red-chilli"],
    productSchemaName: "Indian Black Pepper (Kali Mirch) for Export",
    productSchemaDescription:
      "Indian Malabar Garbled Grade-1 and Tellicherry Extra Bold black pepper — piperine-tested. Spices Board of India accredited exporter.",
  },

  "sesame-seeds": {
    slug: "sesame-seeds-exporter-from-india",
    folio: "№ 32",
    sectionLabel: "SESAME SEEDS",
    category: "Spices",
    enrichedSchema: true,
    seoTitle: "Sesame Seeds Exporter from India",
    seoDescription:
      "Sesame seeds (til) exporter from India — natural white, hulled, black sesame. Oil content 48%+, sortex-cleaned. APEDA, FSSAI registered.",
    h1: "Sesame Seeds (Til) Exporter from India",
    image: "/hero-spices.jpg",
    imageAlt: "Indian sesame seeds (til) for export — Vilora Impex",
    eyebrow: "Natural, hulled and black sesame for global buyers",
    standfirst:
      "Vilora Impex is an APEDA-registered sesame seeds (til) exporter from India, supplying natural white, hulled (machine-decorticated) and black sesame to oil mills, food processors, bakery ingredient buyers and retail private-label brands across the Gulf, Asia, EU and Latin America. Oil content 48%+, sortex-cleaned to 99.95% purity, aflatoxin-screened.",
    introH2: "Indian sesame — variety, oil content and use",
    introBody:
      "India is among the top three sesame exporters globally. Three product forms drive volume: natural white sesame (oil milling and bakery), hulled (decorticated, premium retail and bakery garnish), and black sesame (Asian cuisine and traditional medicine). Vilora Impex sources from Gujarat and Madhya Pradesh, sortex-cleans and certifies oil content and aflatoxin lot-by-lot.",
    productsH2: "Sesame seed grades we export",
    products: [
      { name: "Natural White Sesame", spec: "Oil content 48%+ • sortex 99.95% purity • oil mill and bakery" },
      { name: "Hulled (Decorticated) Sesame", spec: "Machine-hulled • bakery garnish and premium retail" },
      { name: "Black Sesame Seeds", spec: "Asian cuisine and traditional-medicine demand" },
      { name: "Roasted Sesame", spec: "Custom roast for bakery and confectionery buyers" },
      { name: "Sesame for Tahini Manufacture", spec: "Selected white sesame • oil mill spec for Middle East tahini makers" },
      { name: "Organic Sesame", spec: "Certified organic • EU retail demand" },
    ],
    packagingH2: "Sesame packaging and MOQ",
    packagingBody:
      "FCL MOQ is one 20ft container (~25 MT). LCL trial shipments from 5 MT accepted. Packaging in 25/50 kg PP sacks, retail in 100g–1kg under private label. Incoterms: FOB Mundra/Kandla, CIF and CFR. Aflatoxin and salmonella screening included per shipment.",
    portsH2: "Where we ship sesame seeds",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for sesame",
    why: WHY_DEFAULT,
    faqsH2: "Sesame seeds export FAQs",
    faqs: [
      {
        q: "What is the oil content of your natural white sesame?",
        a: "48%+ on dry basis, certified lot-by-lot by NABL-accredited laboratory. Specific oil content locked in the PI cuttest for oil mill and tahini buyers.",
      },
      {
        q: "Do you supply hulled (decorticated) sesame?",
        a: "Yes. Machine-decorticated to bakery-grade whiteness, sortex-cleaned, suitable for bagel topping, hamburger bun garnish and premium retail.",
      },
      {
        q: "Can sesame be supplied to tahini manufacturers in the Middle East?",
        a: "Yes. Tahini-grade sesame is one of our largest spec contracts — Saudi Arabia, UAE and Jordan tahini makers receive selected white sesame with oil content locked, aflatoxin certified and shipment-frequency aligned to their production cycle.",
      },
      {
        q: "What is the MOQ for sesame seeds export from India?",
        a: "FCL MOQ is approximately 25 MT. LCL trial shipments from 5 MT accepted under L/C or 100% advance.",
      },
      {
        q: "Do you supply organic sesame?",
        a: "Yes — on contract. Certified organic white sesame from NPOP-certified farms with EU-equivalence documentation. Premium pricing applies.",
      },
      {
        q: "Which countries import sesame from Vilora Impex?",
        a: "Saudi Arabia, UAE, Jordan, Lebanon (all for tahini), Singapore, Malaysia, Japan (premium hulled and black sesame), South Korea, EU bakery and Latin America food processing.",
      },
    ],
    related: ["spices", "cumin-seeds", "fennel-seeds"],
    productSchemaName: "Indian Sesame Seeds (Til) for Export",
    productSchemaDescription:
      "Indian natural white, hulled and black sesame seeds for export — oil content 48%+, sortex-cleaned, aflatoxin-screened. APEDA registered exporter.",
  },

  "cashew-nuts": {
    slug: "cashew-nuts-exporter-from-india",
    folio: "№ 33",
    sectionLabel: "CASHEW NUTS",
    category: "Dry Fruit",
    enrichedSchema: true,
    seoTitle: "Cashew Nuts Exporter from India | W180, W240, W320",
    seoDescription:
      "Cashew nuts exporter from India — W180, W210, W240, W320, W400 grades. Vacuum or nitrogen-flushed, aflatoxin-tested. APEDA, FSSAI, ISO 22000.",
    h1: "Cashew Nuts Exporter from India",
    image: "/hero-spices.jpg",
    imageAlt: "Indian cashew nuts W180 W240 W320 for export — Vilora Impex",
    eyebrow: "Premium W180–W400 Indian cashew for global buyers",
    standfirst:
      "Vilora Impex is an APEDA-registered cashew nuts exporter from India, supplying premium W180, W210, W240, W320 and W400 grades to importers, retail private-label brands and food-ingredient buyers across the Gulf, EU and Asia. Vacuum-packed or nitrogen-flushed for shelf life, aflatoxin-screened, microbial-spec certified.",
    introH2: "Indian cashew — grade explained",
    introBody:
      "Cashew kernel grades are defined by count per pound — W180 means 180 whole kernels per pound (largest), W400 means 400 per pound (smallest). Premium retail favours W180–W240 for visible whole-kernel display; W320 is the wholesale workhorse for mixed nut snacks; W400 suits ingredient buyers. Vilora Impex sources from Kerala, Karnataka and Maharashtra processing partners.",
    productsH2: "Cashew grades we export",
    products: [
      { name: "Cashew W180", spec: "Premium jumbo kernel • 180 count/lb • super-premium retail" },
      { name: "Cashew W210", spec: "Premium large kernel • 210 count/lb • premium retail and gift packs" },
      { name: "Cashew W240", spec: "Standard premium • 240 count/lb • retail private-label staple" },
      { name: "Cashew W320", spec: "Wholesale workhorse • 320 count/lb • mixed nut and ingredient" },
      { name: "Cashew W400", spec: "Smaller kernel • 400 count/lb • food-ingredient and bakery" },
      { name: "Cashew Splits, Pieces and LWP", spec: "Splits, butts, pieces and large-white-piece for ingredient buyers" },
    ],
    packagingH2: "Cashew packaging and MOQ",
    packagingBody:
      "FCL MOQ is one 20ft container (~18 MT). LCL trial shipments from 1 MT accepted. Packaging in 11.34 kg (25 lb) vacuum tins, 22.68 kg (50 lb) double-flexi vacuum pouches, or nitrogen-flushed 25 kg cartons. Retail packs in 100g–1kg under private label. Incoterms: FOB Mundra/Kandla, CIF and CFR. Aflatoxin and microbial COA per shipment.",
    portsH2: "Where we ship cashew nuts",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for cashew",
    why: WHY_DEFAULT,
    faqsH2: "Cashew nuts export FAQs",
    faqs: [
      {
        q: "Which cashew grade should I import — W180, W240 or W320?",
        a: "Super-premium retail and gift packs → W180. Standard premium retail → W240 (the global private-label staple). Mixed nut and ingredient buyers → W320. Bakery and food processing → W400 or pieces.",
      },
      {
        q: "How is cashew packed to preserve shelf life?",
        a: "11.34 kg (25 lb) vacuum tins for retail-staging, 22.68 kg (50 lb) double-flexi vacuum pouches for bulk, or nitrogen-flushed 25 kg cartons. Vacuum and nitrogen flush both deliver 9–12 month shelf life at controlled storage.",
      },
      {
        q: "Is your cashew aflatoxin-screened?",
        a: "Yes. Every shipment ships with an aflatoxin COA from a NABL-accredited laboratory (B1, B2, G1, G2 and total). EU 1881/2006 and FDA limits as standard, tighter spec on contract.",
      },
      {
        q: "What is the MOQ for cashew export from India?",
        a: "FCL MOQ is approximately 18 MT. LCL trial shipments from 1 MT accepted under L/C or 100% advance.",
      },
      {
        q: "Do you supply cashew under private label?",
        a: "Yes. Private-label retail packs in 100g–1kg under buyer brand. Vacuum-sealed or nitrogen-flushed retail packs available with custom artwork.",
      },
      {
        q: "Which countries import cashew from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, EU (Germany, Netherlands, UK), Australia, Singapore, Japan, Maldives. Gulf retail and EU retail private label drive most volume.",
      },
    ],
    related: ["dry-fruits", "indian-agro"],
    productSchemaName: "Indian Cashew Nuts W180–W400 for Export",
    productSchemaDescription:
      "Premium Indian cashew kernels — W180, W210, W240, W320, W400 — vacuum or nitrogen-flushed, aflatoxin-screened. APEDA registered exporter.",
  },

  "toor-dal": {
    slug: "toor-dal-exporter-from-india",
    folio: "№ 34",
    sectionLabel: "TOOR DAL",
    category: "Pulses",
    enrichedSchema: true,
    seoTitle: "Toor Dal (Arhar) Exporter from India",
    seoDescription:
      "Toor dal (Arhar / pigeon pea) exporter from India — polished and unpolished split. Machine-cleaned, FCL volumes. FSSAI, APEDA registered.",
    h1: "Toor Dal (Arhar) Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Toor dal (Arhar) Indian pulses export — Vilora Impex, Gujarat",
    eyebrow: "Polished toor dal for global Indian-diaspora demand",
    standfirst:
      "Vilora Impex is an APEDA-registered toor dal (Arhar / pigeon pea) exporter from India, supplying polished and unpolished split toor dal to wholesale distributors, Indian-diaspora retailers and HoReCa chains across the Gulf, East Africa, Maldives and Southeast Asia. Machine-cleaned to 99% purity, polish and moisture certified.",
    introH2: "Indian toor dal — origin, polish and quality",
    introBody:
      "Toor dal is the cornerstone of South Asian and Indian-diaspora diets, and India is the largest producer globally. Vilora Impex sources from Maharashtra, Karnataka and Madhya Pradesh, runs each lot through gravity cleaning, polish (or supplies unpolished for organic-leaning retail), and certifies moisture, broken percentage and foreign matter lot-by-lot.",
    productsH2: "Toor dal grades we export",
    products: [
      { name: "Polished Toor Dal", spec: "Premium polished • 99% purity • Gulf retail and diaspora standard" },
      { name: "Unpolished Toor Dal", spec: "Natural finish • organic-leaning retail and HoReCa" },
      { name: "Oil-Polished Toor Dal", spec: "Light oil polish for premium shelf appearance" },
      { name: "Premium Whole Arhar", spec: "Whole pigeon pea (toor whole) • limited demand niche" },
      { name: "Organic Toor Dal", spec: "Certified organic • EU and US diaspora retail" },
      { name: "Custom Toor Spec", spec: "Buyer-locked polish, moisture %, broken % on contract" },
    ],
    packagingH2: "Toor dal packaging and MOQ",
    packagingBody:
      "FCL MOQ is one 20ft container (~25 MT). LCL trial shipments from 5 MT accepted. Packaging in 25/50 kg PP or jute sacks, retail in 1/5/10 kg PP or BOPP-printed under private label. Incoterms: FOB Mundra/Kandla, CIF and CFR. Payment terms: 30% advance + 70% against BL.",
    portsH2: "Where we ship toor dal",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why distributors choose Vilora Impex for toor dal",
    why: WHY_DEFAULT,
    faqsH2: "Toor dal export FAQs",
    faqs: [
      {
        q: "What is the difference between polished, oil-polished and unpolished toor dal?",
        a: "Polished toor dal is mechanically buffed for shelf appearance — the global retail standard. Oil-polished uses a light edible-oil sheen for premium shelf appearance. Unpolished retains the natural finish — preferred by organic-leaning retail and HoReCa chefs.",
      },
      {
        q: "What is the MOQ for toor dal export from India?",
        a: "FCL MOQ is approximately 25 metric tonnes. LCL trial shipments from 5 MT accepted for new buyers under L/C or 100% advance.",
      },
      {
        q: "Do you supply organic toor dal?",
        a: "Yes — on contract. Certified organic toor dal from NPOP-certified farms with EU and US equivalence documentation. Premium pricing applies.",
      },
      {
        q: "What documentation accompanies a toor dal export shipment?",
        a: "Phytosanitary, FSSAI, APEDA, certificate of origin, Bill of Lading, independent lab COA (moisture, broken %, foreign matter, aflatoxin) and polish-finish log — couriered within 48 hours of sail.",
      },
      {
        q: "Can toor dal be supplied under private label retail packs?",
        a: "Yes. Private-label 1/5/10 kg consumer packs in PP or BOPP-printed laminate under buyer brand. Artwork support included, regulatory labelling for destination market reviewed at no charge.",
      },
      {
        q: "Which countries import toor dal from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Kenya, Tanzania, Maldives, Sri Lanka, Bangladesh, Singapore, Malaysia, USA and UK (for Indian-diaspora retail).",
      },
    ],
    related: ["wholesale-pulses", "pulses-supplier", "indian-agro"],
    productSchemaName: "Indian Toor Dal (Arhar) for Export",
    productSchemaDescription:
      "Indian toor dal (Arhar / pigeon pea) — polished, oil-polished and unpolished split. FCL volumes for distributors and Indian-diaspora retail. APEDA registered.",
  },

  "sona-masuri": {
    slug: "sona-masuri-rice-exporter-india",
    folio: "№ 35",
    sectionLabel: "SONA MASURI",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "Sona Masuri Rice Exporter from India",
    seoDescription:
      "Sona Masuri rice exporter from India — premium medium-grain non-basmati, low starch, sortex-cleaned. FSSAI, APEDA registered. FOB Mundra/Kandla.",
    h1: "Sona Masuri Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Sona Masuri Indian rice export — Vilora Impex, Gujarat",
    eyebrow: "Medium-grain Sona Masuri for South-Asia and Gulf demand",
    standfirst:
      "Vilora Impex is an APEDA-registered Sona Masuri rice exporter from India, supplying premium medium-grain low-starch Sona Masuri to importers, retail private-label brands and HoReCa chains across the Gulf, Maldives and Southeast Asia. Sourced from Andhra Pradesh and Telangana milling partners, sortex-cleaned, aged 6 months minimum.",
    introH2: "Sona Masuri — characteristics and demand",
    introBody:
      "Sona Masuri is India's premium medium-grain non-basmati — grain length 5.0–5.5 mm, low starch content, low glycemic index relative to other non-basmati. The South-Asia diaspora and the Gulf middle class are the largest demand pools. Vilora Impex sources direct from Andhra Pradesh (Nellore, Krishna delta) and Telangana, two-pass sortex-cleans and dispatches under our FSSAI/APEDA credentials.",
    productsH2: "Sona Masuri grades we export",
    products: [
      { name: "Premium Sona Masuri (Raw)", spec: "Natural milled • aged 6 months • low starch • sortex-cleaned" },
      { name: "Sona Masuri Steam", spec: "Steam-processed • improved cooking texture" },
      { name: "Sona Masuri Parboiled", spec: "Parboiled medium-grain • premium retail and HoReCa" },
      { name: "HMT Sona Masuri", spec: "Premium HMT-variant Sona Masuri • super-premium retail" },
      { name: "Organic Sona Masuri", spec: "Certified organic • EU and US diaspora retail" },
      { name: "Custom Sona Masuri", spec: "Buyer-locked moisture %, broken %, polish on contract" },
    ],
    packagingH2: "Sona Masuri packaging, MOQ and shipment",
    packagingBody:
      "FCL MOQ is one 20ft container (~25 MT). LCL trial shipments from 5 MT accepted. Packaging in 5/10/25/50 kg PP, BOPP-printed, jute or buyer-branded retail. Incoterms: FOB Mundra/Kandla, CIF and CFR.",
    portsH2: "Where we ship Sona Masuri rice",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for Sona Masuri",
    why: WHY_DEFAULT,
    faqsH2: "Sona Masuri rice export FAQs",
    faqs: [
      {
        q: "What is Sona Masuri rice?",
        a: "Sona Masuri is India's premium medium-grain non-basmati rice — grain length 5.0–5.5 mm, low starch, low glycemic index. Particularly preferred by South-Asia diaspora, Gulf middle class, and health-conscious retail consumers.",
      },
      {
        q: "How does Sona Masuri differ from basmati?",
        a: "Basmati is long-grain (1121 at 8.30 mm+), aromatic, premium-priced. Sona Masuri is medium-grain (5.0–5.5 mm), low-starch, less aromatic — a different culinary use case (everyday meals vs. occasion-cooking) at a different price point.",
      },
      {
        q: "What is the MOQ for Sona Masuri export?",
        a: "FCL MOQ is one 20ft container (~25 MT). LCL trial shipments from 5 MT accepted for new buyers.",
      },
      {
        q: "Do you supply HMT-variant Sona Masuri?",
        a: "Yes — on contract. HMT (Hyderabadi) Sona Masuri is a premium sub-variety with finer grain and improved aroma, sourced direct from Hyderabad-area milling partners.",
      },
      {
        q: "Can Sona Masuri be supplied under private label?",
        a: "Yes. Private-label retail packs in 5/10/25 kg PP or BOPP-printed laminate under buyer brand. Artwork support and destination-market labelling review included.",
      },
      {
        q: "Which countries import Sona Masuri from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Singapore, Malaysia, Maldives, Sri Lanka, USA and UK (for Indian-diaspora retail).",
      },
    ],
    related: ["non-basmati", "rice-exporter", "wholesale-rice"],
    productSchemaName: "Indian Sona Masuri Rice for Export",
    productSchemaDescription:
      "Premium medium-grain Sona Masuri rice from India — sortex-cleaned, aged. APEDA registered exporter shipping FOB Mundra/Kandla.",
  },

  "kesar-mango": {
    slug: "kesar-mango-exporter-from-india",
    folio: "№ 36",
    sectionLabel: "KESAR MANGO",
    category: "Fresh Produce",
    enrichedSchema: true,
    seoTitle: "Kesar Mango Exporter from India",
    seoDescription:
      "Kesar mango exporter from India — Junagadh and Saurashtra origin, GAP-compliant, pre-cooled, hot-water-treated. APEDA registered cold-chain export.",
    h1: "Kesar Mango Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Kesar mango from Junagadh Gujarat for export — Vilora Impex",
    eyebrow: "Junagadh Kesar mango — the saffron-coloured premium",
    standfirst:
      "Vilora Impex is an APEDA-registered Kesar mango exporter from India — sourcing from named GAP-compliant orchards in Junagadh, Talala and Saurashtra (Gujarat), the home of true Kesar. Hot-water-treated (HWT) for fruit-fly compliance, pre-cooled at origin, reefer-shipped at 13 °C with daily temperature logging.",
    introH2: "Kesar mango — origin protection and quality",
    introBody:
      "Kesar mango is a Geographical Indication (GI) protected variety — the saffron-coloured flesh and the distinctive aroma come from Gir-Junagadh terroir. Vilora Impex contracts directly with growers in Junagadh, Talala, Sutrapada and Veraval. Each box is hand-graded, hot-water-treated for non-EU markets / VHT-treated for EU and Japan, pre-cooled, and packed in ventilated 5-kg corrugated cartons.",
    productsH2: "Kesar mango grades we export",
    products: [
      { name: "Kesar Mango — Premium Grade A", spec: "200–300g • uniform saffron flesh • GAP-compliant orchards" },
      { name: "Kesar Mango — Premium Grade B", spec: "150–200g • retail standard premium" },
      { name: "VHT-Treated Kesar (EU/Japan)", spec: "Vapour-heat-treated for fruit-fly compliance • EU and Japan markets" },
      { name: "HWT-Treated Kesar (Gulf/ASEAN)", spec: "Hot-water-treated for non-EU fruit-fly compliance" },
      { name: "Reefer-Pack Kesar", spec: "Pre-cooled, reefer-shipped, daily temperature log" },
      { name: "Air-Freight Kesar", spec: "Premium air-freight to UAE / Singapore / Hong Kong for retail" },
    ],
    packagingH2: "Kesar mango packaging and MOQ",
    packagingBody:
      "Pre-cooled and reefer-shipped at 13 °C. Standard pack is 5 kg ventilated corrugated carton — 6–10 fruits per carton depending on grade. Reefer MOQ is one 40ft container (~1,800–2,200 cartons). Air-freight from 100 cartons accepted to premium destinations. Incoterms: FOB Mundra/Mumbai, CIF and CFR.",
    portsH2: "Where we ship Kesar mango",
    portsBody: "Sea reefer from Mundra (Gujarat) and Nhava Sheva (Maharashtra) to: Jebel Ali (UAE), Dammam (Saudi Arabia), Doha (Qatar), Salalah (Oman), Singapore, Port Klang (Malaysia). Air-freight to: UAE, Singapore, Hong Kong, Tokyo, London Heathrow (EU/UK via VHT compliance).",
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for Kesar mango",
    why: WHY_DEFAULT,
    faqsH2: "Kesar mango export FAQs",
    faqs: [
      {
        q: "Is your Kesar mango GI-protected and from Junagadh?",
        a: "Yes. We contract directly with GAP-compliant orchards in Junagadh, Talala, Sutrapada and Veraval — the protected geographical region for true Kesar. Origin certificate and orchard list provided per shipment.",
      },
      {
        q: "Do you supply VHT-treated Kesar mango for EU and Japan?",
        a: "Yes. Vapour-heat-treated Kesar mango compliant with EU and Japan phytosanitary protocols for fruit-fly. VHT batch log and treatment certificate included per shipment.",
      },
      {
        q: "What is the shelf life of Kesar mango after harvest?",
        a: "10–14 days under reefer conditions at 13 °C. Air-freight reaches Gulf in 6 hours, Singapore in 8 hours, UK / EU in 12 hours — leaves 7–10 days of retail shelf life at destination.",
      },
      {
        q: "When is Kesar mango season?",
        a: "Peak harvest: May to July (Indian summer monsoon onset). Limited early-season fruit available from late April; tail-end stock through early August.",
      },
      {
        q: "What is the MOQ for Kesar mango export?",
        a: "Sea reefer FCL: one 40ft container (~1,800–2,200 cartons). Air-freight: from 100 cartons. Sea trial shipments from 500 cartons accepted on a case-by-case basis for new importers.",
      },
      {
        q: "Which countries import Kesar mango from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Singapore, Malaysia, Maldives, UK (via VHT), Germany, Netherlands, Japan (via VHT), Hong Kong (premium air-freight retail).",
      },
    ],
    related: ["mango", "alphonso-mango", "fresh-produce"],
    productSchemaName: "Kesar Mango from Junagadh, Gujarat — for Export",
    productSchemaDescription:
      "Junagadh-origin Kesar mango — GI-protected variety, GAP-compliant orchards, HWT/VHT treated, reefer-shipped from Gujarat. APEDA registered cold-chain exporter.",
  },

  "alphonso-mango": {
    slug: "alphonso-mango-exporter-from-india",
    folio: "№ 37",
    sectionLabel: "ALPHONSO MANGO",
    category: "Fresh Produce",
    enrichedSchema: true,
    seoTitle: "Alphonso Mango Exporter from India",
    seoDescription:
      "Alphonso mango exporter from India — Ratnagiri and Devgad origin, GAP-compliant, hot-water-treated. APEDA registered cold-chain export.",
    h1: "Alphonso Mango Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Alphonso mango Ratnagiri Devgad for export — Vilora Impex",
    eyebrow: "Ratnagiri-Devgad Alphonso — the king of mangoes",
    standfirst:
      "Vilora Impex is an APEDA-registered Alphonso (Hapus) mango exporter from India — sourcing from named GAP-compliant orchards in Ratnagiri and Devgad (Maharashtra Konkan), the Geographical-Indication-protected home of true Alphonso. Hot-water-treated (HWT) for non-EU fruit-fly compliance, VHT-treated for EU/Japan, pre-cooled, reefer-shipped at 13 °C.",
    introH2: "Alphonso mango — GI protection and quality",
    introBody:
      "Alphonso is a GI-protected variety with a saffron-orange flesh, distinctive aroma and the highest international demand of any Indian mango. Vilora Impex contracts directly with GAP-compliant orchards in Ratnagiri, Devgad, Sindhudurg and adjacent Konkan coastal districts. Each fruit is hand-graded, HWT/VHT-treated per destination protocol, pre-cooled and packed in 4-kg ventilated cartons.",
    productsH2: "Alphonso mango grades we export",
    products: [
      { name: "Alphonso — Premium Grade A", spec: "220–280g • uniform saffron flesh • Ratnagiri / Devgad origin" },
      { name: "Alphonso — Premium Grade B", spec: "180–220g • retail standard premium" },
      { name: "VHT-Treated Alphonso (EU/Japan)", spec: "Vapour-heat-treated for fruit-fly compliance" },
      { name: "HWT-Treated Alphonso (Gulf/ASEAN)", spec: "Hot-water-treated for non-EU fruit-fly compliance" },
      { name: "Reefer-Pack Alphonso", spec: "Pre-cooled, reefer-shipped at 13 °C, daily temperature log" },
      { name: "Air-Freight Alphonso", spec: "Premium air-freight to UAE / Singapore / Hong Kong / London" },
    ],
    packagingH2: "Alphonso mango packaging and MOQ",
    packagingBody:
      "Pre-cooled and reefer-shipped at 13 °C. Standard pack is 4 kg ventilated corrugated carton — 12 fruits per carton (Grade A) or 16 fruits (Grade B). Reefer MOQ is one 40ft container (~2,000–2,400 cartons). Air-freight from 100 cartons. Incoterms: FOB Mumbai (Nhava Sheva) / Mundra, CIF and CFR.",
    portsH2: "Where we ship Alphonso mango",
    portsBody: "Sea reefer from Nhava Sheva (Mumbai) and Mundra to: Jebel Ali (UAE), Dammam (Saudi Arabia), Doha (Qatar), Salalah (Oman), Singapore, Port Klang. Air-freight to UAE, Singapore, Hong Kong, Tokyo, London Heathrow.",
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose Vilora Impex for Alphonso",
    why: WHY_DEFAULT,
    faqsH2: "Alphonso mango export FAQs",
    faqs: [
      {
        q: "Is your Alphonso from Ratnagiri / Devgad?",
        a: "Yes. We contract directly with GAP-compliant orchards in Ratnagiri, Devgad, Sindhudurg and adjacent Konkan coastal districts — the GI-protected region for true Alphonso. Orchard list and origin certificate per shipment.",
      },
      {
        q: "Do you supply VHT-treated Alphonso for EU and Japan?",
        a: "Yes. Vapour-heat-treated Alphonso compliant with EU and Japan phytosanitary protocols. VHT batch log and treatment certificate per shipment.",
      },
      {
        q: "When is Alphonso mango season?",
        a: "Peak harvest: late March to early June. Earliest export shipments begin late February for premium air-freight; main sea-reefer season is April–May.",
      },
      {
        q: "What is the shelf life of Alphonso after harvest?",
        a: "10–12 days under reefer conditions at 13 °C. Air-freight delivers 7–10 days of retail shelf life at destination.",
      },
      {
        q: "What is the MOQ for Alphonso mango export?",
        a: "Sea reefer FCL: one 40ft container (~2,000–2,400 cartons). Air-freight: from 100 cartons. Sea trial shipments from 500 cartons on a case-by-case basis.",
      },
      {
        q: "Which countries import Alphonso from Vilora Impex?",
        a: "UAE, Saudi Arabia, Qatar, Oman, Kuwait, Bahrain, Singapore, Malaysia, Maldives, UK (via VHT), Germany, Netherlands, Japan (via VHT), Hong Kong, USA (via VHT, irradiation alternative).",
      },
    ],
    related: ["mango", "kesar-mango", "fresh-produce"],
    productSchemaName: "Alphonso Mango from Ratnagiri-Devgad, Maharashtra — for Export",
    productSchemaDescription:
      "Ratnagiri-Devgad Alphonso (Hapus) mango — GI-protected, GAP-compliant orchards, HWT/VHT treated, reefer-shipped. APEDA registered cold-chain exporter.",
  },

  // ===== Wave 3 (2026-05-22): variety × processing matrix =====
  // SKRM's URL pattern: each variety gets dedicated landing pages per
  // processing form (Raw / Steam / Sella / Golden Sella). Each combo
  // serves a different destination market and price tier — content is
  // unique per page, not paraphrased.

  "1121-raw": {
    slug: "1121-raw-basmati-rice",
    folio: "№ 42",
    sectionLabel: "1121 RAW",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1121 Raw Basmati Rice Exporter from India",
    seoDescription:
      "1121 Raw Basmati Rice exporter from India — natural milled white grain, 8.30 mm+ length, aged 12 months. Iranian, Iraqi and traditional cuisine grade.",
    h1: "1121 Raw Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1121-steam.jpg",
    imageAlt: "1121 Raw Basmati Rice from India — Vilora Impex",
    eyebrow: "Natural milled long-grain 1121 — aroma-forward",
    standfirst: "Vilora Impex exports 1121 Raw Basmati Rice — the natural-milled, non-parboiled form of India's longest-grain basmati cultivar. Bright white grain, maximum aroma, softer cooked texture. The traditional choice for Iranian chelow, Iraqi timman, and any preparation where aroma is the headline spec rather than cook-stability.",
    introH2: "What 1121 Raw means",
    introBody: "Raw basmati skips the parboiling step that produces Sella's golden hue. The grain stays bright white, aromatic compounds remain at peak, and the cooked grain is softer and fluffier than Sella. Shelf life is shorter (12–18 months vs 18–24 for Sella) but aroma retention is unmatched. Aged 12 months minimum at the milling partner before packing.",
    productsH2: "1121 Raw grades we export",
    products: [
      { name: "1121 Raw — Premium Aged", spec: "Long grain 8.30 mm+ • aged 12 months • <2% broken • two-pass sortex" },
      { name: "1121 Raw — Super Premium", spec: "Aged 18 months • cuttest-locked grain length • ultra-premium retail" },
      { name: "1121 Raw — Iranian Spec", spec: "Iranian-cuisine specification: longer cook, polo / chelow grade" },
      { name: "1121 Raw — UK Diaspora Retail", spec: "Heritage white retail packs for Indian-diaspora supermarkets" },
      { name: "1121 Raw Tibar (Broken)", spec: "Sortex-cleaned brokens for affordability and food-ingredient buyers" },
      { name: "Custom 1121 Raw Spec", spec: "Buyer-locked grain length, broken %, moisture, polish, ageing" },
    ],
    packagingH2: "1121 Raw packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Packaging in 5/10/25/50 kg PP, BOPP-printed laminate, jute, cotton or buyer-branded private label. Raw basmati is moisture-sensitive — BOPP laminate recommended for tropical destinations. Incoterms: FOB Mundra/Kandla, CIF, CFR. Documentation packet within 48 hours of sail.",
    portsH2: "Where we ship 1121 Raw basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers choose us for 1121 Raw basmati",
    why: WHY_DEFAULT,
    faqsH2: "1121 Raw basmati export FAQs",
    faqs: [
      { q: "What's the difference between 1121 Raw and 1121 Sella?", a: "Raw is the natural milled white grain, with maximum aroma but shorter shelf life and softer cooked texture. Sella is parboiled (steamed in the husk before milling) — golden tone, harder grain, longer shelf life, more cook-stable. Both are the same 1121 cultivar at the same grain length." },
      { q: "Which destinations prefer 1121 Raw?", a: "Iran (chelow, polo), Iraq (timman), UK Indian-diaspora retail, USA Indian-grocery channels, and traditional Indian restaurant supply where aroma is the headline spec." },
      { q: "What is the shelf life of 1121 Raw?", a: "12–18 months in cool, dry, sealed storage. Tropical humidity shortens this. Rotate stock every quarter at the importer's warehouse." },
      { q: "Can 1121 Raw be supplied at higher ageing?", a: "Yes. 18-month and 24-month aged 1121 Raw is available on contract for ultra-premium retail and traditional restaurant accounts. Premium of 8–15% over standard 12-month applies." },
    ],
    related: ["1121-basmati", "1121-supplier", "premium-rice", "1121-steam"],
    productSchemaName: "1121 Raw Basmati Rice from India",
    productSchemaDescription: "Premium aged 1121 Raw Basmati — natural milled white long grain, 8.30 mm+, aged 12 months. FSSAI and APEDA registered exporter.",
  },

  "1121-steam": {
    slug: "1121-steam",
    folio: "№ 43",
    sectionLabel: "1121 STEAM",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1121 Steam Basmati Rice Exporter from India",
    seoDescription:
      "1121 Steam Basmati Rice exporter from India — steam-processed creamy-white long grain 8.30 mm+. HoReCa biryani-chain and premium retail benchmark.",
    h1: "1121 Steam Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1121-steam.jpg",
    imageAlt: "1121 Steam Basmati Rice from India — Vilora Impex",
    eyebrow: "Steam-processed creamy white — HoReCa benchmark",
    standfirst: "Vilora Impex exports 1121 Steam Basmati Rice — the steam-processed creamy-white form of India's longest-grain basmati. Aroma comparable to Raw, cook-stability comparable to Sella, visual appearance suited to markets that resist golden-coloured rice. The biryani-chain default and the premium retail standard.",
    introH2: "What 1121 Steam means",
    introBody: "Steam basmati is milled and then gently steam-processed — not parboiled. The grain stays creamy-white, retains aromatic compounds well, and gains some of Sella's cooking stability. Cooks in 18–22 minutes, holds shape through layered biryani dum, and survives chafing-dish service better than Raw.",
    productsH2: "1121 Steam grades we export",
    products: [
      { name: "1121 Steam — Premium HoReCa", spec: "Long grain 8.30 mm+ • aged 12 months • <2% broken • biryani-chain spec" },
      { name: "1121 Steam — Gulf Premium Retail", spec: "5 kg BOPP-printed retail packs for Gulf supermarket premium tier" },
      { name: "1121 Steam — UK Indian Retail", spec: "Heritage white retail for UK Indian-diaspora private label" },
      { name: "1121 Steam — Singapore Spec", spec: "Singapore SFA-compliant labelled retail packs" },
      { name: "1121 Steam — Bulk HoReCa", spec: "25/50 kg PP for biryani-chain volume supply" },
      { name: "Custom 1121 Steam Cuttest", spec: "Buyer-locked grain length, broken %, moisture, ageing, polish" },
    ],
    packagingH2: "1121 Steam packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Standard HoReCa pack is 25 kg PP woven; premium retail uses 5 kg and 10 kg BOPP-printed laminate. Private-label MOQ is one FCL per SKU. Incoterms: FOB Mundra/Kandla, CIF and CFR. Documentation within 48 hours of sail.",
    portsH2: "Where we ship 1121 Steam basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why HoReCa buyers specify 1121 Steam",
    why: WHY_DEFAULT,
    faqsH2: "1121 Steam basmati export FAQs",
    faqs: [
      { q: "Why does HoReCa prefer 1121 Steam over 1121 Raw?", a: "Steam basmati has the aroma of Raw with the cook-stability of Sella. In volume HoReCa service where the cook can be variable, Steam holds shape better than Raw, which is more sensitive to over-cooking." },
      { q: "What's the difference between 1121 Steam and 1121 Sella?", a: "Both are heat-processed but differently. Sella is parboiled (steam through the husk before milling) — golden, hardest grain, longest shelf life. Steam is milled first, then gently steamed — creamy-white, slightly softer than Sella, more aromatic. Steam is the in-between option." },
      { q: "Which destinations prefer 1121 Steam?", a: "Singapore retail, Malaysia retail, UK Indian-diaspora supermarkets, premium UAE retail brands that prefer white-not-golden, and HoReCa biryani chains across the Gulf and Southeast Asia." },
      { q: "What is the shelf life of 1121 Steam?", a: "16–22 months in cool, dry storage — between Raw (12–18) and Sella (18–24). The light steam processing extends shelf life modestly while preserving aroma." },
    ],
    related: ["1121-basmati", "1121-raw", "1121-sella", "premium-rice"],
    productSchemaName: "1121 Steam Basmati Rice from India",
    productSchemaDescription: "1121 Steam Basmati — steam-processed creamy-white long grain 8.30 mm+, aged 12 months. HoReCa and premium retail benchmark. FSSAI/APEDA registered.",
  },

  "1121-sella": {
    slug: "1121-sella-basmati-rice",
    folio: "№ 44",
    sectionLabel: "1121 SELLA",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1121 Sella Basmati Rice Exporter from India",
    seoDescription:
      "1121 Sella Basmati Rice exporter from India — parboiled creamy long grain 8.30 mm+, premium aged. Gulf retail standard, harder grain, longer shelf life.",
    h1: "1121 Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg",
    imageAlt: "1121 Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Parboiled creamy — Gulf retail standard",
    standfirst: "Vilora Impex exports 1121 Sella Basmati Rice — the parboiled (creamy or light-tone) form of India's longest-grain basmati. Hardest grain, longest shelf life, the global Gulf retail standard. When supermarket chains across UAE, Saudi Arabia and Qatar specify '1121 Sella', this is the spec.",
    introH2: "What 1121 Sella means",
    introBody: "Sella is parboiled — paddy partially boiled in the husk before milling. Steam pushes nutrients into the endosperm and gelatinises the starch, producing a hardened grain. Sella appears creamy-white through to light-golden depending on parboil intensity. Cook-stable through long dum, longer shelf life than Raw or Steam, the spec for high-volume Gulf retail.",
    productsH2: "1121 Sella grades we export",
    products: [
      { name: "1121 Sella — Creamy Tone", spec: "Light parboil, creamy-tone grain • Gulf retail private label" },
      { name: "1121 Sella — Premium Aged", spec: "Aged 12 months minimum • <2% broken • premium retail and HoReCa" },
      { name: "1121 Sella — UAE Spec", spec: "UAE ESMA / MOIAT-compliant labelled retail packs" },
      { name: "1121 Sella — Saudi Spec", spec: "Saudi SFDA-registered retail SKUs (per importer registration)" },
      { name: "1121 Sella — Bulk Distributor", spec: "25/50 kg PP for wholesale distributor channels" },
      { name: "Custom 1121 Sella Cuttest", spec: "Buyer-locked grain length, broken %, ageing, parboil intensity" },
    ],
    packagingH2: "1121 Sella packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Standard Gulf retail pack: 5 kg or 10 kg BOPP-printed laminate. Bulk distributor: 25 or 50 kg PP woven, sometimes jute. Private-label MOQ is one FCL per SKU. Halal certification from MOIAT-accepted body included for Gulf retail.",
    portsH2: "Where we ship 1121 Sella basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why Gulf retail standardises on 1121 Sella",
    why: WHY_DEFAULT,
    faqsH2: "1121 Sella basmati export FAQs",
    faqs: [
      { q: "What's the difference between Sella, Creamy Sella and Golden Sella?", a: "All are parboiled. Light parboil yields creamy-white tone (Creamy Sella). Standard parboil yields a light-golden tone (Sella). Deep parboil yields deep-golden (Golden Sella). The deeper the parboil, the harder the grain and the longer the shelf life — but also stronger colour, which some markets resist." },
      { q: "Is 1121 Sella among the most-imported rice in Saudi Arabia?", a: "Yes. Saudi consumers strongly prefer parboiled 1121 over Steam or Raw. SKUs registered with SFDA in 5 kg and 10 kg BOPP retail packs move the highest volume." },
      { q: "What is the shelf life of 1121 Sella?", a: "18–24 months in cool, dry storage. The parboiling process slows aroma degradation, making Sella the longest-shelf-life form of basmati." },
      { q: "Can 1121 Sella ship to EU and US markets?", a: "Yes — under EU 2073/2005 microbial compliance and US FDA FSMA. We engage NABL-accredited lab partners for pesticide-residue MRL and microbial spec testing on EU/US-bound lots." },
    ],
    related: ["1121-basmati", "1121-steam", "1121-golden-sella", "premium-rice"],
    productSchemaName: "1121 Sella Basmati Rice from India",
    productSchemaDescription: "1121 Sella Basmati — parboiled creamy/light-golden long grain 8.30 mm+, aged 12 months. Gulf retail and HoReCa standard. FSSAI/APEDA registered.",
  },

  "1121-golden-sella": {
    slug: "1121-golden-sella-basmati-rice",
    folio: "№ 45",
    sectionLabel: "1121 GOLDEN SELLA",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1121 Golden Sella Basmati Rice Exporter from India",
    seoDescription:
      "1121 Golden Sella Basmati Rice exporter from India — deep-golden parboiled long grain 8.30 mm+. Saudi Arabia, UAE and Gulf retail premium spec.",
    h1: "1121 Golden Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg",
    imageAlt: "1121 Golden Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Deep-golden parboiled — Saudi and UAE premium",
    standfirst: "Vilora Impex exports 1121 Golden Sella Basmati Rice — the deep-golden, intensely parboiled form of India's longest-grain basmati. The premium-tier spec for Saudi Arabian, UAE and broader Gulf retail. Hardest grain, deepest colour, longest shelf life, highest cook-stability.",
    introH2: "What 1121 Golden Sella means",
    introBody: "Golden Sella is parboiled at a higher intensity than standard Sella — the paddy is steamed deeper in the husk before milling, forcing more nutrients and pigments into the endosperm. Result: deep-amber to dark-golden grain, the hardest endosperm of any basmati form, shelf life beyond 24 months, exceptional cook stability through any dum or layered preparation.",
    productsH2: "1121 Golden Sella grades we export",
    products: [
      { name: "1121 Golden Sella — Deep Gold", spec: "Maximum colour intensity • Saudi premium retail standard" },
      { name: "1121 Golden Sella — Standard", spec: "Mid-tone golden • UAE and Qatar retail premium" },
      { name: "1121 Golden Sella — Aged 12+", spec: "Aged 12 months minimum • <2% broken • ultra-premium retail" },
      { name: "1121 Golden Sella — Bulk", spec: "25/50 kg PP for wholesale distributor channels" },
      { name: "1121 Golden Sella — Gift Pack", spec: "1/2/5 kg specialty cotton or BOPP-printed gift packs" },
      { name: "Custom Golden Sella", spec: "Buyer-locked colour intensity, grain length, broken %" },
    ],
    packagingH2: "1121 Golden Sella packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Saudi retail typically specifies 5 kg and 10 kg BOPP-printed laminate with bilingual (Arabic + English) labelling per SFDA. UAE and Qatar prefer the same with MOIAT/ESMA compliance. Gift-pack 1 kg and 2 kg in cotton or premium BOPP for Ramadan / Hajj-season retail. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship 1121 Golden Sella",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why Saudi retail standardises on Golden Sella",
    why: WHY_DEFAULT,
    faqsH2: "1121 Golden Sella export FAQs",
    faqs: [
      { q: "What makes Golden Sella different from regular Sella?", a: "Both are parboiled, but Golden Sella uses a deeper, longer parboil process — more steam, deeper penetration into the grain endosperm. Result: deeper golden colour, harder grain, longer shelf life, higher cook-stability. Premium of 8–15% per MT over standard Sella applies." },
      { q: "Is Golden Sella the Saudi premium spec?", a: "Yes. Saudi retail consumers strongly prefer deep-golden parboiled basmati. The deeper the colour, the higher the perceived premium. SFDA-registered 5 kg and 10 kg BOPP packs in deep-golden 1121 dominate Saudi supermarket shelves." },
      { q: "What is the shelf life of Golden Sella?", a: "24+ months in cool, dry, sealed storage. The deepest parboil produces the most stable grain — Golden Sella is the longest-shelf-life form of basmati available." },
      { q: "Does Golden Sella affect cooking behaviour?", a: "Yes — Golden Sella requires 25–28 minutes cook time at a 1:2.0 water ratio for proper bloom. Slower cook than Steam or Raw, but the cooked grain is the firmest and holds shape best through long preparations." },
    ],
    related: ["1121-basmati", "1121-sella", "1121-steam", "premium-rice"],
    productSchemaName: "1121 Golden Sella Basmati Rice from India",
    productSchemaDescription: "1121 Golden Sella Basmati — deep-golden parboiled long grain 8.30 mm+, aged 12 months. Saudi and UAE premium retail spec. FSSAI/APEDA registered.",
  },

  "1509-raw": {
    slug: "1509-raw",
    folio: "№ 46",
    sectionLabel: "1509 RAW",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1509 Raw Basmati Rice Exporter from India",
    seoDescription:
      "1509 Raw Basmati Rice exporter from India — natural milled white grain 8.0–8.4 mm, short-duration premium basmati. Value-retail and diaspora supply.",
    h1: "1509 Raw Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg",
    imageAlt: "1509 Raw Basmati Rice from India — Vilora Impex",
    eyebrow: "Natural milled 1509 — value-premium retail",
    standfirst: "Vilora Impex exports 1509 Raw Basmati Rice — the natural-milled white form of India's short-duration premium basmati cultivar. Slightly shorter grain than 1121 (8.0–8.4 mm), faster ageing cycle (6 months minimum), lower price point, same aroma-forward character. The value-premium choice for retail private label and Indian-diaspora supermarket supply.",
    introH2: "What 1509 Raw means",
    introBody: "Raw means natural milled, not parboiled — the bright white grain closest to the freshly-milled state. 1509 has a faster ageing cycle than 1121 (6 months vs 12), which means lower inventory cost at the milling partner and a lower per-MT price for the buyer. Aroma is excellent; grain length is 8.0–8.4 mm, visibly long but slightly under 1121's 8.30 mm+ benchmark.",
    productsH2: "1509 Raw grades we export",
    products: [
      { name: "1509 Raw — Standard Premium", spec: "8.0–8.4 mm grain • aged 6 months • value-premium retail" },
      { name: "1509 Raw — Aged 12+", spec: "Extended ageing for premium-positioned retail SKUs" },
      { name: "1509 Raw — UK Diaspora", spec: "Indian-diaspora supermarket private label, UK, USA" },
      { name: "1509 Raw — Bulk", spec: "25/50 kg PP for wholesale distributors" },
      { name: "1509 Raw — Custom Blend Base", spec: "Used in 1121/1509 blends for retail price-point engineering" },
      { name: "Custom 1509 Raw Spec", spec: "Buyer-locked grain length, broken %, polish" },
    ],
    packagingH2: "1509 Raw packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Packaging in 5/10/25/50 kg PP, BOPP-printed laminate, jute or buyer-branded private label. Raw basmati is moisture-sensitive — BOPP laminate recommended for tropical destinations. Incoterms: FOB Mundra/Kandla, CIF and CFR.",
    portsH2: "Where we ship 1509 Raw basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why retailers blend 1509 Raw into 1121 for price points",
    why: WHY_DEFAULT,
    faqsH2: "1509 Raw basmati export FAQs",
    faqs: [
      { q: "How does 1509 Raw compare to 1121 Raw?", a: "1509 grain is 8.0–8.4 mm; 1121 is 8.30 mm+. 1509 ages faster (6 months vs 12) — lower cost. Aroma is similar, slightly less aged-character on 1509. Use 1509 Raw for value-retail and 1121 Raw for premium tier." },
      { q: "Can 1509 Raw be blended with 1121?", a: "Yes — custom 1121/1509 Raw blends are common for retail brands locking specific shelf price points. Blend ratios are written into the PI and certified on the lab COA." },
      { q: "Which destinations prefer 1509 Raw?", a: "Indian-diaspora retail (UK, USA, Australia), value-tier Gulf retail, ASEAN private label, and Indian restaurant supply where the kitchen prefers Raw over Sella." },
      { q: "What is the shelf life of 1509 Raw?", a: "10–16 months in cool, dry storage — slightly shorter than 1121 Raw due to the faster ageing cycle. Rotate quarterly in tropical retail environments." },
    ],
    related: ["1509-basmati", "1121-raw", "premium-rice", "1509-steam"],
    productSchemaName: "1509 Raw Basmati Rice from India",
    productSchemaDescription: "1509 Raw Basmati — natural milled white long grain 8.0–8.4 mm, short-duration premium basmati. Value-retail standard. FSSAI/APEDA registered.",
  },

  "1509-steam": {
    slug: "1509-steam",
    folio: "№ 47",
    sectionLabel: "1509 STEAM",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1509 Steam Basmati Rice Exporter from India",
    seoDescription:
      "1509 Steam Basmati Rice exporter from India — steam-processed creamy-white short-duration premium basmati. HoReCa biryani-chain value spec.",
    h1: "1509 Steam Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg",
    imageAlt: "1509 Steam Basmati Rice from India — Vilora Impex",
    eyebrow: "Steam-processed 1509 — HoReCa value tier",
    standfirst: "Vilora Impex exports 1509 Steam Basmati Rice — the steam-processed creamy-white form of India's short-duration premium basmati. The HoReCa value-tier alternative to 1121 Steam — same cook-stability and creamy appearance, lower price point. Increasingly the spec for biryani-chain expansion and ASEAN HoReCa supply.",
    introH2: "What 1509 Steam means",
    introBody: "Steam basmati is milled and then steam-processed — creamy-white grain with cook-stability between Raw and Sella. 1509 Steam combines this stability with the variety's natural value-pricing (faster ageing cycle, lower inventory cost). The result: a HoReCa-grade basmati at a price point that opens new restaurant accounts and retail private-label slots.",
    productsH2: "1509 Steam grades we export",
    products: [
      { name: "1509 Steam — HoReCa Premium", spec: "8.0–8.4 mm • aged 6 months • cook-stable creamy white" },
      { name: "1509 Steam — Retail Standard", spec: "5/10 kg BOPP-printed retail packs" },
      { name: "1509 Steam — Singapore Spec", spec: "Singapore SFA-compliant labelled retail SKUs" },
      { name: "1509 Steam — Malaysia Spec", spec: "Halal-certified retail packs for Malaysia FSQD" },
      { name: "1509 Steam — Bulk HoReCa", spec: "25/50 kg PP for biryani-chain volume" },
      { name: "Custom 1509 Steam", spec: "Buyer-locked spec, can be blended with 1121 Steam" },
    ],
    packagingH2: "1509 Steam packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). HoReCa standard 25 kg PP woven; retail in 5 kg and 10 kg BOPP-printed laminate. Private-label MOQ is one FCL per SKU. Halal certification from MOIAT or JAKIM-accepted body included for Gulf and ASEAN retail.",
    portsH2: "Where we ship 1509 Steam basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why HoReCa adopts 1509 Steam for expansion",
    why: WHY_DEFAULT,
    faqsH2: "1509 Steam basmati export FAQs",
    faqs: [
      { q: "Why use 1509 Steam over 1121 Steam?", a: "Price. 1509 Steam is typically 15–25% cheaper per MT than 1121 Steam, with the same cooking behaviour and similar visual appearance. Best for HoReCa chains opening additional locations or retail SKUs hitting a specific shelf price." },
      { q: "Is 1509 Steam acceptable for biryani service?", a: "Yes — at HoReCa biryani-chain level it's a fully acceptable spec. The grain holds shape through dum, cooks consistently, and visually presents as a long-grain basmati. Premium biryani houses still prefer 1121 Steam for the marginal grain-length advantage." },
      { q: "Can 1509 Steam be blended with 1121 Steam?", a: "Yes — custom blends are standard for retail brands locking specific price points. 70/30 1509/1121 blends, 50/50, and other ratios per buyer spec." },
      { q: "What is the shelf life of 1509 Steam?", a: "14–18 months in cool, dry storage — slightly less than 1121 Steam due to the variety's faster ageing cycle." },
    ],
    related: ["1509-basmati", "1121-steam", "1509-raw", "premium-rice"],
    productSchemaName: "1509 Steam Basmati Rice from India",
    productSchemaDescription: "1509 Steam Basmati — steam-processed creamy-white long grain 8.0–8.4 mm, short-duration premium. HoReCa value-tier standard. FSSAI/APEDA registered.",
  },

  "1509-sella": {
    slug: "1509-sella-basmati-rice",
    folio: "№ 48",
    sectionLabel: "1509 SELLA",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1509 Sella Basmati Rice Exporter from India",
    seoDescription:
      "1509 Sella Basmati Rice exporter from India — parboiled long grain 8.0–8.4 mm, value-tier Gulf retail spec. Cook-stable, longer shelf life, lower price.",
    h1: "1509 Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg",
    imageAlt: "1509 Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Parboiled 1509 — value-tier Gulf retail",
    standfirst: "Vilora Impex exports 1509 Sella Basmati Rice — the parboiled form of India's short-duration premium basmati cultivar. Value-tier alternative to 1121 Sella with the same parboiled-grain cook-stability and long shelf life, at a meaningfully lower per-MT price. The volume choice for retail private label hitting affordable shelf prices in Gulf and ASEAN markets.",
    introH2: "What 1509 Sella means",
    introBody: "1509 Sella is parboiled — paddy steamed in the husk before milling. Hardened grain, creamy through to light-golden tone, 18-month+ shelf life. Compared to 1121 Sella, slightly shorter grain (8.0–8.4 mm) and faster ageing cycle (6 months vs 12) — both contribute to a lower per-MT FOB price.",
    productsH2: "1509 Sella grades we export",
    products: [
      { name: "1509 Sella — Creamy", spec: "Light parboil • value-tier retail and HoReCa" },
      { name: "1509 Sella — Standard", spec: "Mid-tone parboil • Gulf retail private label" },
      { name: "1509 Sella — Bulk Distributor", spec: "25/50 kg PP for wholesale distribution channels" },
      { name: "1509 Sella — UAE Spec", spec: "ESMA/MOIAT-compliant labelled retail packs" },
      { name: "1509 Sella — Custom Blend Base", spec: "Used in 1121/1509 Sella blends for shelf-price engineering" },
      { name: "Custom 1509 Sella Cuttest", spec: "Buyer-locked spec, parboil intensity, ageing" },
    ],
    packagingH2: "1509 Sella packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Gulf retail: 5 kg and 10 kg BOPP-printed laminate with bilingual labelling. Bulk distributor: 25 or 50 kg PP woven. Private-label MOQ one FCL per SKU. Halal certification included for Gulf retail SKUs.",
    portsH2: "Where we ship 1509 Sella basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why volume retail picks 1509 Sella",
    why: WHY_DEFAULT,
    faqsH2: "1509 Sella basmati export FAQs",
    faqs: [
      { q: "How does 1509 Sella compare to 1121 Sella in performance?", a: "Same parboiled cook-stability and creamy appearance. 1509 Sella has slightly shorter grain (8.0–8.4 mm vs 8.30 mm+) which is visible to a careful eye but doesn't affect cooking behaviour. The main difference is price — 1509 Sella is 15–22% cheaper per MT." },
      { q: "Can 1509 Sella replace 1121 Sella for retail private label?", a: "Yes for value-tier private label and budget retail SKUs. Premium-tier private label (super-premium gift packs, top-shelf retail) typically stays with 1121 Sella for the grain-length advantage. Many retailers carry both as separate SKUs at different price points." },
      { q: "Which destinations import 1509 Sella in volume?", a: "Egypt, Iraq, Yemen, Jordan, Kenya, Tanzania — markets where the price-per-kg is the primary purchasing driver. Also expanding in ASEAN value-retail (Indonesia, Malaysia)." },
      { q: "What is the shelf life of 1509 Sella?", a: "16–22 months in cool, dry storage. Comparable to 1121 Sella, slightly shorter due to the faster cycle but still well within retail rotation cycles." },
    ],
    related: ["1509-basmati", "1121-sella", "1509-steam", "1509-golden-sella"],
    productSchemaName: "1509 Sella Basmati Rice from India",
    productSchemaDescription: "1509 Sella Basmati — parboiled long grain 8.0–8.4 mm, short-duration premium. Value-tier Gulf retail standard. FSSAI/APEDA registered.",
  },

  "1509-golden-sella": {
    slug: "1509-golden-sella-basmati-rice",
    folio: "№ 49",
    sectionLabel: "1509 GOLDEN SELLA",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1509 Golden Sella Basmati Rice Exporter from India",
    seoDescription:
      "1509 Golden Sella Basmati Rice exporter from India — deep-golden parboiled long grain 8.0–8.4 mm, value-tier Gulf retail premium colour spec.",
    h1: "1509 Golden Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg",
    imageAlt: "1509 Golden Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Deep-golden 1509 — value-premium tier",
    standfirst: "Vilora Impex exports 1509 Golden Sella Basmati Rice — the deep-golden parboiled form of India's short-duration premium basmati. The value-tier alternative to 1121 Golden Sella for buyers serving Gulf retail consumers who prefer deep-golden colour at a more accessible price point.",
    introH2: "What 1509 Golden Sella means",
    introBody: "Golden Sella is the deeper-parboiled form — intense steam treatment producing dark-golden grain with the longest shelf life of any basmati form. 1509 Golden Sella delivers this premium-tier colour and cook-stability at a value-tier price relative to 1121 Golden Sella. Used by retail brands launching deep-golden Sella SKUs at competitive shelf prices.",
    productsH2: "1509 Golden Sella grades we export",
    products: [
      { name: "1509 Golden Sella — Deep Gold", spec: "Maximum colour • value-tier Saudi and UAE retail" },
      { name: "1509 Golden Sella — Mid Tone", spec: "Balanced golden • Gulf wholesale distributor" },
      { name: "1509 Golden Sella — Bulk", spec: "25/50 kg PP for wholesale" },
      { name: "1509 Golden Sella — Retail Private Label", spec: "5/10 kg BOPP-printed under buyer brand" },
      { name: "1509 Golden Sella — Custom Cuttest", spec: "Buyer-locked colour intensity and grain spec" },
      { name: "1121/1509 Golden Sella Blend", spec: "Custom blend ratios for retail price-point engineering" },
    ],
    packagingH2: "1509 Golden Sella packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Retail packaging in 5 kg and 10 kg BOPP-printed laminate; bulk in 25 or 50 kg PP. Private-label MOQ one FCL per SKU. SFDA-compliant labelling for Saudi retail; MOIAT/ESMA for UAE.",
    portsH2: "Where we ship 1509 Golden Sella",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why retail picks 1509 Golden Sella",
    why: WHY_DEFAULT,
    faqsH2: "1509 Golden Sella export FAQs",
    faqs: [
      { q: "Why use 1509 Golden Sella over 1121 Golden Sella?", a: "Pricing — 1509 Golden Sella runs 18–28% cheaper per MT than 1121 Golden Sella, with the same deep-golden visual signature consumers expect. Best for retail brands launching value-tier Golden Sella SKUs or accounts under tight margin pressure." },
      { q: "Is the cooking behaviour the same?", a: "Very similar. Both Golden Sellas require 25–28 minutes cook at 1:2.0 water ratio. 1509 Golden Sella grain is marginally shorter, but the cooked grain reaches comparable expansion." },
      { q: "Which markets import 1509 Golden Sella in volume?", a: "Iraq, Yemen, Egypt, Jordan, value-tier Saudi retail, East Africa diaspora retail." },
      { q: "What's the shelf life?", a: "22+ months in cool, dry storage. The deep parboil produces the most-stable grain at the 1509 price point." },
    ],
    related: ["1509-basmati", "1509-sella", "1121-golden-sella"],
    productSchemaName: "1509 Golden Sella Basmati Rice from India",
    productSchemaDescription: "1509 Golden Sella Basmati — deep-golden parboiled long grain 8.0–8.4 mm. Value-tier Gulf retail premium-colour spec. FSSAI/APEDA registered.",
  },

  "1718-raw": {
    slug: "1718-raw-basmati-rice",
    folio: "№ 50",
    sectionLabel: "1718 RAW",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1718 Raw Basmati Rice Exporter from India",
    seoDescription:
      "1718 Raw Basmati Rice exporter from India — disease-resistant 1121 successor in natural milled white form. Premium retail and diaspora supply.",
    h1: "1718 Raw Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "1718 Raw Basmati Rice from India — Vilora Impex",
    eyebrow: "Disease-resistant successor — natural milled",
    standfirst: "Vilora Impex exports 1718 Raw Basmati Rice — the natural-milled white form of Pusa Basmati 1718, the disease-resistant successor to 1121. Same long-grain visual signature, same aroma profile, with better disease resistance on the farm that translates into more consistent supply quality lot-to-lot.",
    introH2: "What 1718 Raw means",
    introBody: "Pusa Basmati 1718 was released by IARI in 2018 as a disease-resistant 1121 successor — same grain length (8.30 mm+), same aroma family, with significantly improved resistance to bacterial blight. For exporters, this means more consistent fresh-crop supply and fewer lots rejected for disease. Raw is the natural-milled white form, retaining maximum aroma.",
    productsH2: "1718 Raw grades we export",
    products: [
      { name: "1718 Raw — Premium Aged", spec: "8.30 mm+ grain • aged 12 months • <2% broken • two-pass sortex" },
      { name: "1718 Raw — Fresh Crop", spec: "Current-season 1718 supply at the milling partner" },
      { name: "1718 Raw — UK Diaspora Retail", spec: "Indian-diaspora supermarket retail packs" },
      { name: "1718 Raw — Bulk Distributor", spec: "25/50 kg PP for wholesale channels" },
      { name: "1718 Raw — Custom Spec", spec: "Buyer-locked grain length, broken %, polish, ageing" },
      { name: "1121/1718 Raw Blend", spec: "Custom blend for supply-resilience programs" },
    ],
    packagingH2: "1718 Raw packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Packaging same as 1121 Raw: 5/10/25/50 kg PP, BOPP-printed laminate, jute, cotton or buyer-branded private label. Raw basmati is moisture-sensitive — BOPP laminate recommended for tropical destinations. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship 1718 Raw basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers pick 1718 over 1121",
    why: WHY_DEFAULT,
    faqsH2: "1718 Raw basmati export FAQs",
    faqs: [
      { q: "Is 1718 the same as 1121?", a: "Same family, different cultivar release. 1718 is the disease-resistant successor to 1121 — same grain length (8.30 mm+), same aroma family, with better farm-level disease resistance. Pricing typically tracks 1121 at the same tier." },
      { q: "Why would I specify 1718 over 1121?", a: "Supply consistency. 1718 production has lower disease-rejection rates at the milling level, meaning fewer lots fail QA and supply is more reliable lot-to-lot. Buyers running large repeat-order programs sometimes specify 1718 for risk management." },
      { q: "Can I blend 1121 with 1718?", a: "Yes — many retail brands run 1121/1718 blended SKUs for supply resilience. The cultivars are visually and behaviourally close enough that a blend is indistinguishable to retail consumers." },
      { q: "Is 1718 a Pusa variety?", a: "Yes — Pusa Basmati 1718 is the formal varietal name. Developed at the Indian Agricultural Research Institute (IARI), Pusa, Delhi. Commercial trade refers to it simply as 1718." },
    ],
    related: ["1121-raw", "premium-rice", "pusa-basmati-supply", "1121-basmati"],
    productSchemaName: "1718 Raw Basmati Rice from India",
    productSchemaDescription: "1718 Raw Basmati — disease-resistant 1121 successor in natural milled white form, 8.30 mm+, aged 12 months. FSSAI/APEDA registered.",
  },

  "1718-steam": {
    slug: "1718-steam-basmati-rice",
    folio: "№ 51",
    sectionLabel: "1718 STEAM",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1718 Steam Basmati Rice Exporter from India",
    seoDescription:
      "1718 Steam Basmati Rice exporter from India — steam-processed creamy-white form of disease-resistant 1121 successor. HoReCa and premium retail.",
    h1: "1718 Steam Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "1718 Steam Basmati Rice from India — Vilora Impex",
    eyebrow: "Steam 1718 — HoReCa supply resilience",
    standfirst: "Vilora Impex exports 1718 Steam Basmati Rice — the steam-processed creamy-white form of Pusa Basmati 1718, the disease-resistant 1121 successor. Same HoReCa biryani-chain cook behaviour as 1121 Steam, with the supply-resilience advantage of a disease-resistant cultivar.",
    introH2: "What 1718 Steam means",
    introBody: "Steam basmati is milled and gently steam-processed — creamy-white grain with cook-stability between Raw and Sella. 1718 Steam delivers the same HoReCa cooking behaviour as 1121 Steam at typically the same pricing tier, with the supply-resilience benefit of the disease-resistant 1718 cultivar.",
    productsH2: "1718 Steam grades we export",
    products: [
      { name: "1718 Steam — Premium HoReCa", spec: "8.30 mm+ • aged 12 months • biryani-chain cook-stable spec" },
      { name: "1718 Steam — Retail Premium", spec: "5/10 kg BOPP-printed retail packs" },
      { name: "1718 Steam — Singapore Spec", spec: "Singapore SFA-compliant labelled retail SKUs" },
      { name: "1718 Steam — Bulk HoReCa", spec: "25/50 kg PP for biryani-chain volume" },
      { name: "1121/1718 Steam Blend", spec: "Custom blend for supply-resilience programs" },
      { name: "Custom 1718 Steam Cuttest", spec: "Buyer-locked grain spec" },
    ],
    packagingH2: "1718 Steam packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). HoReCa standard 25 kg PP woven; retail in 5 kg and 10 kg BOPP-printed. Private-label MOQ one FCL per SKU. Halal certification included for Gulf and ASEAN retail.",
    portsH2: "Where we ship 1718 Steam basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why HoReCa chains adopt 1718 Steam for stability",
    why: WHY_DEFAULT,
    faqsH2: "1718 Steam basmati export FAQs",
    faqs: [
      { q: "Should I switch from 1121 Steam to 1718 Steam?", a: "Not necessarily switch — but consider 1718 Steam as a supply-resilience parallel. Many HoReCa chains carry both, switching between them based on milling-partner availability and disease pressure in any given crop year." },
      { q: "Does 1718 Steam taste different from 1121 Steam?", a: "Essentially indistinguishable at the table. Same aroma family, same grain length, same cooking behaviour. The cultivar difference matters at the farm and mill, not on the plate." },
      { q: "Can private-label artwork stay the same when switching?", a: "Yes — if your private label says 'Indian Basmati Rice' or '1121-grade basmati', the artwork remains valid. Some buyers explicitly print '1121 or equivalent' on labels for supply flexibility." },
      { q: "Shelf life?", a: "16–22 months — same as 1121 Steam. The disease-resistance trait doesn't affect post-mill behaviour." },
    ],
    related: ["1121-steam", "1718-raw", "1718-sella", "premium-rice"],
    productSchemaName: "1718 Steam Basmati Rice from India",
    productSchemaDescription: "1718 Steam Basmati — disease-resistant 1121 successor in steam-processed form, 8.30 mm+, aged 12 months. HoReCa benchmark. FSSAI/APEDA registered.",
  },

  "1718-sella": {
    slug: "1718-sella-basmati-rice",
    folio: "№ 52",
    sectionLabel: "1718 SELLA",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1718 Sella Basmati Rice Exporter from India",
    seoDescription:
      "1718 Sella Basmati Rice exporter from India — parboiled creamy long grain 8.30 mm+, disease-resistant 1121 successor. Gulf retail supply resilience.",
    h1: "1718 Sella Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "1718 Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Parboiled 1718 — Gulf retail resilience",
    standfirst: "Vilora Impex exports 1718 Sella Basmati Rice — the parboiled creamy-to-light-golden form of Pusa Basmati 1718, the disease-resistant 1121 successor. Same Gulf retail standard as 1121 Sella with the supply-resilience benefit of the newer cultivar.",
    introH2: "What 1718 Sella means",
    introBody: "Sella is parboiled — steam through the husk before milling. 1718 Sella delivers the same parboiled cook-stability, long shelf life, and creamy-to-light-golden tone as 1121 Sella, with the cultivar's improved disease resistance reducing supply variability lot-to-lot.",
    productsH2: "1718 Sella grades we export",
    products: [
      { name: "1718 Sella — Premium Aged", spec: "8.30 mm+ • aged 12 months • Gulf retail standard" },
      { name: "1718 Sella — UAE Spec", spec: "MOIAT/ESMA-compliant labelled retail packs" },
      { name: "1718 Sella — Saudi Spec", spec: "SFDA-registered retail SKUs" },
      { name: "1718 Sella — Bulk Distributor", spec: "25/50 kg PP for wholesale" },
      { name: "1121/1718 Sella Blend", spec: "Custom blend for supply-resilience programs" },
      { name: "Custom 1718 Sella Spec", spec: "Buyer-locked grain spec, parboil intensity, ageing" },
    ],
    packagingH2: "1718 Sella packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Gulf retail: 5 kg and 10 kg BOPP-printed laminate with bilingual labelling. Bulk: 25 or 50 kg PP. Private-label MOQ one FCL per SKU.",
    portsH2: "Where we ship 1718 Sella basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why volume retail diversifies into 1718 Sella",
    why: WHY_DEFAULT,
    faqsH2: "1718 Sella basmati export FAQs",
    faqs: [
      { q: "Is 1718 Sella interchangeable with 1121 Sella?", a: "Functionally yes — same grain length, same parboiled behaviour, same retail appearance. Buyers running large retail programs increasingly carry both for supply diversification." },
      { q: "Does Golden Sella exist in 1718?", a: "Yes — 1718 Golden Sella is also available; see the dedicated page. Same deep-golden behaviour as 1121 Golden Sella with 1718's supply-resilience advantage." },
      { q: "Pricing relative to 1121 Sella?", a: "Typically same tier or 1–3% below 1121 Sella depending on crop-year supply dynamics. 1718 has been pricing slightly lower in fresh-crop seasons due to its supply consistency advantage." },
      { q: "Shelf life?", a: "18–24 months — same as 1121 Sella." },
    ],
    related: ["1121-sella", "1718-steam", "1718-golden-sella", "1718-raw"],
    productSchemaName: "1718 Sella Basmati Rice from India",
    productSchemaDescription: "1718 Sella Basmati — disease-resistant 1121 successor in parboiled form, 8.30 mm+, aged 12 months. Gulf retail standard. FSSAI/APEDA registered.",
  },

  "1718-golden-sella": {
    slug: "1718-golden-sella-basmati-rice",
    folio: "№ 53",
    sectionLabel: "1718 GOLDEN SELLA",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "1718 Golden Sella Basmati Rice Exporter from India",
    seoDescription:
      "1718 Golden Sella Basmati Rice exporter from India — deep-golden parboiled long grain 8.30 mm+. Disease-resistant 1121 successor for Saudi premium retail.",
    h1: "1718 Golden Sella Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "1718 Golden Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Deep-golden 1718 — Saudi retail resilience",
    standfirst: "Vilora Impex exports 1718 Golden Sella Basmati Rice — the deep-golden, intensely parboiled form of Pusa Basmati 1718. Same Saudi-Arabia and UAE deep-golden retail standard as 1121 Golden Sella, with the supply-resilience advantage of the disease-resistant cultivar.",
    introH2: "What 1718 Golden Sella means",
    introBody: "Golden Sella is the deeper-parboiled form — intense steam treatment producing dark-golden grain with maximum shelf life and cook-stability. 1718 Golden Sella delivers identical visual signature and cooking behaviour to 1121 Golden Sella at the same premium pricing tier, with the disease-resistance benefit on supply consistency.",
    productsH2: "1718 Golden Sella grades we export",
    products: [
      { name: "1718 Golden Sella — Deep Gold", spec: "Maximum colour intensity • Saudi premium retail" },
      { name: "1718 Golden Sella — Standard", spec: "Mid-tone gold • UAE/Qatar retail premium" },
      { name: "1718 Golden Sella — Aged 12+", spec: "Ultra-premium retail and gift packs" },
      { name: "1718 Golden Sella — Bulk", spec: "25/50 kg PP for wholesale" },
      { name: "1121/1718 Golden Sella Blend", spec: "Custom blend for retail private label" },
      { name: "Custom 1718 Golden Sella", spec: "Buyer-locked colour intensity, grain length, broken %" },
    ],
    packagingH2: "1718 Golden Sella packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Saudi retail: 5 kg and 10 kg BOPP-printed laminate with bilingual SFDA labelling. UAE/Qatar: MOIAT/ESMA-compliant. Bulk: 25/50 kg PP. Private-label MOQ one FCL per SKU.",
    portsH2: "Where we ship 1718 Golden Sella",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why Saudi retail diversifies into 1718 Golden Sella",
    why: WHY_DEFAULT,
    faqsH2: "1718 Golden Sella export FAQs",
    faqs: [
      { q: "Is 1718 Golden Sella the same as 1121 Golden Sella?", a: "Visually and behaviourally identical at the retail level. Same deep-golden colour, same cook time, same grain expansion. The cultivar difference is at the farm level — 1718's disease resistance improves lot-to-lot consistency." },
      { q: "Why are retailers diversifying into 1718?", a: "Supply resilience. Large retail programs with 12-month forward orders benefit from running both 1121 and 1718 as parallel cultivars — if one has a tough crop year, the other typically delivers." },
      { q: "Premium of Golden Sella over standard Sella?", a: "Same as 1121 — 8–15% per MT over standard 1718 Sella. The deep-parboil process is the cost driver." },
      { q: "Shelf life?", a: "24+ months in cool, dry storage — same as 1121 Golden Sella." },
    ],
    related: ["1121-golden-sella", "1718-sella", "1718-steam", "1718-raw"],
    productSchemaName: "1718 Golden Sella Basmati Rice from India",
    productSchemaDescription: "1718 Golden Sella Basmati — disease-resistant 1121 successor in deep-golden parboiled form. Saudi premium retail standard. FSSAI/APEDA registered.",
  },

  "pusa-basmati-supply": {
    slug: "pusa-basmati-rice-exporter",
    folio: "№ 54",
    sectionLabel: "PUSA BASMATI",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "Pusa Basmati Rice Exporter from India",
    seoDescription:
      "Pusa Basmati Rice exporter from India — Pusa 1121, 1509, 1718, 1885 IARI cultivars. Long-grain aromatic basmati from Punjab and Haryana, FSSAI/APEDA registered.",
    h1: "Pusa Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Pusa Basmati Rice from India — Vilora Impex",
    eyebrow: "Pusa cultivars — the modern basmati family",
    standfirst: "Vilora Impex exports the full Pusa Basmati family — Pusa 1121, Pusa 1509, Pusa 1718, Pusa 1885 — the IARI-developed cultivars that produce the majority of India's basmati export volume. Long-grain, aromatic, semi-dwarf high-yield varieties from Punjab and Haryana milling partners.",
    introH2: "The Pusa Basmati family",
    introBody: "Pusa is the location prefix for varieties developed at the Indian Agricultural Research Institute (IARI). The Pusa Basmati family includes Pusa 1121 (the dominant 8.30 mm+ premium), Pusa 1509 (short-duration 8.0–8.4 mm value-premium), Pusa 1718 (disease-resistant 1121 successor) and Pusa 1885 (newer short-duration variant). Most commercial basmati export today is Pusa lineage.",
    productsH2: "Pusa Basmati varieties we export",
    products: [
      { name: "Pusa 1121", spec: "Long grain 8.30 mm+ • the dominant premium • aged 12 months" },
      { name: "Pusa 1509", spec: "Short-duration 8.0–8.4 mm • value-premium • aged 6 months" },
      { name: "Pusa 1718", spec: "Disease-resistant 1121 successor • supply-resilience grade" },
      { name: "Pusa 1885", spec: "Newer short-duration variant • growing share in fresh-crop supply" },
      { name: "Pusa Basmati Steam / Sella / Raw", spec: "All Pusa varieties available in all processing forms" },
      { name: "Custom Pusa Blend", spec: "Buyer-locked blend ratios across Pusa variants" },
    ],
    packagingH2: "Pusa Basmati packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Packaging in 5/10/25/50 kg PP, BOPP-printed laminate, jute, cotton or buyer-branded private label. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship Pusa Basmati",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why importers source the Pusa family",
    why: WHY_DEFAULT,
    faqsH2: "Pusa Basmati export FAQs",
    faqs: [
      { q: "What does 'Pusa' mean in Pusa Basmati?", a: "Pusa is the location prefix for varieties developed at the Indian Agricultural Research Institute (IARI), located in Pusa, Delhi. Cultivars released by IARI carry the Pusa prefix — Pusa Basmati 1121, Pusa Basmati 1509, Pusa Basmati 1718, etc." },
      { q: "Is Pusa Basmati the same as traditional basmati?", a: "No — Pusa varieties are modern semi-dwarf high-yield cultivars. Traditional basmati (Basmati 370, Type-3, Taraori Basmati) are older tall-plant cultivars with lower yields but distinctive aroma profiles, supplied in limited quantities for heritage-positioning brands." },
      { q: "Which Pusa variety should I import?", a: "1121 is the dominant premium choice for retail and HoReCa. 1509 is the value-tier alternative. 1718 is the disease-resistant supply-resilience option. 1885 is newer and limited in supply. Match variety to your destination market and price point." },
      { q: "Can I get a blend across Pusa variants?", a: "Yes — custom 1121/1509, 1121/1718, and other blends are routine. Used by retail brands locking specific shelf prices or running supply-resilience programs." },
    ],
    related: ["1121-basmati", "1509-basmati", "premium-rice"],
    productSchemaName: "Pusa Basmati Rice from India",
    productSchemaDescription: "Full Pusa Basmati family — 1121, 1509, 1718, 1885 IARI cultivars. Long-grain aromatic basmati from Punjab and Haryana. FSSAI/APEDA registered.",
  },

  "pusa-steam": {
    slug: "pusa-steam-basmati-rice",
    folio: "№ 55",
    sectionLabel: "PUSA STEAM",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "Pusa Steam Basmati Rice Exporter from India",
    seoDescription:
      "Pusa Steam Basmati Rice exporter from India — IARI-developed Pusa cultivars in steam-processed creamy-white form. HoReCa and retail premium.",
    h1: "Pusa Steam Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Pusa Steam Basmati Rice from India — Vilora Impex",
    eyebrow: "Steam-processed Pusa — multi-cultivar HoReCa",
    standfirst: "Vilora Impex exports Pusa Steam Basmati Rice — the steam-processed creamy-white form of any Pusa cultivar (1121, 1509, 1718, 1885). For buyers who specify by the Pusa family designation rather than the individual cultivar number, this is the spec.",
    introH2: "What Pusa Steam means",
    introBody: "When a buyer specifies 'Pusa Steam' without a specific cultivar number, the supply is typically Pusa 1121 Steam by default (the dominant cultivar), or a blend of Pusa cultivars depending on availability. All Pusa Steam varieties share the same grain-length tier and cook-stable creamy-white appearance suited to HoReCa biryani service and premium retail.",
    productsH2: "Pusa Steam grades we export",
    products: [
      { name: "Pusa 1121 Steam (default)", spec: "8.30 mm+ • aged 12 months • premium HoReCa" },
      { name: "Pusa 1509 Steam", spec: "8.0–8.4 mm • value-tier HoReCa and retail" },
      { name: "Pusa 1718 Steam", spec: "Disease-resistant 1121 successor • supply-resilience" },
      { name: "Pusa Steam Blend", spec: "Custom multi-cultivar blend per buyer spec" },
      { name: "Pusa Steam Retail Pack", spec: "5/10 kg BOPP-printed retail packs" },
      { name: "Pusa Steam Bulk HoReCa", spec: "25/50 kg PP for biryani-chain volume" },
    ],
    packagingH2: "Pusa Steam packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). HoReCa: 25 kg PP woven. Retail: 5 kg and 10 kg BOPP-printed. Halal certification included for Gulf retail.",
    portsH2: "Where we ship Pusa Steam",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why buyers specify 'Pusa Steam'",
    why: WHY_DEFAULT,
    faqsH2: "Pusa Steam basmati export FAQs",
    faqs: [
      { q: "What does the buyer get when 'Pusa Steam' is specified?", a: "By default, Pusa 1121 Steam — the dominant Pusa cultivar in the steam-processed form. The PI locks the specific cultivar before commit." },
      { q: "Can I specify 'any Pusa Steam' for supply flexibility?", a: "Yes — supply-flexible PIs are common for large repeat-order buyers. The PI specifies acceptable cultivars (e.g., 'Pusa 1121 or Pusa 1718 Steam at supplier's option') and locks the grain length / broken / moisture spec rather than the cultivar." },
      { q: "Pricing across Pusa Steam variants?", a: "Pusa 1121 Steam at the premium tier, Pusa 1509 Steam 15–25% lower, Pusa 1718 Steam at 1121 tier or 1–3% below." },
      { q: "What about Pusa Raw, Sella, Golden Sella?", a: "All three are also available as Pusa-family designations — see /pusa-basmati-rice-exporter for the family overview, or the individual cultivar pages for variant-specific detail." },
    ],
    related: ["pusa-basmati-supply", "1121-steam", "1509-steam", "1718-steam"],
    productSchemaName: "Pusa Steam Basmati Rice from India",
    productSchemaDescription: "Pusa Steam Basmati — IARI Pusa cultivars in steam-processed creamy-white form. HoReCa and retail spec. FSSAI/APEDA registered.",
  },

  "pusa-sella": {
    slug: "pusa-sella-basmati-rice",
    folio: "№ 56",
    sectionLabel: "PUSA SELLA",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "Pusa Sella Basmati Rice Exporter from India",
    seoDescription:
      "Pusa Sella Basmati Rice exporter from India — IARI Pusa cultivars in parboiled creamy/light-golden form. Gulf retail standard.",
    h1: "Pusa Sella Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Pusa Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Parboiled Pusa — Gulf retail family",
    standfirst: "Vilora Impex exports Pusa Sella Basmati Rice — the parboiled creamy-to-light-golden form of any Pusa cultivar. The Gulf retail standard when the buyer specifies by Pusa family designation rather than the individual cultivar number.",
    introH2: "What Pusa Sella means",
    introBody: "Pusa Sella encompasses 1121 Sella (premium), 1509 Sella (value-tier), 1718 Sella (supply-resilience successor), and Pusa 1885 Sella (newer short-duration). All are parboiled, all serve the Gulf retail volume segment, all deliver long shelf life and creamy-to-light-golden tone consumers expect.",
    productsH2: "Pusa Sella grades we export",
    products: [
      { name: "Pusa 1121 Sella (default)", spec: "8.30 mm+ • Gulf retail standard" },
      { name: "Pusa 1509 Sella", spec: "8.0–8.4 mm • value-tier retail and HoReCa" },
      { name: "Pusa 1718 Sella", spec: "Disease-resistant 1121 successor" },
      { name: "Pusa Sella Retail Private Label", spec: "5/10 kg BOPP-printed under buyer brand" },
      { name: "Pusa Sella Bulk Distributor", spec: "25/50 kg PP for wholesale channels" },
      { name: "Custom Pusa Sella Blend", spec: "Buyer-locked blend across Pusa Sella variants" },
    ],
    packagingH2: "Pusa Sella packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Retail: 5/10 kg BOPP-printed. Bulk: 25/50 kg PP. Private-label MOQ one FCL per SKU. Halal and SFDA/MOIAT labelling for Gulf retail included.",
    portsH2: "Where we ship Pusa Sella",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why Gulf retail buys Pusa Sella in volume",
    why: WHY_DEFAULT,
    faqsH2: "Pusa Sella export FAQs",
    faqs: [
      { q: "What does 'Pusa Sella' on the PI mean?", a: "By default Pusa 1121 Sella, or a Pusa-family blend per the PI cuttest. Buyers who want a specific cultivar must specify (e.g., 'Pusa 1509 Sella')." },
      { q: "Pricing tier?", a: "Pusa 1121 Sella at the premium tier; Pusa 1509 Sella 18–22% below; Pusa 1718 Sella at 1121 tier or marginally below." },
      { q: "Can Pusa Sella be supplied at Golden Sella spec?", a: "Yes — see dedicated /pusa-golden-sella-basmati-rice page for the deep-golden parboiled variant." },
      { q: "Shelf life?", a: "18–24 months — same range as the underlying cultivar's Sella form." },
    ],
    related: ["pusa-basmati-supply", "1121-sella", "1509-sella", "pusa-golden-sella"],
    productSchemaName: "Pusa Sella Basmati Rice from India",
    productSchemaDescription: "Pusa Sella Basmati — IARI Pusa cultivars in parboiled creamy/light-golden form. Gulf retail standard. FSSAI/APEDA registered.",
  },

  "pusa-golden-sella": {
    slug: "pusa-golden-sella-basmati-rice",
    folio: "№ 57",
    sectionLabel: "PUSA GOLDEN SELLA",
    category: "Rice",
    enrichedSchema: true,
    seoTitle: "Pusa Golden Sella Basmati Rice Exporter from India",
    seoDescription:
      "Pusa Golden Sella Basmati Rice exporter from India — IARI Pusa cultivars in deep-golden parboiled form. Saudi Arabia and UAE premium retail.",
    h1: "Pusa Golden Sella Basmati Rice Exporter from India",
    image: "/hero-rice.jpg",
    imageAlt: "Pusa Golden Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Deep-golden Pusa — Saudi premium tier",
    standfirst: "Vilora Impex exports Pusa Golden Sella Basmati Rice — the deep-golden, intensely parboiled form of any Pusa cultivar. The Saudi Arabia and UAE premium retail standard when the buyer specifies by Pusa family designation rather than a specific cultivar number.",
    introH2: "What Pusa Golden Sella means",
    introBody: "Golden Sella across the Pusa family — 1121 Golden Sella, 1509 Golden Sella, 1718 Golden Sella, 1885 Golden Sella. All are deeply parboiled, all deliver dark-golden colour and maximum cook-stability. Saudi and UAE retail consumers strongly prefer deep-golden basmati and the Pusa family supplies the majority of this market.",
    productsH2: "Pusa Golden Sella grades we export",
    products: [
      { name: "Pusa 1121 Golden Sella (default)", spec: "Maximum colour • Saudi premium retail" },
      { name: "Pusa 1509 Golden Sella", spec: "Value-tier deep-golden parboiled" },
      { name: "Pusa 1718 Golden Sella", spec: "Disease-resistant 1121 successor • supply-resilience" },
      { name: "Pusa Golden Sella Retail Pack", spec: "5/10 kg BOPP-printed under buyer brand" },
      { name: "Pusa Golden Sella Bulk", spec: "25/50 kg PP for wholesale" },
      { name: "Custom Pusa Golden Sella Blend", spec: "Buyer-locked blend ratios" },
    ],
    packagingH2: "Pusa Golden Sella packaging and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT). Saudi retail: 5/10 kg BOPP-printed with bilingual SFDA-compliant labelling. UAE/Qatar: MOIAT/ESMA-compliant. Bulk: 25/50 kg PP. Private-label MOQ one FCL per SKU.",
    portsH2: "Where we ship Pusa Golden Sella",
    portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT,
    whyH2: "Why Saudi retail buys Pusa Golden Sella",
    why: WHY_DEFAULT,
    faqsH2: "Pusa Golden Sella export FAQs",
    faqs: [
      { q: "What's the dominant Pusa Golden Sella in Saudi retail?", a: "Pusa 1121 Golden Sella accounts for the majority of Saudi premium retail volume. Pusa 1718 Golden Sella is growing as a supply-resilience parallel SKU." },
      { q: "Premium over standard Pusa Sella?", a: "8–15% per MT — the deeper parboil process is the cost driver, same as 1121 Golden Sella vs 1121 Sella." },
      { q: "Cook behaviour?", a: "25–28 minutes at 1:2.0 water ratio. Same as 1121 Golden Sella. The Pusa family designation doesn't change the cooking behaviour at this processing level." },
      { q: "Shelf life?", a: "24+ months in cool, dry storage — the longest of any basmati form." },
    ],
    related: ["pusa-basmati-supply", "pusa-sella", "1121-golden-sella", "1718-golden-sella"],
    productSchemaName: "Pusa Golden Sella Basmati Rice from India",
    productSchemaDescription: "Pusa Golden Sella Basmati — IARI Pusa cultivars in deep-golden parboiled form. Saudi and UAE premium retail standard. FSSAI/APEDA registered.",
  },

  // ===== Wave 5 (2026-05-22): non-basmati variety × processing matrix =====
  // PR11, PR14, Parmal: high-volume long-grain non-basmati commodity rice.
  // Sona Masoori: medium-grain non-basmati (no Golden Sella variant — only Raw/Steam/Sella).
  // IR64: long-grain non-basmati, India's highest-volume non-basmati export.

  // ----- PR11 -----
  "pr11-overview": {
    slug: "pr11-non-basmati-rice", folio: "№ 58", sectionLabel: "PR11", category: "Rice", enrichedSchema: true,
    seoTitle: "PR11 Non-Basmati Rice Exporter from India",
    seoDescription: "PR11 non-basmati rice exporter from India — long-grain Punjab paddy variety, available raw, steam, sella and golden sella. Bulk distributor and tender supply.",
    h1: "PR11 Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "PR11 non-basmati rice from India — Vilora Impex",
    eyebrow: "PR11 Punjab non-basmati — bulk volume",
    standfirst: "Vilora Impex exports PR11 non-basmati rice — a long-grain Punjab paddy variety, available in raw, steam, sella and golden sella forms. The volume choice for bulk distributor channels, government feeding programs, and value-tier retail across the Gulf, East Africa and South Asia.",
    introH2: "What PR11 is", introBody: "PR11 is a long-grain non-basmati paddy variety from Punjab and Haryana — higher yield than basmati, neutral aroma, suited to volume bulk supply rather than premium retail. Grain length 6.5–7.2 mm, available in all four processing forms (raw, steam, sella, golden sella). PR11 is the workhorse variety for buyers who need long-grain Indian rice at a non-basmati price point.",
    productsH2: "PR11 grades we export", products: [
      { name: "PR11 Raw", spec: "Natural milled white • 6.5–7.2 mm grain • bulk distributor and tender supply" },
      { name: "PR11 Steam", spec: "Steam-processed creamy white • HoReCa and retail value tier" },
      { name: "PR11 Sella", spec: "Parboiled creamy/light-golden • Gulf and African bulk retail" },
      { name: "PR11 Golden Sella", spec: "Deep parboil • value-tier Saudi and UAE bulk retail" },
      { name: "PR11 Broken (Tukda)", spec: "Sortex-cleaned brokens for food-ingredient buyers" },
      { name: "Custom PR11 Cuttest", spec: "Buyer-locked broken %, moisture, polish, processing form" },
    ],
    packagingH2: "PR11 packaging, MOQ and shipping",
    packagingBody: "FCL MOQ is one 20ft container (~25 MT) or 40ft FCL (~50 MT) for bulk distributor channels. Standard packaging in 25/50 kg PP woven or jute. Incoterms: FOB Mundra/Kandla, CIF, CFR. Documentation packet within 48 hours of sail.",
    portsH2: "Where we ship PR11", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why distributors choose PR11", why: WHY_DEFAULT,
    faqsH2: "PR11 non-basmati export FAQs", faqs: [
      { q: "Is PR11 the same as basmati?", a: "No. PR11 is a long-grain non-basmati paddy variety from Punjab. It does not have basmati's distinctive aroma or premium price tier. Buyers choose PR11 when they need long-grain Indian rice for bulk distribution at a non-basmati price." },
      { q: "What is the MOQ for PR11?", a: "One 20ft FCL (~25 MT) standard. 40ft FCL (~50 MT) common for repeat distributor accounts. LCL trial shipments from 10 MT accepted." },
      { q: "Which destinations import PR11 in volume?", a: "Iraq, Yemen, Egypt, East Africa (Kenya, Tanzania, Mozambique), Bangladesh, value-tier retail in the Gulf, and food-aid tender supply (UN WFP and similar)." },
      { q: "What's the processing form difference for PR11?", a: "Same as basmati: Raw is natural milled, Steam is steam-processed creamy-white, Sella is parboiled creamy/light-gold, Golden Sella is deep-parboiled dark-gold. Each form has different shelf life and cook stability." },
    ],
    related: ["pr11-raw", "pr11-steam", "pr11-sella", "non-basmati"],
    productSchemaName: "PR11 Non-Basmati Rice from India", productSchemaDescription: "PR11 long-grain non-basmati rice from Punjab. Available raw/steam/sella/golden sella for bulk distributor supply. FSSAI/APEDA registered.",
  },
  "pr11-raw": {
    slug: "pr11-raw-non-basmati-rice", folio: "№ 59", sectionLabel: "PR11 RAW", category: "Rice", enrichedSchema: true,
    seoTitle: "PR11 Raw Non-Basmati Rice Exporter from India",
    seoDescription: "PR11 Raw non-basmati rice exporter from India — natural milled long grain, bulk distributor supply, FCL volumes.",
    h1: "PR11 Raw Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "PR11 Raw non-basmati rice from India — Vilora Impex",
    eyebrow: "PR11 Raw — natural milled bulk volume",
    standfirst: "Vilora Impex exports PR11 Raw non-basmati rice — the natural-milled white form of the Punjab long-grain non-basmati variety. Bulk distributor channels, value-tier retail across the Gulf and East Africa, and tender-supply buyers.",
    introH2: "PR11 Raw spec", introBody: "PR11 Raw is the natural-milled white grain — no parboiling, no steam treatment. Bright white appearance, neutral aroma, soft cooked texture. Best for value retail and food-aid pipelines where parboiling premium isn't justified. Cooks in 12–16 minutes at a 1:1.7 water ratio.",
    productsH2: "PR11 Raw grades we export", products: [
      { name: "PR11 Raw — Standard", spec: "5–15% broken to spec • 25/50 kg PP" },
      { name: "PR11 Raw — Premium", spec: "<5% broken • value-retail private label" },
      { name: "PR11 Raw — Bulk Distributor", spec: "50 kg PP/jute for wholesale" },
      { name: "PR11 Raw — Tender Spec", spec: "WFP/government tender-spec lab COA included" },
      { name: "PR11 Raw — Brokens", spec: "Sortex-cleaned brokens, food-ingredient grade" },
      { name: "Custom PR11 Raw", spec: "Buyer-locked broken %, moisture, polish" },
    ],
    packagingH2: "PR11 Raw packaging and shipping",
    packagingBody: "FCL MOQ one 20ft container (~25 MT) or 40ft (~50 MT). Packaging in 25/50 kg PP or jute. Tender-supply contracts may require buyer-spec PP woven with PSI inspection. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship PR11 Raw", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why distributors pick PR11 Raw", why: WHY_DEFAULT,
    faqsH2: "PR11 Raw export FAQs", faqs: [
      { q: "Why choose PR11 Raw over PR11 Sella?", a: "PR11 Raw is cheaper per MT (no parboiling cost). Best for fast-rotating retail and food-aid where shelf-life premium isn't critical. PR11 Sella is preferred for longer-shelf-life retail and HoReCa." },
      { q: "What's the shelf life of PR11 Raw?", a: "8–14 months in cool, dry storage. Tropical humidity shortens this. Rotate stock every quarter at the importer's warehouse." },
      { q: "Which countries import PR11 Raw in volume?", a: "Iraq, Yemen, Kenya, Tanzania, Bangladesh, Indonesia value retail, food-aid tender supply." },
      { q: "MOQ?", a: "One 20ft FCL standard. 40ft FCL common for tender supply and distributor accounts." },
    ],
    related: ["pr11-overview", "pr11-steam", "pr11-sella", "non-basmati"],
    productSchemaName: "PR11 Raw Non-Basmati Rice", productSchemaDescription: "PR11 natural milled long-grain non-basmati rice from Punjab. Bulk distributor and tender supply. FSSAI/APEDA registered.",
  },
  "pr11-steam": {
    slug: "pr11-steam-non-basmati-rice", folio: "№ 60", sectionLabel: "PR11 STEAM", category: "Rice", enrichedSchema: true,
    seoTitle: "PR11 Steam Non-Basmati Rice Exporter from India",
    seoDescription: "PR11 Steam non-basmati rice exporter from India — steam-processed creamy-white long grain, value-retail and HoReCa.",
    h1: "PR11 Steam Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "PR11 Steam non-basmati rice from India — Vilora Impex",
    eyebrow: "PR11 Steam — value-retail HoReCa",
    standfirst: "Vilora Impex exports PR11 Steam non-basmati rice — the steam-processed creamy-white form. Cook-stable, longer shelf life than Raw, suited to value retail and HoReCa volume supply across Gulf, East Africa and ASEAN markets.",
    introH2: "PR11 Steam spec", introBody: "PR11 Steam is milled then gently steam-processed — creamy-white grain that holds shape better through service than Raw. Cooks in 14–18 minutes. Bridge between Raw (cheap, soft) and Sella (premium-tier non-basmati). Best for HoReCa accounts that want cook-stable long-grain at non-basmati pricing.",
    productsH2: "PR11 Steam grades we export", products: [
      { name: "PR11 Steam — Value HoReCa", spec: "5–10% broken • 25 kg PP for biryani-style chain service" },
      { name: "PR11 Steam — Retail Standard", spec: "5/10 kg PP retail packs" },
      { name: "PR11 Steam — Bulk", spec: "25/50 kg PP for wholesale distribution" },
      { name: "PR11 Steam — Private Label", spec: "Buyer-branded retail under value tier" },
      { name: "PR11 Steam — Tender Spec", spec: "Government tender pipeline supply" },
      { name: "Custom PR11 Steam", spec: "Buyer-locked broken %, moisture, polish" },
    ],
    packagingH2: "PR11 Steam packaging and shipping",
    packagingBody: "FCL MOQ one 20ft container (~25 MT). Packaging 5/10/25/50 kg PP. Private-label MOQ one FCL per SKU. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship PR11 Steam", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why HoReCa picks PR11 Steam", why: WHY_DEFAULT,
    faqsH2: "PR11 Steam export FAQs", faqs: [
      { q: "Why PR11 Steam over PR11 Sella for HoReCa?", a: "PR11 Steam is cheaper (no parboiling cost) and visually creamy-white rather than golden. HoReCa accounts that don't want golden tone choose Steam." },
      { q: "Shelf life?", a: "12–16 months. Longer than Raw, shorter than Sella." },
      { q: "Which markets import PR11 Steam?", a: "Singapore, Malaysia value retail, UAE HoReCa value-tier, East African retail." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT)." },
    ],
    related: ["pr11-overview", "pr11-raw", "pr11-sella", "non-basmati"],
    productSchemaName: "PR11 Steam Non-Basmati Rice", productSchemaDescription: "PR11 steam-processed creamy-white long-grain non-basmati rice. Value-retail and HoReCa supply. FSSAI/APEDA registered.",
  },
  "pr11-sella": {
    slug: "pr11-sella-non-basmati-rice", folio: "№ 61", sectionLabel: "PR11 SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "PR11 Sella Non-Basmati Rice Exporter from India",
    seoDescription: "PR11 Sella non-basmati rice exporter from India — parboiled long grain, bulk Gulf retail and African distributor supply.",
    h1: "PR11 Sella Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "PR11 Sella non-basmati rice from India — Vilora Impex",
    eyebrow: "PR11 Sella — bulk Gulf and African retail",
    standfirst: "Vilora Impex exports PR11 Sella non-basmati rice — the parboiled creamy-to-light-golden form. Cook-stable, long shelf life, the volume choice for Gulf budget retail and African distributor channels where consumers expect parboiled tone at a non-basmati price.",
    introH2: "PR11 Sella spec", introBody: "PR11 Sella is parboiled — paddy steamed in the husk before milling. Hardened grain, creamy-to-light-golden tone, 14–18-month shelf life. Cooks in 18–22 minutes at 1:1.9 water ratio. Volume choice for buyers who want non-basmati long-grain rice with parboiled cook-stability.",
    productsH2: "PR11 Sella grades", products: [
      { name: "PR11 Sella — Creamy", spec: "Light parboil • Gulf and African retail" },
      { name: "PR11 Sella — Standard", spec: "Mid-tone parboil • bulk distributor" },
      { name: "PR11 Sella — Retail Private Label", spec: "5/10/25 kg under buyer brand" },
      { name: "PR11 Sella — Bulk", spec: "50 kg PP/jute for wholesale" },
      { name: "PR11 Sella — Tender Spec", spec: "Government tender pipeline" },
      { name: "Custom PR11 Sella", spec: "Buyer-locked parboil intensity, broken %" },
    ],
    packagingH2: "PR11 Sella packaging and shipping",
    packagingBody: "FCL MOQ one 20ft (~25 MT) or 40ft (~50 MT). Packaging in 5/10/25/50 kg PP, BOPP-printed for retail, jute for bulk distributor. Halal cert included for Gulf retail SKUs. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship PR11 Sella", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why distributors pick PR11 Sella", why: WHY_DEFAULT,
    faqsH2: "PR11 Sella export FAQs", faqs: [
      { q: "Why PR11 Sella over basmati Sella?", a: "Price. PR11 Sella runs 40–55% cheaper per MT than 1121 Sella, with similar cook behaviour at a shorter grain length. Best for value-tier retail and bulk distribution where end-consumer doesn't pay the basmati premium." },
      { q: "Shelf life?", a: "14–18 months in cool, dry storage." },
      { q: "Markets?", a: "Egypt, Iraq, Yemen, Jordan, Kenya, Tanzania, Mozambique, Bangladesh value retail." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT). 40ft FCL common for tender supply." },
    ],
    related: ["pr11-overview", "pr11-raw", "pr11-steam", "pr11-golden-sella"],
    productSchemaName: "PR11 Sella Non-Basmati Rice", productSchemaDescription: "PR11 parboiled long-grain non-basmati rice. Bulk Gulf and African retail standard. FSSAI/APEDA registered.",
  },
  "pr11-golden-sella": {
    slug: "pr11-golden-sella-non-basmati-rice", folio: "№ 62", sectionLabel: "PR11 GOLDEN SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "PR11 Golden Sella Non-Basmati Rice Exporter from India",
    seoDescription: "PR11 Golden Sella non-basmati rice exporter from India — deep-golden parboiled long grain. Value-tier Saudi and UAE retail.",
    h1: "PR11 Golden Sella Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "PR11 Golden Sella non-basmati rice from India — Vilora Impex",
    eyebrow: "PR11 Golden Sella — value-tier Gulf retail",
    standfirst: "Vilora Impex exports PR11 Golden Sella non-basmati rice — the deep-golden parboiled form of the Punjab long-grain non-basmati variety. Value-tier alternative to 1121 Golden Sella for Saudi, UAE and Gulf retail consumers who prefer deep-golden colour at a non-basmati price.",
    introH2: "PR11 Golden Sella spec", introBody: "PR11 Golden Sella is deep-parboiled — intense steam treatment producing dark-amber grain. Maximum shelf life among PR11 forms (18-22 months), highest cook stability. Cooks in 22–26 minutes at 1:2.0 water ratio. The value-tier deep-golden choice for buyers in markets where consumers expect golden non-basmati at an affordable shelf price.",
    productsH2: "PR11 Golden Sella grades", products: [
      { name: "PR11 Golden Sella — Deep Gold", spec: "Maximum colour • value-tier Saudi/UAE retail" },
      { name: "PR11 Golden Sella — Standard", spec: "Mid-tone gold • bulk distributor" },
      { name: "PR11 Golden Sella — Retail Private Label", spec: "5/10 kg under buyer brand" },
      { name: "PR11 Golden Sella — Bulk", spec: "25/50 kg PP for wholesale" },
      { name: "1121/PR11 Golden Sella Blend", spec: "Custom blend for retail price-point engineering" },
      { name: "Custom PR11 Golden Sella", spec: "Buyer-locked colour intensity, broken %" },
    ],
    packagingH2: "PR11 Golden Sella packaging",
    packagingBody: "FCL MOQ one 20ft (~25 MT). Retail packaging 5/10 kg BOPP-printed with bilingual labelling for Gulf. Bulk 25/50 kg PP. Halal cert included for Gulf retail. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship PR11 Golden Sella", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why value retail picks PR11 Golden Sella", why: WHY_DEFAULT,
    faqsH2: "PR11 Golden Sella export FAQs", faqs: [
      { q: "How does PR11 Golden Sella differ from 1121 Golden Sella?", a: "Same deep-golden visual appearance and cook stability. PR11 is shorter grain (6.5–7.2 mm vs 1121's 8.30 mm+) and ~50–60% cheaper per MT. Best for budget retail SKUs hitting a specific shelf price." },
      { q: "Shelf life?", a: "18–22 months in cool, dry storage. The deepest parboil produces the most stable PR11 form." },
      { q: "Markets?", a: "Value-tier Saudi retail, Iraq, Egypt, Yemen, East African budget retail." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT)." },
    ],
    related: ["pr11-overview", "pr11-sella", "1121-golden-sella", "non-basmati"],
    productSchemaName: "PR11 Golden Sella Non-Basmati Rice", productSchemaDescription: "PR11 deep-golden parboiled long-grain non-basmati. Value-tier Gulf and African retail. FSSAI/APEDA registered.",
  },

  // ----- PR14 -----
  "pr14-overview": {
    slug: "pr14-non-basmati-rice", folio: "№ 63", sectionLabel: "PR14", category: "Rice", enrichedSchema: true,
    seoTitle: "PR14 Non-Basmati Rice Exporter from India",
    seoDescription: "PR14 non-basmati rice exporter from India — long-grain Punjab paddy variety in raw, steam, sella and golden sella. Bulk distributor and tender supply.",
    h1: "PR14 Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "PR14 non-basmati rice from India — Vilora Impex",
    eyebrow: "PR14 Punjab non-basmati — value volume",
    standfirst: "Vilora Impex exports PR14 non-basmati rice — a sister long-grain Punjab paddy variety to PR11, available in all four processing forms. Volume choice for bulk distributor channels, tender supply, and value-tier retail across Iraq, Yemen, East Africa and South Asia.",
    introH2: "What PR14 is", introBody: "PR14 is a long-grain non-basmati paddy variety released by Punjab Agricultural University, sister to PR11 in the PR-family. Grain length 6.5–7.0 mm, neutral aroma, suited to volume bulk supply. Available raw, steam, sella and golden sella — similar tier and pricing to PR11.",
    productsH2: "PR14 grades", products: [
      { name: "PR14 Raw", spec: "Natural milled white • bulk distributor and tender supply" },
      { name: "PR14 Steam", spec: "Steam-processed creamy white • HoReCa value tier" },
      { name: "PR14 Sella", spec: "Parboiled creamy/light-golden • Gulf bulk retail" },
      { name: "PR14 Golden Sella", spec: "Deep parboil • value-tier Saudi/UAE retail" },
      { name: "PR14 Broken", spec: "Sortex-cleaned brokens for food-ingredient buyers" },
      { name: "Custom PR14 Cuttest", spec: "Buyer-locked spec, processing form" },
    ],
    packagingH2: "PR14 packaging and shipping",
    packagingBody: "FCL MOQ one 20ft (~25 MT) or 40ft (~50 MT). Packaging in 25/50 kg PP or jute. Tender supply contracts with PSI inspection on request. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship PR14", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why distributors choose PR14", why: WHY_DEFAULT,
    faqsH2: "PR14 export FAQs", faqs: [
      { q: "How does PR14 differ from PR11?", a: "Same family of Punjab non-basmati. Slightly different agronomic traits at the farm, virtually indistinguishable at the export-grade. PR11 and PR14 are often interchangeable on tender supply." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT). 40ft (~50 MT) common for tender." },
      { q: "Markets?", a: "Iraq, Yemen, Egypt, East Africa, Bangladesh, value retail in Gulf." },
      { q: "Can PR11/PR14 be blended?", a: "Yes — common for tender supply where the spec calls for 'long-grain Indian non-basmati' without specifying the variety code." },
    ],
    related: ["pr14-raw", "pr14-steam", "pr14-sella", "pr11-overview", "non-basmati"],
    productSchemaName: "PR14 Non-Basmati Rice", productSchemaDescription: "PR14 long-grain non-basmati from Punjab. Available all 4 processing forms for bulk distributor supply. FSSAI/APEDA registered.",
  },
  "pr14-raw": {
    slug: "pr14-raw-non-basmati-rice", folio: "№ 64", sectionLabel: "PR14 RAW", category: "Rice", enrichedSchema: true,
    seoTitle: "PR14 Raw Non-Basmati Rice Exporter from India",
    seoDescription: "PR14 Raw non-basmati rice exporter from India — natural milled long grain, bulk distributor and tender supply.",
    h1: "PR14 Raw Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "PR14 Raw non-basmati rice from India — Vilora Impex",
    eyebrow: "PR14 Raw — bulk volume",
    standfirst: "Vilora Impex exports PR14 Raw non-basmati rice — the natural-milled white form of the Punjab long-grain variety. Bulk distributor channels, value retail and food-aid tender supply.",
    introH2: "PR14 Raw spec", introBody: "PR14 Raw is the natural-milled white grain — no parboil, no steam. Neutral aroma, soft cooked texture, short shelf life (8–14 months). Best for fast-rotating bulk distribution and food-aid pipelines. Cooks in 12–16 minutes.",
    productsH2: "PR14 Raw grades", products: [
      { name: "PR14 Raw — Standard", spec: "5–15% broken • 25/50 kg PP" },
      { name: "PR14 Raw — Premium", spec: "<5% broken • value-retail private label" },
      { name: "PR14 Raw — Bulk", spec: "50 kg PP/jute for wholesale" },
      { name: "PR14 Raw — Tender Spec", spec: "WFP/government tender with PSI inspection" },
      { name: "PR14 Raw — Brokens", spec: "Sortex-cleaned broken rice, food-ingredient grade" },
      { name: "Custom PR14 Raw", spec: "Buyer-locked broken %, moisture, polish" },
    ],
    packagingH2: "PR14 Raw packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Packaging 25/50 kg PP. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship PR14 Raw", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why bulk distributors pick PR14 Raw", why: WHY_DEFAULT,
    faqsH2: "PR14 Raw export FAQs", faqs: [
      { q: "How does PR14 Raw differ from IR64 Raw?", a: "Both are long-grain non-basmati raw forms. PR14 is from Punjab (northern India), IR64 is from Andhra Pradesh / West Bengal. Pricing similar; choice depends on milling-partner availability and buyer-preference for origin." },
      { q: "Shelf life?", a: "8–14 months." },
      { q: "Markets?", a: "Iraq, Yemen, Kenya, Tanzania, Bangladesh value retail, tender supply." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT)." },
    ],
    related: ["pr14-overview", "pr14-steam", "pr11-raw", "ir64-overview"],
    productSchemaName: "PR14 Raw Non-Basmati Rice", productSchemaDescription: "PR14 natural milled long-grain non-basmati from Punjab. Bulk distributor and tender supply. FSSAI/APEDA registered.",
  },
  "pr14-steam": {
    slug: "pr14-steam-non-basmati-rice", folio: "№ 65", sectionLabel: "PR14 STEAM", category: "Rice", enrichedSchema: true,
    seoTitle: "PR14 Steam Non-Basmati Rice Exporter from India",
    seoDescription: "PR14 Steam non-basmati rice exporter from India — steam-processed creamy-white long grain, value-retail and HoReCa.",
    h1: "PR14 Steam Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "PR14 Steam non-basmati rice from India — Vilora Impex",
    eyebrow: "PR14 Steam — value HoReCa",
    standfirst: "Vilora Impex exports PR14 Steam non-basmati rice — the steam-processed creamy-white form. Cook-stable, retail-friendly, suited to HoReCa accounts and value retail at non-basmati pricing.",
    introH2: "PR14 Steam spec", introBody: "PR14 Steam is milled then gently steam-processed — creamy-white grain with cook-stability between Raw and Sella. Cooks in 14–18 minutes. Best for HoReCa and retail accounts that want long-grain visual appearance at a non-basmati price point.",
    productsH2: "PR14 Steam grades", products: [
      { name: "PR14 Steam — Value HoReCa", spec: "5–10% broken • 25 kg PP" },
      { name: "PR14 Steam — Retail Standard", spec: "5/10 kg PP retail packs" },
      { name: "PR14 Steam — Bulk", spec: "25/50 kg PP" },
      { name: "PR14 Steam — Private Label", spec: "Buyer-branded value tier" },
      { name: "PR11/PR14 Steam Blend", spec: "Tender-spec long-grain blend" },
      { name: "Custom PR14 Steam", spec: "Buyer-locked spec" },
    ],
    packagingH2: "PR14 Steam packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Packaging 5/10/25/50 kg PP. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship PR14 Steam", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why HoReCa picks PR14 Steam", why: WHY_DEFAULT,
    faqsH2: "PR14 Steam export FAQs", faqs: [
      { q: "PR14 Steam vs Sella?", a: "Steam is creamy-white, Sella is parboiled creamy/light-gold. Steam is cheaper and visually less yellow. Pick based on destination market preference." },
      { q: "Shelf life?", a: "12–16 months." },
      { q: "Markets?", a: "Singapore, Malaysia value retail, East African retail, UAE HoReCa value tier." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["pr14-overview", "pr14-raw", "pr14-sella", "pr11-steam"],
    productSchemaName: "PR14 Steam Non-Basmati Rice", productSchemaDescription: "PR14 steam-processed long-grain non-basmati. Value-retail and HoReCa. FSSAI/APEDA registered.",
  },
  "pr14-sella": {
    slug: "pr14-sella-non-basmati-rice", folio: "№ 66", sectionLabel: "PR14 SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "PR14 Sella Non-Basmati Rice Exporter from India",
    seoDescription: "PR14 Sella non-basmati rice exporter from India — parboiled long grain, bulk Gulf retail and African distributor supply.",
    h1: "PR14 Sella Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "PR14 Sella non-basmati rice from India — Vilora Impex",
    eyebrow: "PR14 Sella — bulk Gulf retail",
    standfirst: "Vilora Impex exports PR14 Sella non-basmati rice — the parboiled creamy-to-light-golden form. The volume choice for Gulf budget retail, East African distribution, and tender supply where parboiled cook-stability is needed at a non-basmati price.",
    introH2: "PR14 Sella spec", introBody: "PR14 Sella is parboiled — paboard steamed in the husk before milling, hardening the grain. Creamy-to-light-golden tone, 14–18-month shelf life, cooks in 18–22 minutes. The non-basmati parallel to 1121 Sella at a far lower price point.",
    productsH2: "PR14 Sella grades", products: [
      { name: "PR14 Sella — Creamy", spec: "Light parboil • Gulf retail" },
      { name: "PR14 Sella — Standard", spec: "Mid-tone parboil • bulk distributor" },
      { name: "PR14 Sella — Retail Private Label", spec: "5/10/25 kg under buyer brand" },
      { name: "PR14 Sella — Bulk", spec: "50 kg PP/jute for wholesale" },
      { name: "PR11/PR14 Sella Blend", spec: "Tender-spec long-grain Sella blend" },
      { name: "Custom PR14 Sella", spec: "Buyer-locked parboil intensity, broken %" },
    ],
    packagingH2: "PR14 Sella packaging", packagingBody: "FCL MOQ one 20ft (~25 MT) or 40ft (~50 MT). Packaging in 5/10/25/50 kg PP, BOPP for retail, jute for bulk. Halal cert included for Gulf retail. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship PR14 Sella", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why distributors pick PR14 Sella", why: WHY_DEFAULT,
    faqsH2: "PR14 Sella export FAQs", faqs: [
      { q: "PR14 Sella vs PR11 Sella?", a: "Virtually interchangeable in supply terms. Choice driven by milling-partner availability. PR11 may have marginally more polished retail appearance; PR14 slightly more rugged grain. Both serve the same volume retail and bulk distributor channels." },
      { q: "Shelf life?", a: "14–18 months." },
      { q: "Markets?", a: "Egypt, Iraq, Yemen, Jordan, Kenya, Tanzania, Bangladesh budget retail." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT)." },
    ],
    related: ["pr14-overview", "pr14-raw", "pr14-golden-sella", "pr11-sella"],
    productSchemaName: "PR14 Sella Non-Basmati Rice", productSchemaDescription: "PR14 parboiled long-grain non-basmati. Bulk Gulf and African retail. FSSAI/APEDA registered.",
  },
  "pr14-golden-sella": {
    slug: "pr14-golden-sella-non-basmati-rice", folio: "№ 67", sectionLabel: "PR14 GOLDEN SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "PR14 Golden Sella Non-Basmati Rice Exporter from India",
    seoDescription: "PR14 Golden Sella non-basmati rice exporter from India — deep-golden parboiled long grain, value-tier Saudi/UAE retail.",
    h1: "PR14 Golden Sella Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "PR14 Golden Sella non-basmati rice from India — Vilora Impex",
    eyebrow: "PR14 Golden Sella — value Gulf retail",
    standfirst: "Vilora Impex exports PR14 Golden Sella non-basmati rice — the deep-golden parboiled form. Value-tier Saudi and UAE retail, budget HoReCa, East African budget distribution.",
    introH2: "PR14 Golden Sella spec", introBody: "PR14 Golden Sella is deep-parboiled — dark-amber grain with maximum shelf life (18–22 months) and cook stability. Cooks in 22–26 minutes at 1:2.0 water ratio. The deep-golden non-basmati option for retail brands hitting budget shelf prices.",
    productsH2: "PR14 Golden Sella grades", products: [
      { name: "PR14 Golden Sella — Deep Gold", spec: "Max colour • value-tier Gulf retail" },
      { name: "PR14 Golden Sella — Standard", spec: "Mid-gold • bulk distributor" },
      { name: "PR14 Golden Sella — Retail Private Label", spec: "5/10 kg BOPP under buyer brand" },
      { name: "PR14 Golden Sella — Bulk", spec: "25/50 kg PP" },
      { name: "Mixed Long-Grain Golden Sella Blend", spec: "PR11/PR14/IR64 blend for tender" },
      { name: "Custom PR14 Golden Sella", spec: "Buyer-locked colour intensity" },
    ],
    packagingH2: "PR14 Golden Sella packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Retail 5/10 kg BOPP. Bulk 25/50 kg PP. Halal cert for Gulf retail. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship PR14 Golden Sella", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why budget retail picks PR14 Golden Sella", why: WHY_DEFAULT,
    faqsH2: "PR14 Golden Sella export FAQs", faqs: [
      { q: "How does PR14 Golden Sella compare to 1121 Golden Sella?", a: "Same visual deep-golden appearance, shorter grain (6.5–7.0 mm vs 8.30 mm+), ~55–65% cheaper per MT. For retail SKUs hitting budget price points where the consumer wants golden Sella but doesn't pay basmati premium." },
      { q: "Shelf life?", a: "18–22 months." },
      { q: "Markets?", a: "Value-tier Saudi, Yemen, Iraq, Egypt, East African budget retail." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["pr14-overview", "pr14-sella", "pr11-golden-sella", "1121-golden-sella"],
    productSchemaName: "PR14 Golden Sella Non-Basmati Rice", productSchemaDescription: "PR14 deep-golden parboiled non-basmati. Value-tier Gulf and African retail. FSSAI/APEDA registered.",
  },

  // ----- Parmal -----
  "parmal-overview": {
    slug: "parmal-non-basmati-rice", folio: "№ 68", sectionLabel: "PARMAL", category: "Rice", enrichedSchema: true,
    seoTitle: "Parmal Non-Basmati Rice Exporter from India",
    seoDescription: "Parmal non-basmati rice exporter from India — long-grain North Indian variety in raw, steam, sella and golden sella forms.",
    h1: "Parmal Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Parmal non-basmati rice from India — Vilora Impex",
    eyebrow: "Parmal — long-grain North Indian non-basmati",
    standfirst: "Vilora Impex exports Parmal non-basmati rice — a long-grain North Indian paddy variety in all four processing forms. The volume choice for bulk distributor channels and value retail across the Gulf, East Africa and South Asia.",
    introH2: "What Parmal is", introBody: "Parmal is a long-grain non-basmati paddy variety widely grown in North India (Uttar Pradesh, Haryana, Punjab). Grain length 6.5–7.2 mm, neutral aroma, faster ageing than basmati varieties. Available raw, steam, sella and golden sella. Comparable to PR11/PR14 in market positioning and pricing.",
    productsH2: "Parmal grades", products: [
      { name: "Parmal Raw", spec: "Natural milled white • bulk distributor and tender" },
      { name: "Parmal Steam", spec: "Steam-processed creamy white • value retail" },
      { name: "Parmal Sella", spec: "Parboiled creamy/light-golden • Gulf and African bulk" },
      { name: "Parmal Golden Sella", spec: "Deep parboil • value Saudi/UAE retail" },
      { name: "Parmal Broken", spec: "Sortex-cleaned brokens for food-ingredient buyers" },
      { name: "Custom Parmal Cuttest", spec: "Buyer-locked spec, processing form" },
    ],
    packagingH2: "Parmal packaging and shipping", packagingBody: "FCL MOQ one 20ft (~25 MT) or 40ft (~50 MT). Packaging 25/50 kg PP or jute. Tender supply with PSI inspection on request. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship Parmal", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why distributors choose Parmal", why: WHY_DEFAULT,
    faqsH2: "Parmal export FAQs", faqs: [
      { q: "How does Parmal differ from PR11/PR14?", a: "All are long-grain North Indian non-basmati varieties. Parmal is the traditional name; PR11/PR14 are specific cultivar codes. At export-grade level they're interchangeable for most buyers; specific cultivar matters at the milling partner." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT)." },
      { q: "Markets?", a: "Same as PR11/PR14 — Iraq, Yemen, Egypt, East Africa, Bangladesh." },
      { q: "Can Parmal be blended with PR11/PR14?", a: "Yes — common practice for tender supply where the spec calls for 'long-grain Indian non-basmati' without specifying cultivar." },
    ],
    related: ["parmal-raw", "parmal-steam", "parmal-sella", "pr11-overview"],
    productSchemaName: "Parmal Non-Basmati Rice", productSchemaDescription: "Parmal long-grain non-basmati from North India. All 4 processing forms for bulk distributor supply. FSSAI/APEDA registered.",
  },
  "parmal-raw": {
    slug: "parmal-raw-non-basmati-rice", folio: "№ 69", sectionLabel: "PARMAL RAW", category: "Rice", enrichedSchema: true,
    seoTitle: "Parmal Raw Non-Basmati Rice Exporter from India",
    seoDescription: "Parmal Raw non-basmati rice exporter from India — natural milled long grain for bulk distributor and tender supply.",
    h1: "Parmal Raw Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Parmal Raw non-basmati rice from India — Vilora Impex",
    eyebrow: "Parmal Raw — bulk volume", standfirst: "Vilora Impex exports Parmal Raw non-basmati rice — the natural-milled white form. Bulk distributor channels, food-aid tender supply, and value retail at non-basmati pricing.",
    introH2: "Parmal Raw spec", introBody: "Parmal Raw is the natural-milled white grain. Neutral aroma, soft cooked texture, 8–14 month shelf life. Best for fast-rotating distribution and food-aid pipelines.",
    productsH2: "Parmal Raw grades", products: [
      { name: "Parmal Raw — Standard", spec: "5–15% broken • 25/50 kg PP" },
      { name: "Parmal Raw — Premium", spec: "<5% broken • value-retail private label" },
      { name: "Parmal Raw — Bulk", spec: "50 kg PP/jute for wholesale" },
      { name: "Parmal Raw — Tender Spec", spec: "WFP/government tender with PSI" },
      { name: "Parmal Raw — Brokens", spec: "Sortex-cleaned brokens" },
      { name: "Custom Parmal Raw", spec: "Buyer-locked spec" },
    ],
    packagingH2: "Parmal Raw packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Packaging 25/50 kg PP. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship Parmal Raw", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why bulk distributors pick Parmal Raw", why: WHY_DEFAULT,
    faqsH2: "Parmal Raw export FAQs", faqs: [
      { q: "Parmal Raw vs PR11/PR14 Raw?", a: "Effectively interchangeable. Choice depends on milling partner and current supply." },
      { q: "Shelf life?", a: "8–14 months." },
      { q: "Markets?", a: "Iraq, Yemen, East Africa, Bangladesh, food-aid tender." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["parmal-overview", "parmal-steam", "pr11-raw", "ir64-overview"],
    productSchemaName: "Parmal Raw Non-Basmati Rice", productSchemaDescription: "Parmal natural milled long-grain non-basmati. Bulk supply. FSSAI/APEDA registered.",
  },
  "parmal-steam": {
    slug: "parmal-steam-non-basmati-rice", folio: "№ 70", sectionLabel: "PARMAL STEAM", category: "Rice", enrichedSchema: true,
    seoTitle: "Parmal Steam Non-Basmati Rice Exporter from India",
    seoDescription: "Parmal Steam non-basmati rice exporter from India — steam-processed creamy-white long grain, value retail.",
    h1: "Parmal Steam Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Parmal Steam non-basmati rice from India — Vilora Impex",
    eyebrow: "Parmal Steam — value retail",
    standfirst: "Vilora Impex exports Parmal Steam non-basmati rice — the steam-processed creamy-white form. Value HoReCa and retail supply at non-basmati pricing.",
    introH2: "Parmal Steam spec", introBody: "Parmal Steam is milled then steam-processed — creamy-white grain with mid-range cook-stability. Cooks in 14–18 minutes. Bridge between Raw and Sella in non-basmati category.",
    productsH2: "Parmal Steam grades", products: [
      { name: "Parmal Steam — Standard", spec: "5–10% broken • 25 kg PP" },
      { name: "Parmal Steam — Retail", spec: "5/10 kg PP retail packs" },
      { name: "Parmal Steam — Bulk", spec: "25/50 kg PP" },
      { name: "Parmal Steam — Private Label", spec: "Buyer-branded value tier" },
      { name: "Long-Grain Steam Blend", spec: "Parmal/PR11/PR14 Steam blend for tender" },
      { name: "Custom Parmal Steam", spec: "Buyer-locked spec" },
    ],
    packagingH2: "Parmal Steam packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Packaging 5/10/25/50 kg PP.",
    portsH2: "Where we ship Parmal Steam", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why HoReCa picks Parmal Steam", why: WHY_DEFAULT,
    faqsH2: "Parmal Steam export FAQs", faqs: [
      { q: "Shelf life?", a: "12–16 months." },
      { q: "Markets?", a: "Singapore, Malaysia value retail, UAE HoReCa, East African retail." },
      { q: "MOQ?", a: "One 20ft FCL." },
      { q: "Can Parmal Steam be blended with PR11/14 Steam?", a: "Yes — common for tender." },
    ],
    related: ["parmal-overview", "parmal-raw", "parmal-sella", "pr11-steam"],
    productSchemaName: "Parmal Steam Non-Basmati Rice", productSchemaDescription: "Parmal steam-processed long-grain non-basmati. Value retail and HoReCa. FSSAI/APEDA registered.",
  },
  "parmal-sella": {
    slug: "parmal-sella", folio: "№ 71", sectionLabel: "PARMAL SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "Parmal Sella Non-Basmati Rice Exporter from India",
    seoDescription: "Parmal Sella non-basmati rice exporter from India — parboiled long grain, bulk Gulf and African retail supply.",
    h1: "Parmal Sella Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Parmal Sella non-basmati rice from India — Vilora Impex",
    eyebrow: "Parmal Sella — Gulf bulk retail",
    standfirst: "Vilora Impex exports Parmal Sella non-basmati rice — parboiled creamy-to-light-golden form. Volume Gulf retail, African distribution, tender supply.",
    introH2: "Parmal Sella spec", introBody: "Parmal Sella is parboiled — hardened grain, creamy-to-light-golden tone, 14–18-month shelf life. Cooks in 18–22 minutes. Non-basmati parallel to 1121 Sella at a fraction of the price.",
    productsH2: "Parmal Sella grades", products: [
      { name: "Parmal Sella — Creamy", spec: "Light parboil • Gulf retail" },
      { name: "Parmal Sella — Standard", spec: "Mid-tone • bulk distributor" },
      { name: "Parmal Sella — Retail Private Label", spec: "5/10/25 kg under buyer brand" },
      { name: "Parmal Sella — Bulk", spec: "50 kg PP/jute" },
      { name: "Long-Grain Sella Blend", spec: "Parmal/PR11/PR14 blend for tender" },
      { name: "Custom Parmal Sella", spec: "Buyer-locked spec" },
    ],
    packagingH2: "Parmal Sella packaging", packagingBody: "FCL MOQ one 20ft (~25 MT) or 40ft (~50 MT). Halal cert for Gulf. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship Parmal Sella", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why distributors pick Parmal Sella", why: WHY_DEFAULT,
    faqsH2: "Parmal Sella export FAQs", faqs: [
      { q: "Shelf life?", a: "14–18 months." },
      { q: "Markets?", a: "Egypt, Iraq, Yemen, Jordan, Kenya, Tanzania, Bangladesh." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT)." },
      { q: "Parmal Sella vs PR11 Sella?", a: "Interchangeable in supply terms — both Punjab long-grain non-basmati parboiled." },
    ],
    related: ["parmal-overview", "parmal-raw", "parmal-golden-sella", "pr11-sella"],
    productSchemaName: "Parmal Sella Non-Basmati Rice", productSchemaDescription: "Parmal parboiled long-grain non-basmati. Bulk Gulf and African retail. FSSAI/APEDA registered.",
  },
  "parmal-golden-sella": {
    slug: "parmal-golden-sella-non-basmati-rice", folio: "№ 72", sectionLabel: "PARMAL GOLDEN SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "Parmal Golden Sella Non-Basmati Rice Exporter from India",
    seoDescription: "Parmal Golden Sella non-basmati rice exporter from India — deep-golden parboiled long grain, value-tier retail.",
    h1: "Parmal Golden Sella Non-Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Parmal Golden Sella non-basmati rice from India — Vilora Impex",
    eyebrow: "Parmal Golden Sella — budget Gulf retail",
    standfirst: "Vilora Impex exports Parmal Golden Sella non-basmati rice — deep-golden parboiled long-grain at value-tier pricing. Budget Gulf retail and East African distribution.",
    introH2: "Parmal Golden Sella spec", introBody: "Parmal Golden Sella is deep-parboiled — dark-amber grain, maximum shelf life (18–22 months), highest cook stability among Parmal forms. Cooks in 22–26 minutes.",
    productsH2: "Parmal Golden Sella grades", products: [
      { name: "Parmal Golden Sella — Deep Gold", spec: "Max colour • value Gulf retail" },
      { name: "Parmal Golden Sella — Standard", spec: "Mid-gold • bulk distributor" },
      { name: "Parmal Golden Sella — Retail Private Label", spec: "5/10 kg BOPP" },
      { name: "Parmal Golden Sella — Bulk", spec: "25/50 kg PP" },
      { name: "1121/Parmal Golden Sella Blend", spec: "Custom blend for retail price engineering" },
      { name: "Custom Parmal Golden Sella", spec: "Buyer-locked spec" },
    ],
    packagingH2: "Parmal Golden Sella packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Retail 5/10 kg BOPP with bilingual labelling. Bulk 25/50 kg PP. Halal cert for Gulf.",
    portsH2: "Where we ship Parmal Golden Sella", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why budget retail picks Parmal Golden Sella", why: WHY_DEFAULT,
    faqsH2: "Parmal Golden Sella export FAQs", faqs: [
      { q: "Markets?", a: "Value-tier Saudi, Yemen, Iraq, Egypt, East African budget retail." },
      { q: "Shelf life?", a: "18–22 months." },
      { q: "MOQ?", a: "One 20ft FCL." },
      { q: "How does pricing compare to 1121 Golden Sella?", a: "~55–65% cheaper per MT. Same visual deep-golden appearance, shorter grain length." },
    ],
    related: ["parmal-overview", "parmal-sella", "pr11-golden-sella", "1121-golden-sella"],
    productSchemaName: "Parmal Golden Sella Non-Basmati Rice", productSchemaDescription: "Parmal deep-golden parboiled non-basmati. Value-tier Gulf and African retail. FSSAI/APEDA registered.",
  },

  // ----- IR64 (variety×processing) -----
  "ir64-overview": {
    slug: "ir64-rice-exporter", folio: "№ 73", sectionLabel: "IR64", category: "Rice", enrichedSchema: true,
    seoTitle: "IR64 Rice Exporter from India",
    seoDescription: "IR64 rice exporter from India — India's highest-volume non-basmati export. Raw, steam, sella and golden sella for bulk distributor and tender supply.",
    h1: "IR64 Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "IR64 rice from India — Vilora Impex",
    eyebrow: "IR64 — highest-volume Indian non-basmati",
    standfirst: "Vilora Impex exports IR64 rice — the highest-volume Indian non-basmati cultivar. IRRI-developed long-grain variety dual-cropped across Andhra Pradesh, Telangana, West Bengal and Tamil Nadu. The workhorse for bulk distributor, government tender and food-aid supply across West and East Africa, Bangladesh and Indonesia.",
    introH2: "What IR64 is", introBody: "IR64 is a long-grain rice cultivar (6.5–7.2 mm uncooked) developed at the International Rice Research Institute (IRRI) in the Philippines. Adopted across India as a high-yield, semi-dwarf variety. Available in all processing forms; commodity-tier pricing.",
    productsH2: "IR64 grades", products: [
      { name: "IR64 Raw", spec: "Natural milled white • 5–25% broken to spec • bulk supply" },
      { name: "IR64 Steam", spec: "Steam-processed creamy white • value retail and HoReCa" },
      { name: "IR64 Sella", spec: "Parboiled creamy/light-golden • West African and Gulf bulk" },
      { name: "IR64 Golden Sella", spec: "Deep parboil • value retail" },
      { name: "IR64 Tukda (Broken)", spec: "100% broken • food-ingredient, brewing, animal feed" },
      { name: "Custom IR64 Cuttest", spec: "Buyer-locked broken %, processing form" },
    ],
    packagingH2: "IR64 packaging and shipping", packagingBody: "FCL MOQ one 20ft (~25 MT) or 40ft (~50 MT). Standard 25/50 kg PP woven. Government tender contracts with PSI inspection. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship IR64", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why volume buyers choose IR64", why: WHY_DEFAULT,
    faqsH2: "IR64 export FAQs", faqs: [
      { q: "Is IR64 the same as PR11/PR14/Parmal?", a: "All are long-grain non-basmati commodity varieties. IR64 is from southern/eastern India (AP, WB), PR11/14/Parmal are northern. At export-grade level virtually interchangeable for commodity buyers." },
      { q: "Why is IR64 India's highest-volume non-basmati export?", a: "Yield (6–8 MT/hectare), 120-day cycle enabling double-cropping, consistent supply year-round, lowest per-MT FOB price among premium long-grain Indian rice." },
      { q: "Markets?", a: "West Africa (Senegal, Côte d'Ivoire, Nigeria), East Africa (Kenya, Tanzania), Bangladesh, Indonesia value retail, food-aid tender pipelines." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT) standard." },
    ],
    related: ["ir64-raw", "ir64-steam", "ir64-sella", "non-basmati", "bulk-non-basmati"],
    productSchemaName: "IR64 Rice from India", productSchemaDescription: "IR64 long-grain non-basmati rice — India's highest-volume non-basmati export. All processing forms for bulk distributor and tender supply. FSSAI/APEDA registered.",
  },
  "ir64-raw": {
    slug: "ir64-raw-rice", folio: "№ 74", sectionLabel: "IR64 RAW", category: "Rice", enrichedSchema: true,
    seoTitle: "IR64 Raw Rice Exporter from India",
    seoDescription: "IR64 Raw rice exporter from India — natural milled long-grain non-basmati for bulk distributor, tender and food-aid supply.",
    h1: "IR64 Raw Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "IR64 Raw rice from India — Vilora Impex",
    eyebrow: "IR64 Raw — bulk distributor", standfirst: "Vilora Impex exports IR64 Raw rice — the natural-milled white form. The volume choice for West African bulk distribution, Bangladesh value retail, Indonesian retail and food-aid tender supply.",
    introH2: "IR64 Raw spec", introBody: "IR64 Raw is the natural-milled white grain. Neutral aroma, 5–25% broken to buyer spec. Best for fast-rotating distribution and food-aid pipelines. Cooks in 12–16 minutes.",
    productsH2: "IR64 Raw grades", products: [
      { name: "IR64 Raw 5% broken", spec: "Premium retail, gift packs • $640–$720/MT" },
      { name: "IR64 Raw 10% broken", spec: "Standard retail • $580–$660/MT" },
      { name: "IR64 Raw 15% broken", spec: "Distributor, HoReCa • $520–$610/MT" },
      { name: "IR64 Raw 25% broken", spec: "Bulk distributor, food-aid • $460–$540/MT" },
      { name: "IR64 Raw 100% Broken (Tukda)", spec: "Food-ingredient, brewing • $340–$430/MT" },
      { name: "Custom IR64 Raw Cuttest", spec: "Buyer-locked broken %, moisture, polish" },
    ],
    packagingH2: "IR64 Raw packaging", packagingBody: "FCL MOQ one 20ft (~25 MT) or 40ft (~50 MT). 25/50 kg PP woven. Tender contracts may require buyer-branded sacks with PSI. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship IR64 Raw", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why volume buyers pick IR64 Raw", why: WHY_DEFAULT,
    faqsH2: "IR64 Raw export FAQs", faqs: [
      { q: "What broken % should I specify?", a: "5% for premium retail, 10–15% for standard retail and HoReCa, 25% for bulk distribution and food-aid. Lower broken = higher price." },
      { q: "Shelf life?", a: "8–14 months." },
      { q: "Markets?", a: "Senegal, Côte d'Ivoire, Nigeria, Bangladesh, Indonesia, Kenya, Tanzania." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT)." },
    ],
    related: ["ir64-overview", "ir64-steam", "ir64-sella", "non-basmati"],
    productSchemaName: "IR64 Raw Rice", productSchemaDescription: "IR64 natural milled long-grain non-basmati. Bulk distributor and tender supply. FSSAI/APEDA registered.",
  },
  "ir64-steam": {
    slug: "ir64-steam-rice", folio: "№ 75", sectionLabel: "IR64 STEAM", category: "Rice", enrichedSchema: true,
    seoTitle: "IR64 Steam Rice Exporter from India",
    seoDescription: "IR64 Steam rice exporter from India — steam-processed long-grain non-basmati for value retail and HoReCa.",
    h1: "IR64 Steam Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "IR64 Steam rice from India — Vilora Impex",
    eyebrow: "IR64 Steam — value HoReCa", standfirst: "Vilora Impex exports IR64 Steam rice — steam-processed creamy-white form. Cook-stable value-tier supply for HoReCa and retail across the Gulf, ASEAN and East Africa.",
    introH2: "IR64 Steam spec", introBody: "IR64 Steam is milled then steam-processed — creamy-white grain, mid-range cook stability. Cooks in 14–18 minutes. Bridge between Raw and Sella at non-basmati commodity pricing.",
    productsH2: "IR64 Steam grades", products: [
      { name: "IR64 Steam — Premium Retail", spec: "5–10% broken • 5/10 kg BOPP" },
      { name: "IR64 Steam — Standard", spec: "15% broken • 25 kg PP" },
      { name: "IR64 Steam — Bulk Distributor", spec: "50 kg PP/jute" },
      { name: "IR64 Steam — Private Label", spec: "Buyer-branded value tier" },
      { name: "IR64 Steam — HoReCa", spec: "25 kg PP for biryani-style chain volume" },
      { name: "Custom IR64 Steam", spec: "Buyer-locked spec" },
    ],
    packagingH2: "IR64 Steam packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Packaging 5/10/25/50 kg PP. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship IR64 Steam", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why HoReCa picks IR64 Steam", why: WHY_DEFAULT,
    faqsH2: "IR64 Steam export FAQs", faqs: [
      { q: "Shelf life?", a: "12–16 months." },
      { q: "Markets?", a: "Singapore, Malaysia value retail, UAE HoReCa value tier, East African retail." },
      { q: "MOQ?", a: "One 20ft FCL." },
      { q: "IR64 Steam vs PR11 Steam?", a: "Same processing form; different origin region (IR64 from southern India, PR11 from Punjab). Choice depends on milling-partner availability." },
    ],
    related: ["ir64-overview", "ir64-raw", "ir64-sella", "pr11-steam"],
    productSchemaName: "IR64 Steam Rice", productSchemaDescription: "IR64 steam-processed long-grain non-basmati. Value retail and HoReCa. FSSAI/APEDA registered.",
  },
  "ir64-sella": {
    slug: "ir64-sella-rice", folio: "№ 76", sectionLabel: "IR64 SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "IR64 Sella Rice Exporter from India",
    seoDescription: "IR64 Sella rice exporter from India — parboiled long-grain non-basmati for West African bulk and Gulf retail supply.",
    h1: "IR64 Sella Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "IR64 Sella rice from India — Vilora Impex",
    eyebrow: "IR64 Sella — West African and Gulf bulk",
    standfirst: "Vilora Impex exports IR64 Sella rice — parboiled creamy-to-light-golden long-grain non-basmati. The volume choice for West African bulk distribution, Gulf budget retail, and Bangladesh staple supply.",
    introH2: "IR64 Sella spec", introBody: "IR64 Sella is parboiled — hardened grain, creamy-to-light-golden tone, 14–18-month shelf life. The volume non-basmati Sella for African and South Asian markets.",
    productsH2: "IR64 Sella grades", products: [
      { name: "IR64 Sella — Creamy", spec: "Light parboil • Gulf budget retail" },
      { name: "IR64 Sella — Standard", spec: "Mid-tone parboil • West African bulk" },
      { name: "IR64 Sella — Premium", spec: "Aged 3+ months • value retail private label" },
      { name: "IR64 Sella — Bulk", spec: "50 kg PP/jute" },
      { name: "IR64 Sella — Tender Spec", spec: "Government tender supply with PSI" },
      { name: "Custom IR64 Sella", spec: "Buyer-locked parboil intensity" },
    ],
    packagingH2: "IR64 Sella packaging", packagingBody: "FCL MOQ one 20ft (~25 MT) or 40ft (~50 MT). 25/50 kg PP/jute for bulk; 5/10 kg BOPP for retail. Halal cert for Gulf. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship IR64 Sella", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why African distributors pick IR64 Sella", why: WHY_DEFAULT,
    faqsH2: "IR64 Sella export FAQs", faqs: [
      { q: "Shelf life?", a: "14–18 months." },
      { q: "Markets?", a: "West Africa (Senegal, Côte d'Ivoire, Nigeria), Bangladesh, Yemen, Egypt." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT)." },
      { q: "How does IR64 Sella compare on price?", a: "Among the cheapest premium long-grain Indian Sella options — 50–60% below 1121 Sella per MT." },
    ],
    related: ["ir64-overview", "ir64-raw", "ir64-golden-sella", "pr11-sella"],
    productSchemaName: "IR64 Sella Rice", productSchemaDescription: "IR64 parboiled long-grain non-basmati. West African and Gulf bulk supply. FSSAI/APEDA registered.",
  },
  "ir64-golden-sella": {
    slug: "ir64-golden-sella-rice", folio: "№ 77", sectionLabel: "IR64 GOLDEN SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "IR64 Golden Sella Rice Exporter from India",
    seoDescription: "IR64 Golden Sella rice exporter from India — deep-golden parboiled long-grain non-basmati. Budget retail and bulk supply.",
    h1: "IR64 Golden Sella Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "IR64 Golden Sella rice from India — Vilora Impex",
    eyebrow: "IR64 Golden Sella — budget bulk retail",
    standfirst: "Vilora Impex exports IR64 Golden Sella rice — deep-golden parboiled long-grain. The cheapest deep-golden long-grain Indian rice option. Volume budget retail and bulk distribution.",
    introH2: "IR64 Golden Sella spec", introBody: "IR64 Golden Sella is deep-parboiled — dark-amber grain, 18–22 month shelf life. Among the lowest-priced deep-golden long-grain Indian options. Cooks in 22–26 minutes.",
    productsH2: "IR64 Golden Sella grades", products: [
      { name: "IR64 Golden Sella — Deep Gold", spec: "Maximum colour • budget retail" },
      { name: "IR64 Golden Sella — Standard", spec: "Mid-gold • bulk distributor" },
      { name: "IR64 Golden Sella — Bulk", spec: "25/50 kg PP" },
      { name: "Long-Grain Golden Sella Blend", spec: "IR64/PR11/PR14 blend for tender" },
      { name: "IR64 Golden Sella — Private Label", spec: "5/10 kg BOPP for budget retail" },
      { name: "Custom IR64 Golden Sella", spec: "Buyer-locked spec" },
    ],
    packagingH2: "IR64 Golden Sella packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Retail 5/10 kg BOPP. Bulk 25/50 kg PP. Halal cert for Gulf.",
    portsH2: "Where we ship IR64 Golden Sella", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why budget retail picks IR64 Golden Sella", why: WHY_DEFAULT,
    faqsH2: "IR64 Golden Sella export FAQs", faqs: [
      { q: "How cheap is IR64 Golden Sella compared to 1121?", a: "~60–70% cheaper per MT. The lowest-priced deep-golden long-grain Indian option. Best for budget retail SKUs and food-aid where shelf-life premium matters but cost matters more." },
      { q: "Shelf life?", a: "18–22 months." },
      { q: "Markets?", a: "Budget Saudi, Yemen, Iraq, Egypt, West and East African budget retail." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["ir64-overview", "ir64-sella", "pr11-golden-sella", "parmal-golden-sella"],
    productSchemaName: "IR64 Golden Sella Rice", productSchemaDescription: "IR64 deep-golden parboiled long-grain non-basmati. Budget retail and bulk supply. FSSAI/APEDA registered.",
  },

  // ----- Sona Masoori variants (we have overview already at /sona-masuri-rice-exporter-india) -----
  "sona-masuri-raw": {
    slug: "sona-masuri-raw-rice", folio: "№ 78", sectionLabel: "SONA MASOORI RAW", category: "Rice", enrichedSchema: true,
    seoTitle: "Sona Masoori Raw Rice Exporter from India",
    seoDescription: "Sona Masoori Raw rice exporter from India — natural milled medium-grain non-basmati, South-Asia diaspora retail and Gulf middle-class.",
    h1: "Sona Masoori Raw Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Sona Masoori Raw rice from India — Vilora Impex",
    eyebrow: "Sona Masoori Raw — diaspora retail standard",
    standfirst: "Vilora Impex exports Sona Masoori Raw rice — the natural-milled medium-grain form of India's premium non-basmati cultivar. The standard for South-Asia diaspora retail in Singapore, UK, USA, and the Gulf middle class.",
    introH2: "Sona Masoori Raw spec", introBody: "Sona Masoori is a medium-grain non-basmati from Andhra Pradesh / Telangana / Karnataka. Raw is the natural-milled white form. Grain length 5.0–5.5 mm, low starch, soft cooked texture. Cooks in 14–16 minutes at 1:1.6 water ratio.",
    productsH2: "Sona Masoori Raw grades", products: [
      { name: "Sona Masoori Raw — Premium", spec: "Aged 6 months • sortex-cleaned • diaspora retail" },
      { name: "Sona Masoori Raw — HMT Variant", spec: "Premium Hyderabadi variant • finer grain" },
      { name: "Sona Masoori Raw — Retail", spec: "5/10 kg BOPP retail packs" },
      { name: "Sona Masoori Raw — Bulk", spec: "25 kg PP for wholesale" },
      { name: "Sona Masoori Raw — Organic", spec: "NPOP-certified organic for EU/US retail" },
      { name: "Custom Sona Masoori Raw", spec: "Buyer-locked moisture, broken %, polish" },
    ],
    packagingH2: "Sona Masoori Raw packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Packaging 5/10/25/50 kg PP, BOPP-printed for retail, jute for bulk. Incoterms: FOB Mundra/Kandla, CIF, CFR. Origin sourcing from AP/Telangana ships from Mundra via inland transport.",
    portsH2: "Where we ship Sona Masoori Raw", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why diaspora retail picks Sona Masoori Raw", why: WHY_DEFAULT,
    faqsH2: "Sona Masoori Raw export FAQs", faqs: [
      { q: "Why Raw over Sella for Sona Masoori?", a: "Diaspora consumers (South-Asia heritage) strongly prefer Raw — the traditional everyday-rice form. Sella tones aren't expected in this category." },
      { q: "Shelf life?", a: "10–14 months. Medium-grain Sona Masoori ages faster than long-grain basmati." },
      { q: "Markets?", a: "Singapore, Malaysia, UK Indian-diaspora supermarkets, USA Indian-grocery, UAE/Gulf middle-class retail." },
      { q: "MOQ?", a: "One 20ft FCL (~25 MT)." },
    ],
    related: ["sona-masuri", "sona-masuri-sella", "sona-masuri-steam", "non-basmati"],
    productSchemaName: "Sona Masoori Raw Rice", productSchemaDescription: "Sona Masoori natural milled medium-grain non-basmati. South-Asia diaspora retail and Gulf middle-class. FSSAI/APEDA registered.",
  },
  "sona-masuri-steam": {
    slug: "sona-masuri-steam-rice", folio: "№ 79", sectionLabel: "SONA MASOORI STEAM", category: "Rice", enrichedSchema: true,
    seoTitle: "Sona Masoori Steam Rice Exporter from India",
    seoDescription: "Sona Masoori Steam rice exporter from India — steam-processed medium-grain non-basmati, improved cook stability for HoReCa.",
    h1: "Sona Masoori Steam Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Sona Masoori Steam rice from India — Vilora Impex",
    eyebrow: "Sona Masoori Steam — HoReCa-grade medium-grain",
    standfirst: "Vilora Impex exports Sona Masoori Steam rice — steam-processed medium-grain non-basmati with improved cook stability over Raw. Best for South-Indian HoReCa and food-service supply.",
    introH2: "Sona Masoori Steam spec", introBody: "Sona Masoori Steam is milled then gently steam-processed — creamy-white medium-grain with better service-line stability than Raw. Cooks in 15–18 minutes. South-Indian biryani chains, pongal/upma HoReCa.",
    productsH2: "Sona Masoori Steam grades", products: [
      { name: "Sona Masoori Steam — HoReCa", spec: "25 kg PP for South-Indian food-service" },
      { name: "Sona Masoori Steam — Retail", spec: "5/10 kg BOPP retail packs" },
      { name: "Sona Masoori Steam — Premium Aged", spec: "Aged 6+ months • premium retail private label" },
      { name: "Sona Masoori Steam — Bulk", spec: "50 kg PP for wholesale" },
      { name: "Sona Masoori Steam — Organic", spec: "Certified organic for EU/US diaspora" },
      { name: "Custom Sona Masoori Steam", spec: "Buyer-locked spec" },
    ],
    packagingH2: "Sona Masoori Steam packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Packaging 5/10/25/50 kg PP. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship Sona Masoori Steam", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why HoReCa picks Sona Masoori Steam", why: WHY_DEFAULT,
    faqsH2: "Sona Masoori Steam export FAQs", faqs: [
      { q: "Why Steam over Raw for HoReCa Sona Masoori?", a: "Better cook-stability for volume service. Raw softens too quickly under chafing-dish service for South-Indian biryani chains; Steam holds shape." },
      { q: "Shelf life?", a: "12–16 months." },
      { q: "Markets?", a: "Singapore South-Indian HoReCa, UAE South-Indian restaurant supply, USA diaspora retail, India-Gulf middle-class." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["sona-masuri", "sona-masuri-raw", "sona-masuri-sella", "ir64-steam"],
    productSchemaName: "Sona Masoori Steam Rice", productSchemaDescription: "Sona Masoori steam-processed medium-grain non-basmati. HoReCa-grade. FSSAI/APEDA registered.",
  },
  "sona-masuri-sella": {
    slug: "sona-masuri-sella-rice", folio: "№ 80", sectionLabel: "SONA MASOORI SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "Sona Masoori Sella Rice Exporter from India",
    seoDescription: "Sona Masoori Sella rice exporter from India — parboiled medium-grain non-basmati with long shelf life for Gulf retail.",
    h1: "Sona Masoori Sella Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Sona Masoori Sella rice from India — Vilora Impex",
    eyebrow: "Sona Masoori Sella — long-shelf retail",
    standfirst: "Vilora Impex exports Sona Masoori Sella rice — the parboiled form of India's premium medium-grain non-basmati. Long shelf life, creamy-golden tone, for retail brands needing Sona Masoori with extended retail shelf rotation.",
    introH2: "Sona Masoori Sella spec", introBody: "Sona Masoori Sella is parboiled — medium-grain with hardened endosperm. Creamy to light-golden tone, 16–20-month shelf life. Cooks in 18–22 minutes at 1:1.8 water ratio. The long-shelf option in the Sona Masoori family.",
    productsH2: "Sona Masoori Sella grades", products: [
      { name: "Sona Masoori Sella — Creamy", spec: "Light parboil • Gulf retail" },
      { name: "Sona Masoori Sella — Standard", spec: "Mid-tone parboil • bulk distributor" },
      { name: "Sona Masoori Sella — Retail Private Label", spec: "5/10/25 kg under buyer brand" },
      { name: "Sona Masoori Sella — Bulk", spec: "50 kg PP/jute" },
      { name: "Sona Masoori Sella — HMT Sella", spec: "Premium Hyderabadi Sella" },
      { name: "Custom Sona Masoori Sella", spec: "Buyer-locked parboil intensity, broken %" },
    ],
    packagingH2: "Sona Masoori Sella packaging", packagingBody: "FCL MOQ one 20ft (~25 MT). Packaging 5/10/25/50 kg PP, BOPP for retail. Halal cert for Gulf retail. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship Sona Masoori Sella", portsBody: PORTS_DEFAULT,
    process: PROCESS_DEFAULT, whyH2: "Why retail picks Sona Masoori Sella", why: WHY_DEFAULT,
    faqsH2: "Sona Masoori Sella export FAQs", faqs: [
      { q: "Is Sona Masoori usually Sella?", a: "Not traditionally — Sona Masoori is most commonly traded as Raw. Sella exists for retail brands needing the long-shelf advantage. Most diaspora retail still prefers Raw." },
      { q: "Shelf life?", a: "16–20 months — comparable to 1121 Sella." },
      { q: "Markets?", a: "Gulf middle-class retail, Maldives diaspora retail, Bangladesh value retail." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["sona-masuri", "sona-masuri-raw", "sona-masuri-steam"],
    productSchemaName: "Sona Masoori Sella Rice", productSchemaDescription: "Sona Masoori parboiled medium-grain non-basmati. Long shelf life retail. FSSAI/APEDA registered.",
  },

  /* ─── 1401 BASMATI (Pusa 1401) — 2026-06-01 gap-fill wave ─────── */
  "1401-overview": {
    slug: "1401-basmati-rice-exporter", folio: "№ 81", sectionLabel: "1401 BASMATI", category: "Rice", enrichedSchema: true,
    seoTitle: "1401 Basmati Rice Exporter from India",
    seoDescription: "1401 Basmati Rice exporter from India — extra-long-grain Pusa 1401, value-premium aromatic basmati for Gulf, Iraqi and African retail. FSSAI/APEDA registered.",
    h1: "1401 Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1121-steam.jpg", imageAlt: "1401 Basmati Rice from India — Vilora Impex",
    eyebrow: "Pusa 1401 — value-premium aromatic",
    standfirst: "Vilora Impex exports 1401 Basmati Rice — Pusa Basmati 1401, an evolved extra-long-grain cultivar that sits between 1509 and 1121 on both price and aroma. Strong cooked elongation, a clean aroma profile and a consistently lower per-MT cost than 1121 make it the workhorse aromatic basmati for Gulf, Iraqi and African retail private label.",
    introH2: "What 1401 Basmati is",
    introBody: "Pusa Basmati 1401 is a high-yielding evolved basmati released by IARI. Grain length 8.0–8.3 mm, pronounced aroma and reliable cooked elongation around 2x. It delivers most of the 1121 eating experience at a price closer to 1509 — which is why it moves in volume through Middle-East and East-African retail channels.",
    productsH2: "1401 grades we export", products: [
      { name: "1401 Raw — Premium Aged", spec: "8.0–8.3 mm • aged 9–12 months • <2% broken • two-pass sortex" },
      { name: "1401 Steam — Gulf Retail", spec: "Steam-finished white • firm separate grain" },
      { name: "1401 Sella — Parboiled", spec: "Cream parboil • long shelf life • value retail" },
      { name: "1401 Golden Sella", spec: "Deep-golden parboil • Iraq and Yemen retail" },
      { name: "1401 — Bulk Distributor", spec: "25/50 kg PP for wholesale channels" },
      { name: "Custom 1401 Cuttest", spec: "Buyer-locked grain length, broken %, ageing" },
    ],
    packagingH2: "1401 packaging and shipping", packagingBody: "FCL MOQ is one 20ft container (~25 MT). 5/10/25/50 kg PP, BOPP-printed laminate, jute or buyer-branded private label. Halal certification included for Gulf retail SKUs. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship 1401 basmati", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why importers pick 1401", why: WHY_DEFAULT,
    faqsH2: "1401 basmati export FAQs", faqs: [
      { q: "Where does 1401 sit between 1509 and 1121?", a: "On both grain length and price. 1401 is longer-grained and more aromatic than 1509, shorter and more affordable than 1121 — the mid-tier aromatic choice, typically 8–15% cheaper per MT than 1121." },
      { q: "Is 1401 a Pusa variety?", a: "Yes — Pusa Basmati 1401, developed at the Indian Agricultural Research Institute (IARI), Pusa, Delhi. Trade refers to it simply as 1401." },
      { q: "Which markets buy 1401?", a: "Iraq, Saudi Arabia and UAE value retail, Yemen, Jordan, and East and West Africa — markets that want a genuine aromatic basmati at a working price point." },
      { q: "What is the MOQ?", a: "One 20ft FCL (~25 MT) per grade. Private label is one FCL per SKU." },
    ],
    related: ["1401-raw", "1401-steam", "1401-sella", "1121-basmati", "1509-basmati"],
    productSchemaName: "1401 Basmati Rice from India", productSchemaDescription: "Pusa 1401 extra-long-grain basmati, 8.0–8.3 mm, value-premium aromatic. FSSAI/APEDA registered.",
  },
  "1401-raw": {
    slug: "1401-raw-basmati-rice", folio: "№ 82", sectionLabel: "1401 RAW", category: "Rice", enrichedSchema: true,
    seoTitle: "1401 Raw Basmati Rice Exporter from India",
    seoDescription: "1401 Raw Basmati Rice exporter from India — natural-milled white Pusa 1401, extra-long aromatic grain for premium and diaspora retail.",
    h1: "1401 Raw Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "1401 Raw Basmati Rice from India — Vilora Impex",
    eyebrow: "Natural-milled white — maximum aroma",
    standfirst: "Vilora Impex exports 1401 Raw Basmati Rice — the natural-milled white form of Pusa 1401. Raw milling retains the cultivar's full aroma, making it the form of choice for diaspora retail and HoReCa buyers who want a fragrant, value-premium basmati that still cooks long and separate.",
    introH2: "What 1401 Raw means",
    introBody: "Raw is milled directly from dried paddy with no parboiling — the grain stays bright white and keeps the most aroma. 1401 Raw is aged 9–12 months to firm up cook behaviour. It is moisture-sensitive, so BOPP laminate is recommended for tropical destinations.",
    productsH2: "1401 Raw grades we export", products: [
      { name: "1401 Raw — Premium Aged", spec: "8.0–8.3 mm • aged 12 months • <2% broken" },
      { name: "1401 Raw — Fresh Crop", spec: "Current-season supply at the milling partner" },
      { name: "1401 Raw — Diaspora Retail", spec: "5/10 kg packs for South-Asian supermarket retail" },
      { name: "1401 Raw — Bulk", spec: "25/50 kg PP for wholesale distributors" },
      { name: "Custom 1401 Raw Spec", spec: "Buyer-locked grain length, broken %, polish, ageing" },
    ],
    packagingH2: "1401 Raw packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10/25/50 kg PP, BOPP laminate, jute or private label. BOPP recommended for humid markets. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship 1401 Raw basmati", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why retail picks 1401 Raw", why: WHY_DEFAULT,
    faqsH2: "1401 Raw basmati export FAQs", faqs: [
      { q: "Is Raw more aromatic than Sella?", a: "Yes. Parboiling reduces aroma, so Raw 1401 carries the strongest fragrance of the four forms — best for aroma-led retail and gourmet HoReCa." },
      { q: "How long is 1401 Raw aged?", a: "Typically 9–12 months. Ageing firms the grain so it cooks long and separate rather than sticky." },
      { q: "Which markets prefer Raw?", a: "UK, Canada and US diaspora retail, premium Gulf retail, and HoReCa accounts that cook to order." },
    ],
    related: ["1401-overview", "1401-steam", "1121-raw", "1121-basmati"],
    productSchemaName: "1401 Raw Basmati Rice from India", productSchemaDescription: "Pusa 1401 natural-milled white basmati, 8.0–8.3 mm, aged, maximum aroma. FSSAI/APEDA registered.",
  },
  "1401-steam": {
    slug: "1401-steam-basmati-rice", folio: "№ 83", sectionLabel: "1401 STEAM", category: "Rice", enrichedSchema: true,
    seoTitle: "1401 Steam Basmati Rice Exporter from India",
    seoDescription: "1401 Steam Basmati Rice exporter from India — steam-finished Pusa 1401, firm separate grain for Gulf retail and food service.",
    h1: "1401 Steam Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1121-steam.jpg", imageAlt: "1401 Steam Basmati Rice from India — Vilora Impex",
    eyebrow: "Steam-finished white — firm separate grain",
    standfirst: "Vilora Impex exports 1401 Steam Basmati Rice — Pusa 1401 finished with light steam pressure that firms the grain without the colour or shelf-life shift of full parboiling. The result is a bright white, non-sticky grain that holds its shape in bulk catering — a popular middle ground between Raw and Sella for Gulf retail and food service.",
    introH2: "What 1401 Steam means",
    introBody: "Steam (or steam-polished) treatment passes light steam over the milled grain to harden the starch surface. Grain stays white, cooks firmer and more separate than Raw, with most of the aroma retained. Shelf life sits between Raw and Sella.",
    productsH2: "1401 Steam grades we export", products: [
      { name: "1401 Steam — Gulf Retail", spec: "Bright white • firm separate cook" },
      { name: "1401 Steam — HoReCa", spec: "Catering grade • holds shape under volume cooking" },
      { name: "1401 Steam — Private Label", spec: "5/10 kg BOPP under buyer brand" },
      { name: "1401 Steam — Bulk", spec: "25/50 kg PP for wholesale" },
      { name: "Custom 1401 Steam Cuttest", spec: "Buyer-locked spec" },
    ],
    packagingH2: "1401 Steam packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10/25/50 kg PP, BOPP laminate, private label. Halal for Gulf SKUs. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship 1401 Steam basmati", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why food service picks 1401 Steam", why: WHY_DEFAULT,
    faqsH2: "1401 Steam basmati export FAQs", faqs: [
      { q: "What is the difference between Steam and Sella?", a: "Steam is a light surface treatment that keeps grain white; Sella is full parboiling that hardens the grain through and adds a cream-to-golden tone and longer shelf life. Steam cooks closer to Raw, Sella is the most cook-stable." },
      { q: "Is Steam good for biryani and catering?", a: "Yes — firm separate grain holds shape through bulk and reheat cycles, which is why HoReCa and caterers favour Steam." },
      { q: "MOQ?", a: "One 20ft FCL per grade." },
    ],
    related: ["1401-overview", "1401-raw", "1401-sella", "1121-steam"],
    productSchemaName: "1401 Steam Basmati Rice from India", productSchemaDescription: "Pusa 1401 steam-finished basmati, firm separate white grain. FSSAI/APEDA registered.",
  },
  "1401-sella": {
    slug: "1401-sella-basmati-rice", folio: "№ 84", sectionLabel: "1401 SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "1401 Sella Basmati Rice Exporter from India",
    seoDescription: "1401 Sella Basmati Rice exporter from India — parboiled Pusa 1401, cook-stable cream grain with long shelf life for value retail.",
    h1: "1401 Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "1401 Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Parboiled — cook-stable, long shelf life",
    standfirst: "Vilora Impex exports 1401 Sella Basmati Rice — the parboiled form of Pusa 1401. Hardened cream grain, 18-month-plus shelf life and forgiving cook behaviour make it a dependable value-retail and HoReCa basmati that resists breakage through long supply chains.",
    introH2: "What 1401 Sella means",
    introBody: "Sella is parboiled — paddy steamed in the husk before milling, which hardens the grain and lifts the tone to cream. The result is the most cook-tolerant 1401 form: low breakage, long shelf life and consistent separate grain even when overcooked. The aromatic edge softens versus Raw.",
    productsH2: "1401 Sella grades we export", products: [
      { name: "1401 Sella — Cream", spec: "Light parboil • value retail and HoReCa" },
      { name: "1401 Sella — Standard", spec: "Mid-tone parboil • Gulf retail private label" },
      { name: "1401 Sella — Bulk Distributor", spec: "25/50 kg PP for wholesale" },
      { name: "1401 Sella — UAE Spec", spec: "MOIAT/ESMA-compliant labelled packs" },
      { name: "Custom 1401 Sella Cuttest", spec: "Buyer-locked parboil intensity and spec" },
    ],
    packagingH2: "1401 Sella packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10 kg BOPP retail, 25/50 kg PP bulk. Private label one FCL per SKU. Halal for Gulf retail. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship 1401 Sella basmati", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why value retail picks 1401 Sella", why: WHY_DEFAULT,
    faqsH2: "1401 Sella basmati export FAQs", faqs: [
      { q: "Why choose 1401 Sella over 1509 Sella?", a: "Longer, more aromatic grain. 1401 Sella reads more premium on the plate than 1509 Sella while staying well below 1121 Sella on price — a useful mid-tier Sella SKU." },
      { q: "What is the shelf life?", a: "18–22 months in cool, dry storage — typical of parboiled basmati." },
      { q: "Which markets import 1401 Sella?", a: "Iraq, Yemen, Egypt, Jordan, value-tier Saudi and UAE retail, and East Africa." },
    ],
    related: ["1401-overview", "1401-golden-sella", "1509-sella", "1121-sella"],
    productSchemaName: "1401 Sella Basmati Rice from India", productSchemaDescription: "Pusa 1401 parboiled basmati, cream grain, long shelf life, value retail. FSSAI/APEDA registered.",
  },
  "1401-golden-sella": {
    slug: "1401-golden-sella-basmati-rice", folio: "№ 85", sectionLabel: "1401 GOLDEN SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "1401 Golden Sella Basmati Rice Exporter from India",
    seoDescription: "1401 Golden Sella Basmati Rice exporter from India — deep-golden parboiled Pusa 1401, premium-colour value-tier spec for Gulf retail.",
    h1: "1401 Golden Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "1401 Golden Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Deep-golden parboil — premium colour, value tier",
    standfirst: "Vilora Impex exports 1401 Golden Sella Basmati Rice — the deep-parboiled, dark-golden form of Pusa 1401. It gives retail brands the premium golden colour Gulf consumers associate with quality, with the longest shelf life of any 1401 form, at a price band below 1121 Golden Sella.",
    introH2: "What 1401 Golden Sella means",
    introBody: "Golden Sella is the deepest parboil — intense steam produces a dark-golden grain and maximum cook-stability. 1401 Golden Sella delivers that signature colour and shelf life at a value-tier price, used by brands launching golden-Sella SKUs at competitive shelf prices.",
    productsH2: "1401 Golden Sella grades we export", products: [
      { name: "1401 Golden Sella — Deep Gold", spec: "Maximum colour • Iraq, Yemen, Saudi value retail" },
      { name: "1401 Golden Sella — Mid Tone", spec: "Balanced golden • Gulf wholesale" },
      { name: "1401 Golden Sella — Retail Private Label", spec: "5/10 kg BOPP under buyer brand" },
      { name: "1401 Golden Sella — Bulk", spec: "25/50 kg PP for wholesale" },
      { name: "Custom Golden Cuttest", spec: "Buyer-locked colour intensity and grain spec" },
    ],
    packagingH2: "1401 Golden Sella packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10 kg BOPP retail, 25/50 kg PP bulk. SFDA labelling for Saudi, MOIAT/ESMA for UAE. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship 1401 Golden Sella", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why retail picks 1401 Golden Sella", why: WHY_DEFAULT,
    faqsH2: "1401 Golden Sella export FAQs", faqs: [
      { q: "Why use 1401 Golden Sella over 1121 Golden Sella?", a: "Price. 1401 Golden Sella carries the same deep-golden visual signature consumers expect, typically 15–25% cheaper per MT than 1121 Golden Sella — ideal for value-tier golden SKUs." },
      { q: "What is the shelf life?", a: "22+ months. The deep parboil produces the most stable grain in the 1401 range." },
      { q: "Markets?", a: "Iraq, Yemen, Egypt, value Saudi retail, East-African diaspora retail." },
    ],
    related: ["1401-overview", "1401-sella", "1509-golden-sella", "1121-golden-sella"],
    productSchemaName: "1401 Golden Sella Basmati Rice from India", productSchemaDescription: "Pusa 1401 deep-golden parboiled basmati, premium-colour value tier. FSSAI/APEDA registered.",
  },

  /* ─── SUGANDHA BASMATI ─────────────────────────────────────── */
  "sugandha-overview": {
    slug: "sugandha-basmati-rice-exporter", folio: "№ 86", sectionLabel: "SUGANDHA BASMATI", category: "Rice", enrichedSchema: true,
    seoTitle: "Sugandha Basmati Rice Exporter from India",
    seoDescription: "Sugandha Basmati Rice exporter from India — fragrant evolved basmati, the volume value-aromatic for Gulf and African retail. FSSAI/APEDA registered.",
    h1: "Sugandha Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "Sugandha Basmati Rice from India — Vilora Impex",
    eyebrow: "Fragrant evolved basmati — volume value tier",
    standfirst: "Vilora Impex exports Sugandha Basmati Rice — an evolved aromatic cultivar whose name literally means fragrant. With pronounced aroma, medium-long grain and one of the most accessible price points in the basmati family, Sugandha is a high-volume retail and HoReCa basmati across the Gulf, Iraq and Africa.",
    introH2: "What Sugandha Basmati is",
    introBody: "Sugandha is an evolved basmati known for strong aroma at a low price. Grain length 8.0–8.4 mm with good cooked elongation. It is most often traded as Sella, Steam or Golden Sella for shelf stability, and is a frequent base for cost-engineered retail blends.",
    productsH2: "Sugandha grades we export", products: [
      { name: "Sugandha Raw", spec: "Natural-milled white • aroma-led retail" },
      { name: "Sugandha Steam", spec: "Firm separate grain • food service" },
      { name: "Sugandha Sella", spec: "Cream parboil • long shelf value retail" },
      { name: "Sugandha Golden Sella", spec: "Deep-golden parboil • Iraq and Yemen retail" },
      { name: "Sugandha — Bulk Distributor", spec: "25/50 kg PP for wholesale" },
      { name: "Custom Sugandha Cuttest", spec: "Buyer-locked grade and form" },
    ],
    packagingH2: "Sugandha packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10/25/50 kg PP, BOPP laminate, private label. Halal for Gulf retail. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship Sugandha basmati", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why importers pick Sugandha", why: WHY_DEFAULT,
    faqsH2: "Sugandha basmati export FAQs", faqs: [
      { q: "Why is Sugandha so popular in the Gulf?", a: "It pairs a genuine basmati aroma with one of the lowest per-MT prices in the family, making it the default value-aromatic for high-rotation Gulf and Iraqi retail." },
      { q: "Which form sells most?", a: "Sella and Golden Sella, because their long shelf life suits distribution-heavy value retail. Raw moves where aroma is the priority." },
      { q: "Can Sugandha be blended?", a: "Yes — it is a common base in 1121/Sugandha and 1509/Sugandha blends used to hit specific shelf prices." },
      { q: "MOQ?", a: "One 20ft FCL per grade; private label one FCL per SKU." },
    ],
    related: ["sugandha-sella", "sugandha-golden-sella", "1509-basmati", "1401-overview"],
    productSchemaName: "Sugandha Basmati Rice from India", productSchemaDescription: "Fragrant evolved basmati, 8.0–8.4 mm, volume value-aromatic. FSSAI/APEDA registered.",
  },
  "sugandha-raw": {
    slug: "sugandha-raw-basmati-rice", folio: "№ 87", sectionLabel: "SUGANDHA RAW", category: "Rice", enrichedSchema: true,
    seoTitle: "Sugandha Raw Basmati Rice Exporter from India",
    seoDescription: "Sugandha Raw Basmati Rice exporter from India — natural-milled white fragrant basmati for aroma-led value retail.",
    h1: "Sugandha Raw Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Sugandha Raw Basmati Rice from India — Vilora Impex",
    eyebrow: "Natural-milled white — aroma-led",
    standfirst: "Vilora Impex exports Sugandha Raw Basmati Rice — the natural-milled white form of the Sugandha cultivar, carrying its full fragrance. The aroma-led, budget-friendly option for diaspora and value retail that still wants a basmati that cooks long and separate.",
    introH2: "What Sugandha Raw means",
    introBody: "Raw milling keeps the grain bright white and preserves maximum aroma. Sugandha Raw is aged to firm cook behaviour and is moisture-sensitive — BOPP laminate suits humid markets.",
    productsH2: "Sugandha Raw grades we export", products: [
      { name: "Sugandha Raw — Aged", spec: "8.0–8.4 mm • aged • <3% broken" },
      { name: "Sugandha Raw — Fresh Crop", spec: "Current-season supply" },
      { name: "Sugandha Raw — Diaspora Retail", spec: "5/10 kg packs" },
      { name: "Sugandha Raw — Bulk", spec: "25/50 kg PP" },
      { name: "Custom Sugandha Raw Spec", spec: "Buyer-locked grade" },
    ],
    packagingH2: "Sugandha Raw packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10/25/50 kg PP, BOPP, private label. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship Sugandha Raw", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why retail picks Sugandha Raw", why: WHY_DEFAULT,
    faqsH2: "Sugandha Raw export FAQs", faqs: [
      { q: "Is Raw the most aromatic Sugandha form?", a: "Yes — Raw retains the most fragrance, since parboiling reduces aroma." },
      { q: "Markets?", a: "Value diaspora retail, Gulf budget retail, African retail." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["sugandha-overview", "sugandha-sella", "1509-raw"],
    productSchemaName: "Sugandha Raw Basmati Rice from India", productSchemaDescription: "Sugandha natural-milled white basmati, aroma-led value. FSSAI/APEDA registered.",
  },
  "sugandha-steam": {
    slug: "sugandha-steam-basmati-rice", folio: "№ 88", sectionLabel: "SUGANDHA STEAM", category: "Rice", enrichedSchema: true,
    seoTitle: "Sugandha Steam Basmati Rice Exporter from India",
    seoDescription: "Sugandha Steam Basmati Rice exporter from India — steam-finished fragrant basmati, firm separate grain for food service and retail.",
    h1: "Sugandha Steam Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "Sugandha Steam Basmati Rice from India — Vilora Impex",
    eyebrow: "Steam-finished — firm separate grain",
    standfirst: "Vilora Impex exports Sugandha Steam Basmati Rice — Sugandha finished with light steam for a firmer, more separate cook than Raw while keeping the grain white. A practical value choice for Gulf food service and retail that needs cook consistency under volume.",
    introH2: "What Sugandha Steam means",
    introBody: "Steam treatment hardens the grain surface so it cooks firm and separate, retaining most aroma and keeping the grain white. Shelf life sits between Raw and Sella.",
    productsH2: "Sugandha Steam grades we export", products: [
      { name: "Sugandha Steam — Retail", spec: "Bright white • firm cook" },
      { name: "Sugandha Steam — HoReCa", spec: "Catering grade" },
      { name: "Sugandha Steam — Private Label", spec: "5/10 kg BOPP" },
      { name: "Sugandha Steam — Bulk", spec: "25/50 kg PP" },
      { name: "Custom Steam Cuttest", spec: "Buyer-locked spec" },
    ],
    packagingH2: "Sugandha Steam packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10/25/50 kg PP, BOPP, private label. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship Sugandha Steam", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why food service picks Sugandha Steam", why: WHY_DEFAULT,
    faqsH2: "Sugandha Steam export FAQs", faqs: [
      { q: "Steam vs Sella for Sugandha?", a: "Steam keeps the grain white and cooks closer to Raw; Sella is fully parboiled, cream-to-golden, with the longest shelf life. Steam suits caterers wanting firm white grain." },
      { q: "Markets?", a: "Gulf food service, value retail." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["sugandha-overview", "sugandha-sella", "1509-steam"],
    productSchemaName: "Sugandha Steam Basmati Rice from India", productSchemaDescription: "Sugandha steam-finished basmati, firm separate white grain. FSSAI/APEDA registered.",
  },
  "sugandha-sella": {
    slug: "sugandha-sella-basmati-rice", folio: "№ 89", sectionLabel: "SUGANDHA SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "Sugandha Sella Basmati Rice Exporter from India",
    seoDescription: "Sugandha Sella Basmati Rice exporter from India — parboiled fragrant basmati, the high-volume value-Sella for Gulf and African retail.",
    h1: "Sugandha Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "Sugandha Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Parboiled — the volume value-Sella",
    standfirst: "Vilora Impex exports Sugandha Sella Basmati Rice — the parboiled form of Sugandha and one of the most shipped value-Sella basmatis in the trade. Cook-stable cream grain, long shelf life and a low per-MT price make it the backbone SKU for distribution-heavy Gulf, Iraqi and African retail.",
    introH2: "What Sugandha Sella means",
    introBody: "Parboiling hardens the grain and lifts the tone to cream, producing the most cook-tolerant Sugandha form with 18-month-plus shelf life and low breakage. The aroma softens versus Raw, traded off for stability and price.",
    productsH2: "Sugandha Sella grades we export", products: [
      { name: "Sugandha Sella — Cream", spec: "Light parboil • value retail" },
      { name: "Sugandha Sella — Standard", spec: "Mid-tone • Gulf retail private label" },
      { name: "Sugandha Sella — Bulk Distributor", spec: "25/50 kg PP" },
      { name: "Sugandha Sella — Blend Base", spec: "Cost-engineering base for retail blends" },
      { name: "Custom Sugandha Sella Cuttest", spec: "Buyer-locked parboil and spec" },
    ],
    packagingH2: "Sugandha Sella packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10 kg BOPP retail, 25/50 kg PP bulk. Halal for Gulf retail. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship Sugandha Sella", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why value retail picks Sugandha Sella", why: WHY_DEFAULT,
    faqsH2: "Sugandha Sella export FAQs", faqs: [
      { q: "Why is Sugandha Sella so widely shipped?", a: "It hits the lowest workable price for a parboiled aromatic basmati with reliable cook-stability — the volume choice for price-led markets." },
      { q: "Shelf life?", a: "18–22 months in cool, dry storage." },
      { q: "Markets?", a: "Iraq, Yemen, Egypt, Jordan, East Africa, value Gulf retail." },
    ],
    related: ["sugandha-overview", "sugandha-golden-sella", "1509-sella", "1401-sella"],
    productSchemaName: "Sugandha Sella Basmati Rice from India", productSchemaDescription: "Sugandha parboiled basmati, cream grain, long shelf value retail. FSSAI/APEDA registered.",
  },
  "sugandha-golden-sella": {
    slug: "sugandha-golden-sella-basmati-rice", folio: "№ 90", sectionLabel: "SUGANDHA GOLDEN SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "Sugandha Golden Sella Basmati Rice Exporter from India",
    seoDescription: "Sugandha Golden Sella Basmati Rice exporter from India — deep-golden parboiled fragrant basmati for Iraq, Yemen and Gulf value retail.",
    h1: "Sugandha Golden Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "Sugandha Golden Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Deep-golden parboil — price-led colour",
    standfirst: "Vilora Impex exports Sugandha Golden Sella Basmati Rice — the deep-golden parboiled form of Sugandha. It delivers the premium golden colour and maximum shelf life that Iraqi and Yemeni retail demand, at the lowest golden-Sella price band in the range.",
    introH2: "What Sugandha Golden Sella means",
    introBody: "The deepest parboil produces dark-golden grain and the longest shelf life. Sugandha Golden Sella is the budget golden-Sella workhorse — the colour and stability of a premium golden Sella at a value price.",
    productsH2: "Sugandha Golden Sella grades we export", products: [
      { name: "Sugandha Golden Sella — Deep Gold", spec: "Maximum colour • Iraq and Yemen retail" },
      { name: "Sugandha Golden Sella — Mid Tone", spec: "Balanced golden • Gulf wholesale" },
      { name: "Sugandha Golden Sella — Private Label", spec: "5/10 kg BOPP" },
      { name: "Sugandha Golden Sella — Bulk", spec: "25/50 kg PP" },
      { name: "Custom Golden Cuttest", spec: "Buyer-locked colour and spec" },
    ],
    packagingH2: "Sugandha Golden Sella packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10 kg BOPP retail, 25/50 kg PP bulk. SFDA/ESMA labelling available. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship Sugandha Golden Sella", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why retail picks Sugandha Golden Sella", why: WHY_DEFAULT,
    faqsH2: "Sugandha Golden Sella export FAQs", faqs: [
      { q: "Why Sugandha Golden Sella?", a: "It is the lowest-priced way to put a deep-golden Sella basmati on the shelf, with full cook-stability — ideal for the high-rotation Iraqi and Yemeni golden-Sella market." },
      { q: "Shelf life?", a: "22+ months." },
      { q: "Markets?", a: "Iraq, Yemen, Egypt, value Gulf retail, East Africa." },
    ],
    related: ["sugandha-overview", "sugandha-sella", "1509-golden-sella", "1401-golden-sella"],
    productSchemaName: "Sugandha Golden Sella Basmati Rice from India", productSchemaDescription: "Sugandha deep-golden parboiled basmati, value golden-Sella. FSSAI/APEDA registered.",
  },

  /* ─── SHARBATI BASMATI ─────────────────────────────────────── */
  "sharbati-overview": {
    slug: "sharbati-basmati-rice-exporter", folio: "№ 91", sectionLabel: "SHARBATI BASMATI", category: "Rice", enrichedSchema: true,
    seoTitle: "Sharbati Basmati Rice Exporter from India",
    seoDescription: "Sharbati Basmati Rice exporter from India — economical medium-long basmati for budget retail, HoReCa and African markets. FSSAI/APEDA registered.",
    h1: "Sharbati Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "Sharbati Basmati Rice from India — Vilora Impex",
    eyebrow: "Economical medium-long basmati",
    standfirst: "Vilora Impex exports Sharbati Basmati Rice — an economical evolved basmati with medium-long grain and a soft, slightly sweet cook. It is the budget basmati of choice for high-volume catering, institutional supply and African and Gulf value retail where price per kilo is the deciding factor.",
    introH2: "What Sharbati Basmati is",
    introBody: "Sharbati is a non-traditional evolved basmati, shorter than 1121 (roughly 7.5–8.0 mm) and priced well below it. It cooks soft with a gentle aroma, making it popular for biryani houses, canteens and budget retail. Most volume moves as Sella, Steam or Golden Sella.",
    productsH2: "Sharbati grades we export", products: [
      { name: "Sharbati Raw", spec: "Natural-milled white • budget retail" },
      { name: "Sharbati Steam", spec: "Firm separate grain • catering" },
      { name: "Sharbati Sella", spec: "Cream parboil • long shelf budget retail" },
      { name: "Sharbati Golden Sella", spec: "Deep-golden parboil • value golden SKUs" },
      { name: "Sharbati — Bulk Distributor", spec: "25/50 kg PP for wholesale and institutions" },
      { name: "Custom Sharbati Cuttest", spec: "Buyer-locked grade and form" },
    ],
    packagingH2: "Sharbati packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10/25/50 kg PP, BOPP laminate, jute, private label. Halal for Gulf retail. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship Sharbati basmati", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why buyers pick Sharbati", why: WHY_DEFAULT,
    faqsH2: "Sharbati basmati export FAQs", faqs: [
      { q: "How is Sharbati different from 1121?", a: "Sharbati is shorter-grained, softer-cooking and significantly cheaper. It trades the long-grain drama of 1121 for a working price point — ideal for catering and budget retail rather than premium SKUs." },
      { q: "Is Sharbati good for biryani houses?", a: "Yes — its soft cook and low price make it a staple for high-volume biryani and pulao kitchens, especially in Sella and Steam forms." },
      { q: "Which markets buy Sharbati?", a: "West and East Africa, Iraq, Yemen, value Gulf retail, and institutional supply." },
      { q: "MOQ?", a: "One 20ft FCL per grade." },
    ],
    related: ["sharbati-sella", "sharbati-golden-sella", "sugandha-overview", "parmal-overview"],
    productSchemaName: "Sharbati Basmati Rice from India", productSchemaDescription: "Economical evolved basmati, 7.5–8.0 mm, budget retail and catering. FSSAI/APEDA registered.",
  },
  "sharbati-raw": {
    slug: "sharbati-raw-basmati-rice", folio: "№ 92", sectionLabel: "SHARBATI RAW", category: "Rice", enrichedSchema: true,
    seoTitle: "Sharbati Raw Basmati Rice Exporter from India",
    seoDescription: "Sharbati Raw Basmati Rice exporter from India — natural-milled white economical basmati for budget and diaspora retail.",
    h1: "Sharbati Raw Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Sharbati Raw Basmati Rice from India — Vilora Impex",
    eyebrow: "Natural-milled white — budget aromatic",
    standfirst: "Vilora Impex exports Sharbati Raw Basmati Rice — the natural-milled white form of Sharbati, keeping its soft aroma at the lowest basmati price tier. A practical everyday basmati for budget retail and diaspora grocery.",
    introH2: "What Sharbati Raw means",
    introBody: "Raw milling retains aroma and keeps the grain white. Sharbati Raw is aged to improve cook behaviour and is moisture-sensitive — BOPP laminate suits humid markets.",
    productsH2: "Sharbati Raw grades we export", products: [
      { name: "Sharbati Raw — Aged", spec: "7.5–8.0 mm • aged • budget retail" },
      { name: "Sharbati Raw — Fresh Crop", spec: "Current-season supply" },
      { name: "Sharbati Raw — Retail", spec: "5/10 kg packs" },
      { name: "Sharbati Raw — Bulk", spec: "25/50 kg PP" },
      { name: "Custom Sharbati Raw Spec", spec: "Buyer-locked grade" },
    ],
    packagingH2: "Sharbati Raw packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10/25/50 kg PP, BOPP, private label. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship Sharbati Raw", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why retail picks Sharbati Raw", why: WHY_DEFAULT,
    faqsH2: "Sharbati Raw export FAQs", faqs: [
      { q: "Is Sharbati Raw a true basmati?", a: "It is an evolved basmati — aromatic and long-ish grained, but shorter and softer than traditional or 1121 basmati. It is sold honestly as Sharbati, not as 1121." },
      { q: "Markets?", a: "Budget and diaspora retail, African retail." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["sharbati-overview", "sharbati-sella", "sugandha-raw"],
    productSchemaName: "Sharbati Raw Basmati Rice from India", productSchemaDescription: "Sharbati natural-milled white basmati, budget aromatic. FSSAI/APEDA registered.",
  },
  "sharbati-steam": {
    slug: "sharbati-steam-basmati-rice", folio: "№ 93", sectionLabel: "SHARBATI STEAM", category: "Rice", enrichedSchema: true,
    seoTitle: "Sharbati Steam Basmati Rice Exporter from India",
    seoDescription: "Sharbati Steam Basmati Rice exporter from India — steam-finished economical basmati, firm separate grain for catering and value retail.",
    h1: "Sharbati Steam Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "Sharbati Steam Basmati Rice from India — Vilora Impex",
    eyebrow: "Steam-finished — firm catering grain",
    standfirst: "Vilora Impex exports Sharbati Steam Basmati Rice — Sharbati finished with light steam for a firmer, more separate cook, keeping the grain white. The value catering grain for high-volume kitchens and budget retail that need cook consistency.",
    introH2: "What Sharbati Steam means",
    introBody: "Steam treatment firms the grain surface so it cooks separate and holds shape under volume cooking, keeping the grain white with shelf life between Raw and Sella.",
    productsH2: "Sharbati Steam grades we export", products: [
      { name: "Sharbati Steam — Catering", spec: "Holds shape under volume cooking" },
      { name: "Sharbati Steam — Retail", spec: "Bright white firm grain" },
      { name: "Sharbati Steam — Private Label", spec: "5/10 kg BOPP" },
      { name: "Sharbati Steam — Bulk", spec: "25/50 kg PP" },
      { name: "Custom Steam Cuttest", spec: "Buyer-locked spec" },
    ],
    packagingH2: "Sharbati Steam packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10/25/50 kg PP, BOPP, private label. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship Sharbati Steam", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why caterers pick Sharbati Steam", why: WHY_DEFAULT,
    faqsH2: "Sharbati Steam export FAQs", faqs: [
      { q: "Why Steam over Sella for catering?", a: "Steam keeps grain white and cooks closer to fresh-milled while still firm — useful where appearance matters but budget rules out 1121 Steam." },
      { q: "Markets?", a: "Institutional and HoReCa supply, value retail, Africa." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["sharbati-overview", "sharbati-sella", "sugandha-steam"],
    productSchemaName: "Sharbati Steam Basmati Rice from India", productSchemaDescription: "Sharbati steam-finished basmati, firm separate white grain. FSSAI/APEDA registered.",
  },
  "sharbati-sella": {
    slug: "sharbati-sella-basmati-rice", folio: "№ 94", sectionLabel: "SHARBATI SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "Sharbati Sella Basmati Rice Exporter from India",
    seoDescription: "Sharbati Sella Basmati Rice exporter from India — parboiled economical basmati, long shelf life for budget retail, Africa and Gulf.",
    h1: "Sharbati Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "Sharbati Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Parboiled — budget long-shelf retail",
    standfirst: "Vilora Impex exports Sharbati Sella Basmati Rice — the parboiled form of Sharbati and the lowest-priced cook-stable basmati Sella we ship. Long shelf life, low breakage and a budget price make it a backbone SKU for institutional supply and price-led African and Gulf retail.",
    introH2: "What Sharbati Sella means",
    introBody: "Parboiling hardens the grain and lifts tone to cream, giving the most cook-tolerant Sharbati form with 18-month-plus shelf life. The everyday parboiled basmati for the most price-sensitive channels.",
    productsH2: "Sharbati Sella grades we export", products: [
      { name: "Sharbati Sella — Cream", spec: "Light parboil • budget retail" },
      { name: "Sharbati Sella — Standard", spec: "Mid-tone • institutional supply" },
      { name: "Sharbati Sella — Bulk Distributor", spec: "25/50 kg PP" },
      { name: "Sharbati Sella — Blend Base", spec: "Cost-engineering base for retail blends" },
      { name: "Custom Sharbati Sella Cuttest", spec: "Buyer-locked parboil and spec" },
    ],
    packagingH2: "Sharbati Sella packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10 kg BOPP retail, 25/50 kg PP bulk and institutional. Halal for Gulf retail. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship Sharbati Sella", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why value channels pick Sharbati Sella", why: WHY_DEFAULT,
    faqsH2: "Sharbati Sella export FAQs", faqs: [
      { q: "Is Sharbati Sella the cheapest basmati Sella?", a: "It is among the lowest-priced cook-stable basmati Sellas in the trade, which is why it dominates institutional and budget-retail tenders." },
      { q: "Shelf life?", a: "18–22 months in cool, dry storage." },
      { q: "Markets?", a: "West and East Africa, Iraq, Yemen, institutional supply, value Gulf retail." },
    ],
    related: ["sharbati-overview", "sharbati-golden-sella", "sugandha-sella", "parmal-sella"],
    productSchemaName: "Sharbati Sella Basmati Rice from India", productSchemaDescription: "Sharbati parboiled basmati, budget long-shelf retail and institutional. FSSAI/APEDA registered.",
  },
  "sharbati-golden-sella": {
    slug: "sharbati-golden-sella-basmati-rice", folio: "№ 95", sectionLabel: "SHARBATI GOLDEN SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "Sharbati Golden Sella Basmati Rice Exporter from India",
    seoDescription: "Sharbati Golden Sella Basmati Rice exporter from India — deep-golden parboiled economical basmati for budget golden SKUs in Africa and the Gulf.",
    h1: "Sharbati Golden Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "Sharbati Golden Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Deep-golden parboil — budget colour SKU",
    standfirst: "Vilora Impex exports Sharbati Golden Sella Basmati Rice — the deep-golden parboiled form of Sharbati. It puts the premium golden look on the shelf at the lowest price point in our golden range, for African and Gulf retail brands building entry-tier golden SKUs.",
    introH2: "What Sharbati Golden Sella means",
    introBody: "The deepest parboil produces dark-golden grain and the longest shelf life. Sharbati Golden Sella is the budget entry to the golden-Sella category — colour and stability without the premium-variety price.",
    productsH2: "Sharbati Golden Sella grades we export", products: [
      { name: "Sharbati Golden Sella — Deep Gold", spec: "Maximum colour • Africa and Iraq retail" },
      { name: "Sharbati Golden Sella — Mid Tone", spec: "Balanced golden • wholesale" },
      { name: "Sharbati Golden Sella — Private Label", spec: "5/10 kg BOPP" },
      { name: "Sharbati Golden Sella — Bulk", spec: "25/50 kg PP" },
      { name: "Custom Golden Cuttest", spec: "Buyer-locked colour and spec" },
    ],
    packagingH2: "Sharbati Golden Sella packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10 kg BOPP retail, 25/50 kg PP bulk. SFDA/ESMA labelling available. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship Sharbati Golden Sella", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why retail picks Sharbati Golden Sella", why: WHY_DEFAULT,
    faqsH2: "Sharbati Golden Sella export FAQs", faqs: [
      { q: "Who buys Sharbati Golden Sella?", a: "Brands and distributors that want a deep-golden Sella at the lowest possible landed cost — common in West and East Africa and price-led Gulf retail." },
      { q: "Shelf life?", a: "22+ months." },
      { q: "MOQ?", a: "One 20ft FCL." },
    ],
    related: ["sharbati-overview", "sharbati-sella", "sugandha-golden-sella"],
    productSchemaName: "Sharbati Golden Sella Basmati Rice from India", productSchemaDescription: "Sharbati deep-golden parboiled basmati, budget golden SKU. FSSAI/APEDA registered.",
  },

  /* ─── TRADITIONAL BASMATI ──────────────────────────────────── */
  "traditional-overview": {
    slug: "traditional-basmati-rice-exporter", folio: "№ 96", sectionLabel: "TRADITIONAL BASMATI", category: "Rice", enrichedSchema: true,
    seoTitle: "Traditional Basmati Rice Exporter from India",
    seoDescription: "Traditional Basmati Rice exporter from India — original long-grain landrace basmati with intense aroma and dramatic cooked elongation. Premium gourmet supply.",
    h1: "Traditional Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1121-steam.jpg", imageAlt: "Traditional Basmati Rice from India — Vilora Impex",
    eyebrow: "Original landrace — gourmet premium",
    standfirst: "Vilora Impex exports Traditional Basmati Rice — the original landrace basmati prized for intense natural aroma and dramatic cooked elongation. Aged for depth and sold to gourmet retail, fine-dining HoReCa and premium gift-pack brands that want the genuine heritage grain, not an evolved high-yield cultivar.",
    introH2: "What Traditional Basmati is",
    introBody: "Traditional basmati refers to the original pre-evolved landrace varieties (such as Dehraduni-type and CSR lines). The grain is finer than 1121 before cooking but elongates dramatically — often close to twice its length — with a pronounced aroma the evolved varieties only partly carry. Yields are low, so it commands the top of the price range and is almost always aged and sold Raw.",
    productsH2: "Traditional grades we export", products: [
      { name: "Traditional Raw — Premium Aged", spec: "Aged 12–24 months • intense aroma • gourmet retail" },
      { name: "Traditional Raw — Gift Pack Base", spec: "Top-tier appearance for premium gift SKUs" },
      { name: "Traditional Sella", spec: "Parboiled heritage grain • long shelf premium" },
      { name: "Traditional — HoReCa Premium", spec: "Fine-dining biryani and pulao supply" },
      { name: "Custom Traditional Cuttest", spec: "Buyer-locked ageing, grain and aroma spec" },
    ],
    packagingH2: "Traditional packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT); smaller premium trial lots discussed case-by-case. 1/5/10 kg premium BOPP retail and gift packs, 25 kg PP for HoReCa. Private label one FCL per SKU. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship Traditional basmati", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why premium buyers pick Traditional", why: WHY_DEFAULT,
    faqsH2: "Traditional basmati export FAQs", faqs: [
      { q: "How is Traditional different from 1121?", a: "Traditional is the original landrace; 1121 is an evolved long-grain cultivar bred for grain length and yield. Traditional has a finer raw grain, more intense aroma and elongates more on cooking, but yields less and costs more — it is the gourmet, heritage choice." },
      { q: "Why is Traditional more expensive?", a: "Low farm yield and long ageing. It is positioned as a premium gourmet grain, so volumes are smaller and prices sit at the top of the basmati range." },
      { q: "Which form is most common?", a: "Raw, aged. Traditional is bought for aroma and elongation, both of which Raw milling preserves best. Sella exists for premium long-shelf retail." },
      { q: "Which markets buy Traditional?", a: "Premium UK, EU and North-American diaspora retail, Gulf gift-pack and gourmet retail, and fine-dining HoReCa." },
    ],
    related: ["traditional-raw", "traditional-sella", "1121-basmati", "premium-rice"],
    productSchemaName: "Traditional Basmati Rice from India", productSchemaDescription: "Original landrace basmati, intense aroma, dramatic elongation, aged gourmet grade. FSSAI/APEDA registered.",
  },
  "traditional-raw": {
    slug: "traditional-raw-basmati-rice", folio: "№ 97", sectionLabel: "TRADITIONAL RAW", category: "Rice", enrichedSchema: true,
    seoTitle: "Traditional Raw Basmati Rice Exporter from India",
    seoDescription: "Traditional Raw Basmati Rice exporter from India — aged natural-milled landrace basmati with maximum aroma and elongation for gourmet retail.",
    h1: "Traditional Raw Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Traditional Raw Basmati Rice from India — Vilora Impex",
    eyebrow: "Aged natural-milled — maximum aroma",
    standfirst: "Vilora Impex exports Traditional Raw Basmati Rice — the aged, natural-milled form of heritage landrace basmati. This is the grain at its most aromatic and most dramatically elongating: the premium choice for gourmet retail and fine-dining kitchens that cook basmati as the centrepiece.",
    introH2: "What Traditional Raw means",
    introBody: "Raw milling with long ageing (often 12–24 months) gives Traditional basmati its hallmark: a fine white grain that nearly doubles in length on cooking, with an aroma evolved varieties cannot fully match. Moisture-sensitive — premium BOPP recommended.",
    productsH2: "Traditional Raw grades we export", products: [
      { name: "Traditional Raw — 24-Month Aged", spec: "Maximum aroma and elongation • gourmet retail" },
      { name: "Traditional Raw — 12-Month Aged", spec: "Premium retail and HoReCa" },
      { name: "Traditional Raw — Gift Pack", spec: "1/5 kg premium presentation packs" },
      { name: "Traditional Raw — HoReCa", spec: "25 kg PP fine-dining supply" },
      { name: "Custom Traditional Raw Spec", spec: "Buyer-locked ageing and aroma profile" },
    ],
    packagingH2: "Traditional Raw packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT); premium trial lots case-by-case. 1/5/10 kg premium BOPP and gift packs, 25 kg PP HoReCa. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship Traditional Raw", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why gourmet retail picks Traditional Raw", why: WHY_DEFAULT,
    faqsH2: "Traditional Raw export FAQs", faqs: [
      { q: "How much does Traditional Raw elongate?", a: "Well-aged Traditional basmati can elongate close to twice its raw length on cooking, more than most evolved varieties — a key reason gourmet buyers specify it." },
      { q: "How long is it aged?", a: "Typically 12–24 months. Longer ageing deepens aroma and improves elongation; we can supply to a specified ageing band." },
      { q: "Markets?", a: "Premium diaspora retail in the UK, EU and North America, Gulf gourmet and gift retail, fine dining." },
    ],
    related: ["traditional-overview", "traditional-sella", "1121-raw", "premium-rice"],
    productSchemaName: "Traditional Raw Basmati Rice from India", productSchemaDescription: "Aged natural-milled landrace basmati, maximum aroma and elongation. FSSAI/APEDA registered.",
  },
  "traditional-sella": {
    slug: "traditional-sella-basmati-rice", folio: "№ 98", sectionLabel: "TRADITIONAL SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "Traditional Sella Basmati Rice Exporter from India",
    seoDescription: "Traditional Sella Basmati Rice exporter from India — parboiled heritage landrace basmati combining premium aroma with long shelf life.",
    h1: "Traditional Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1509-steam.jpg", imageAlt: "Traditional Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Parboiled heritage grain — premium long shelf",
    standfirst: "Vilora Impex exports Traditional Sella Basmati Rice — the parboiled form of heritage landrace basmati. It pairs the premium provenance and aroma of Traditional basmati with the cook-stability and long shelf life of Sella, for premium retail brands that need both prestige and distribution resilience.",
    introH2: "What Traditional Sella means",
    introBody: "Parboiling the heritage grain hardens it for low breakage and 18-month-plus shelf life while keeping much of the characteristic aroma. A niche premium SKU positioned above value Sellas like 1509 or Sugandha.",
    productsH2: "Traditional Sella grades we export", products: [
      { name: "Traditional Sella — Cream", spec: "Premium parboil • gourmet long-shelf retail" },
      { name: "Traditional Sella — Standard", spec: "Premium Gulf retail private label" },
      { name: "Traditional Sella — HoReCa", spec: "25 kg PP fine-dining supply" },
      { name: "Custom Traditional Sella Cuttest", spec: "Buyer-locked parboil and spec" },
    ],
    packagingH2: "Traditional Sella packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10 kg premium BOPP retail, 25 kg PP HoReCa. Private label one FCL per SKU. Incoterms: FOB, CIF, CFR.",
    portsH2: "Where we ship Traditional Sella", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why premium retail picks Traditional Sella", why: WHY_DEFAULT,
    faqsH2: "Traditional Sella export FAQs", faqs: [
      { q: "Why choose Traditional Sella over 1121 Sella?", a: "Provenance and aroma. Traditional Sella carries the heritage-landrace story and a deeper aroma, positioning above 1121 Sella for premium brands — at a correspondingly higher price." },
      { q: "Shelf life?", a: "18–22 months in cool, dry storage." },
      { q: "Markets?", a: "Premium diaspora and Gulf retail, gourmet HoReCa." },
    ],
    related: ["traditional-overview", "traditional-raw", "1121-sella"],
    productSchemaName: "Traditional Sella Basmati Rice from India", productSchemaDescription: "Parboiled heritage landrace basmati, premium aroma with long shelf life. FSSAI/APEDA registered.",
  },

  /* ─── WHITE SELLA + PUSA RAW (gap fills) ───────────────────── */
  "white-sella": {
    slug: "white-sella-basmati-rice", folio: "№ 99", sectionLabel: "WHITE SELLA", category: "Rice", enrichedSchema: true,
    seoTitle: "White Sella Basmati Rice Exporter from India",
    seoDescription: "White Sella Basmati Rice exporter from India — light-parboiled cream-white 1121 basmati, cook-stable with long shelf life and no golden tint.",
    h1: "White Sella Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1121-steam.jpg", imageAlt: "White Sella Basmati Rice from India — Vilora Impex",
    eyebrow: "Light-parboiled — cream-white, no golden tint",
    standfirst: "Vilora Impex exports White Sella Basmati Rice — the light-parboiled form that delivers Sella cook-stability and long shelf life while keeping a clean cream-white colour, with none of the golden tint. The choice for retail markets that want parboiled durability but prefer the look of white rice on the plate.",
    introH2: "What White Sella means",
    introBody: "White Sella is parboiled with controlled, lighter steam so the grain hardens for low breakage and long shelf life but stays cream-white rather than turning golden. Built on 1121 (and similar long-grain) basmati, it gives the cook-stability of Sella with a white presentation.",
    productsH2: "White Sella grades we export", products: [
      { name: "White Sella — 1121 Base", spec: "8.30 mm+ • cream-white • premium retail" },
      { name: "White Sella — Standard", spec: "Gulf retail private label" },
      { name: "White Sella — Bulk Distributor", spec: "25/50 kg PP for wholesale" },
      { name: "White Sella — UAE Spec", spec: "MOIAT/ESMA-compliant labelled packs" },
      { name: "Custom White Sella Cuttest", spec: "Buyer-locked grain, parboil intensity, broken %" },
    ],
    packagingH2: "White Sella packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10 kg BOPP retail, 25/50 kg PP bulk. Private label one FCL per SKU. Halal for Gulf retail. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship White Sella basmati", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why retail picks White Sella", why: WHY_DEFAULT,
    faqsH2: "White Sella basmati export FAQs", faqs: [
      { q: "What is the difference between White Sella and Golden Sella?", a: "Both are parboiled and cook-stable. White Sella uses lighter steam to keep the grain cream-white; Golden Sella uses deeper parboiling that turns the grain dark golden. The choice is purely the colour your market prefers." },
      { q: "Does White Sella cook like 1121 Sella?", a: "Yes — it is 1121-based parboiled rice, so it has the same firm, separate, non-sticky cook and long shelf life, just in white form." },
      { q: "Which markets prefer White Sella?", a: "Markets where consumers associate quality with bright white rice rather than golden — parts of the Gulf, Europe and North-American diaspora retail." },
      { q: "MOQ?", a: "One 20ft FCL per grade." },
    ],
    related: ["1121-sella", "1121-golden-sella", "1121-basmati", "premium-rice"],
    productSchemaName: "White Sella Basmati Rice from India", productSchemaDescription: "Light-parboiled 1121 basmati, cream-white, cook-stable, long shelf life. FSSAI/APEDA registered.",
  },
  "pusa-raw": {
    slug: "pusa-raw-basmati-rice", folio: "№ 100", sectionLabel: "PUSA RAW", category: "Rice", enrichedSchema: true,
    seoTitle: "Pusa Raw Basmati Rice Exporter from India",
    seoDescription: "Pusa Raw Basmati Rice exporter from India — natural-milled white Pusa Basmati, aromatic long grain for premium and diaspora retail.",
    h1: "Pusa Raw Basmati Rice Exporter from India",
    image: "/hero-rice.jpg", imageAlt: "Pusa Raw Basmati Rice from India — Vilora Impex",
    eyebrow: "Natural-milled white Pusa — aroma-led",
    standfirst: "Vilora Impex exports Pusa Raw Basmati Rice — the natural-milled white form of Pusa Basmati, the IARI-bred long-grain line behind much of India's modern basmati trade. Aromatic, long-grained and aged for cook behaviour, it is a dependable premium-retail and diaspora basmati.",
    introH2: "What Pusa Raw means",
    introBody: "Pusa Basmati is the family of long-grain basmati cultivars developed at the Indian Agricultural Research Institute (IARI), Pusa. Raw is the natural-milled white form, aged to firm the grain and retaining the most aroma. Moisture-sensitive — BOPP laminate suits humid markets.",
    productsH2: "Pusa Raw grades we export", products: [
      { name: "Pusa Raw — Premium Aged", spec: "Long grain • aged 12 months • <2% broken • two-pass sortex" },
      { name: "Pusa Raw — Fresh Crop", spec: "Current-season supply at the milling partner" },
      { name: "Pusa Raw — Diaspora Retail", spec: "5/10 kg packs for South-Asian supermarket retail" },
      { name: "Pusa Raw — Bulk", spec: "25/50 kg PP for wholesale" },
      { name: "Custom Pusa Raw Spec", spec: "Buyer-locked grain length, broken %, polish, ageing" },
    ],
    packagingH2: "Pusa Raw packaging and shipping", packagingBody: "FCL MOQ one 20ft container (~25 MT). 5/10/25/50 kg PP, BOPP laminate, jute or private label. BOPP recommended for humid markets. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship Pusa Raw basmati", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why retail picks Pusa Raw", why: WHY_DEFAULT,
    faqsH2: "Pusa Raw basmati export FAQs", faqs: [
      { q: "What is Pusa Basmati?", a: "Pusa Basmati is the family of long-grain basmati cultivars (including 1121, 1401, 1509 and 1718) developed at IARI, Pusa. Traded simply as Pusa Raw when sold as the general natural-milled white form." },
      { q: "How does Pusa Raw compare to 1121 Raw?", a: "1121 is a specific Pusa cultivar with the longest grain. General Pusa Raw is aromatic long-grain basmati that sits just below 1121 on grain length and price, and is a good value-premium alternative." },
      { q: "Which markets buy Pusa Raw?", a: "Diaspora retail in the UK, Canada and US, and premium Gulf retail." },
    ],
    related: ["pusa-basmati-supply", "pusa-steam", "pusa-sella", "1121-raw"],
    productSchemaName: "Pusa Raw Basmati Rice from India", productSchemaDescription: "Natural-milled white Pusa basmati, aromatic long grain, aged. FSSAI/APEDA registered.",
  },

  /* ─── PESTICIDE-FREE / RESIDUE-TESTED RICE HUB ─────────────── */
  "pesticide-free": {
    slug: "pesticide-free-basmati-rice-exporter", folio: "№ 101", sectionLabel: "PESTICIDE-FREE RICE", category: "Rice", enrichedSchema: true,
    seoTitle: "Pesticide-Free Basmati Rice Exporter from India",
    seoDescription: "Pesticide-free, residue-tested basmati & non-basmati rice exporter from India — every lot lab-tested against destination MRL limits (EU, GCC). FSSAI/APEDA registered.",
    h1: "Pesticide-Free Basmati Rice Exporter from India",
    image: "/catalogue/rice-basmati-1121-steam.jpg", imageAlt: "Pesticide-free residue-tested basmati rice from India — Vilora Impex",
    eyebrow: "Residue-tested to destination MRL limits",
    standfirst: "Vilora Impex supplies pesticide-free, residue-tested rice — basmati and non-basmati lots screened by accredited labs against the maximum residue limits (MRLs) of the destination market before they ship. For importers into the EU, GCC and other strict-MRL regions, this is the difference between a clean port clearance and a rejected container.",
    introH2: "What pesticide-free means in practice",
    introBody: "There is no commercial rice grown with zero inputs at scale, so honest exporters do not claim it. What we provide is residue management: lots sourced from controlled-input fields and then lab-tested against the importing country's MRL schedule (for example the EU MRL list or GCC standards). Each shipment carries its residue analysis report, so your customs and food-safety clearance is backed by data, not a marketing word.",
    productsH2: "Residue-tested rice we supply", products: [
      { name: "Pesticide-Free 1121 Basmati", spec: "Raw / Steam / Sella • EU & GCC MRL-tested" },
      { name: "Pesticide-Free 1509 Basmati", spec: "Value-tier • residue report per lot" },
      { name: "Pesticide-Free Sona Masoori", spec: "Non-basmati • MRL-tested for EU retail" },
      { name: "Pesticide-Free Non-Basmati (IR64/Parmal)", spec: "Bulk • residue-screened" },
      { name: "Low-Residue Programme", spec: "Buyer-locked MRL schedule + lab certificate per shipment" },
      { name: "Custom MRL Spec", spec: "Tested to your destination's exact residue limits" },
    ],
    packagingH2: "Pesticide-free rice packaging and documentation", packagingBody: "FCL MOQ one 20ft container (~25 MT). Standard 5/10/25/50 kg packing plus a per-lot residue analysis certificate from an accredited lab, phyto, FSSAI and APEDA documentation. We agree the MRL schedule to test against before milling. Incoterms: FOB Mundra/Kandla, CIF, CFR.",
    portsH2: "Where we ship residue-tested rice", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why importers specify residue-tested rice", why: [
      "Per-lot residue analysis against your destination MRL schedule (EU, GCC, others)",
      "Lab certificate couriered with the documentation packet — clears strict food-safety inspection",
      "Sourced from controlled-input fields, not open-market mixed lots",
      "FSSAI licensed, APEDA registered, ISO 22000 — full traceability to the milling lot",
      "Sample-and-approve before commit; the tested spec is locked into the contract",
      "Reduces the single biggest import risk: a container rejected at port for residue breach",
    ],
    faqsH2: "Pesticide-free rice export FAQs", faqs: [
      { q: "Is your rice genuinely pesticide-free?", a: "We supply residue-tested rice: lots screened by accredited labs against your destination's MRL limits, with the certificate shipped per lot. We do not claim zero-input organic unless a lot is specifically certified organic — residue-tested to MRL is the honest, verifiable standard most serious importers actually need." },
      { q: "Which MRL standards do you test against?", a: "Whichever applies to your market — most commonly the EU MRL schedule and GCC/Gulf standards. We agree the exact residue list before milling so the lot is tested against the right limits." },
      { q: "Do I get the lab report with my shipment?", a: "Yes. A per-lot residue analysis certificate from an accredited laboratory travels with the phyto, FSSAI, COO and Bill of Lading in the documentation packet." },
      { q: "Can you supply certified organic rice as well?", a: "For genuinely certified-organic requirements we can discuss a certified-organic lot with the corresponding certification chain. That is separate from, and more involved than, standard residue-tested supply." },
      { q: "What is the MOQ?", a: "One 20ft FCL (~25 MT) per spec." },
    ],
    related: ["1121-basmati", "1509-basmati", "sona-masuri", "certifications", "rice-supplier-india"],
    productSchemaName: "Pesticide-Free Residue-Tested Rice from India", productSchemaDescription: "Basmati and non-basmati rice lab-tested against destination MRL limits, with per-lot residue certificate. FSSAI/APEDA/ISO 22000.",
  },

  /* ─── PRICE-GUIDE PAGES ────────────────────────────────────── */
  "basmati-price": {
    slug: "basmati-rice-price", folio: "№ 102", sectionLabel: "BASMATI PRICE", category: "Rice", enrichedSchema: true,
    aggregateLowPrice: "700", aggregateHighPrice: "1700",
    seoTitle: "Basmati Rice Price from India — FOB Indication by Variety",
    seoDescription: "Basmati rice price guide from India — indicative FOB price bands for 1121, 1509, 1401, Sugandha & Sharbati basmati by form. Live quote same day from Vilora Impex.",
    h1: "Basmati Rice Price from India",
    image: "/catalogue/rice-basmati-1121-steam.jpg", imageAlt: "Basmati rice price guide from India — Vilora Impex",
    eyebrow: "Indicative FOB bands — live quote same day",
    standfirst: "What does basmati rice cost to import from India? It depends on the variety, the processing form and the season. Below is an indicative FOB price guide by variety so you can budget — but the real number is the one we quote you the same day against your spec, quantity, destination port and payment terms.",
    introH2: "How basmati pricing works",
    introBody: "Basmati is priced per metric tonne, FOB Mundra or Kandla, and moves with three things: the variety (1121 commands the top, Sharbati and Sugandha the value end), the form (Golden Sella and aged Raw cost more than plain Sella), and the crop season. Published bands are indicative; a firm price is locked at contract against your exact cuttest, packing and L/C or advance terms.",
    productsH2: "Indicative basmati FOB price bands (per MT)", products: [
      { name: "1121 Steam / Raw (premium)", spec: "Top-tier long grain • higher band" },
      { name: "1121 Golden Sella", spec: "Premium colour • upper-mid band" },
      { name: "1509 Sella / Golden Sella", spec: "Value-tier parboiled • mid band" },
      { name: "1401 / Pusa basmati", spec: "Value-premium aromatic • mid band" },
      { name: "Sugandha / Sharbati", spec: "Economical aromatic • lower band" },
      { name: "Your exact quote", spec: "Same-day, against spec + port + terms" },
    ],
    packagingH2: "What moves the price on your quote", packagingBody: "Packing (printed retail BOPP costs more than plain PP), private-label artwork, Incoterm (FOB vs CIF/CFR adds freight and insurance), payment terms (advance vs L/C), and order size (full FCL vs part-load consolidation). Tell us all five and we quote a firm number the same day.",
    portsH2: "Pricing basis and shipping", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why buyers ask Vilora for a basmati quote", why: WHY_DEFAULT,
    faqsH2: "Basmati rice price FAQs", faqs: [
      { q: "Can you publish exact basmati prices?", a: "Not as a fixed figure — basmati FOB prices move weekly with crop, variety and currency, and the real price depends on your spec, packing and payment terms. We publish indicative bands and quote a firm price the same day you send your requirement." },
      { q: "Which basmati is cheapest to import?", a: "Among aromatic basmatis, Sharbati and Sugandha sit at the value end, then 1509 and 1401, with 1121 at the premium end. Plain Sella is cheaper than Golden Sella or aged Raw within any variety." },
      { q: "What is the price difference between 1121 and 1509?", a: "1121 typically runs 15–25% above 1509 per MT for the equivalent form, reflecting its longer grain and stronger aroma." },
      { q: "How do I get a firm quote?", a: "Send variety, form, quantity, destination port, packing and payment terms to exports@viloraimpex.com — we reply the same day with a firm FOB or CIF price and a sample plan." },
    ],
    related: ["1121-basmati", "1509-basmati", "1401-overview", "sugandha-overview", "rice-supplier-india", "indian-rice-price"],
    productSchemaName: "Basmati Rice from India — FOB Price Indication", productSchemaDescription: "Indicative FOB basmati price bands by variety and form; firm same-day quote against spec. FSSAI/APEDA registered.",
  },
  "indian-rice-price": {
    slug: "indian-rice-price", folio: "№ 103", sectionLabel: "INDIAN RICE PRICE", category: "Rice", enrichedSchema: true,
    aggregateLowPrice: "420", aggregateHighPrice: "1700",
    seoTitle: "Indian Rice Price — FOB Guide for Basmati & Non-Basmati",
    seoDescription: "Indian rice price guide — indicative FOB bands for basmati and non-basmati rice (1121, 1509, Sona Masoori, IR64, Parmal) by form. Same-day quote from Vilora Impex.",
    h1: "Indian Rice Price — Export FOB Guide",
    image: "/hero-rice.jpg", imageAlt: "Indian rice price guide from India — Vilora Impex",
    eyebrow: "Indicative FOB bands — basmati & non-basmati",
    standfirst: "An indicative FOB price guide for Indian rice — both basmati and non-basmati — so importers can budget before they ask for a quote. Non-basmati sits at the value end, basmati spans the mid-to-premium range. Every firm price is quoted same day against your spec, quantity, port and terms.",
    introH2: "How Indian rice is priced for export",
    introBody: "All export rice is priced per metric tonne, FOB Mundra or Kandla. Non-basmati varieties (IR64, Parmal, Sona Masoori) occupy the value end; basmati spans mid to premium, with 1121 at the top. Form (Raw, Steam, Sella, Golden Sella), crop season, packing and Incoterm all move the final figure.",
    productsH2: "Indicative Indian rice FOB bands (per MT)", products: [
      { name: "Non-Basmati (IR64 / Parmal)", spec: "Bulk staple • value band" },
      { name: "Sona Masoori", spec: "Medium-grain non-basmati • value-mid band" },
      { name: "Sugandha / Sharbati basmati", spec: "Economical aromatic • lower-mid band" },
      { name: "1509 / 1401 basmati", spec: "Value-premium basmati • mid band" },
      { name: "1121 basmati", spec: "Premium long grain • upper band" },
      { name: "Your exact quote", spec: "Same-day, against spec + port + terms" },
    ],
    packagingH2: "What determines your final price", packagingBody: "Variety and form set the base; then packing (printed BOPP vs plain PP), private label, Incoterm (FOB vs CIF/CFR), payment terms and order size adjust it. Share these and we quote a firm number the same day.",
    portsH2: "Pricing basis and shipping", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "Why buyers price their rice programme with Vilora", why: WHY_DEFAULT,
    faqsH2: "Indian rice price FAQs", faqs: [
      { q: "What is the cheapest Indian rice to import?", a: "Non-basmati staples such as IR64 and Parmal are the lowest-priced per MT, followed by Sona Masoori. Basmati costs more, with 1121 at the premium end." },
      { q: "Why can't you list one fixed price?", a: "Export rice prices move with crop season, variety, currency and your specific spec, packing and payment terms. We give indicative bands here and a firm same-day quote once you send your requirement." },
      { q: "Do prices include freight?", a: "Published bands are FOB (rice loaded at the Indian port). CIF or CFR quotes add ocean freight and, for CIF, insurance to your destination port — we can quote either." },
      { q: "How do I get a firm quote?", a: "Email variety, form, quantity, destination port, packing and payment terms to exports@viloraimpex.com for a same-day firm price." },
    ],
    related: ["basmati-price", "non-basmati", "rice-exporter", "sona-masuri", "rice-supplier-india"],
    productSchemaName: "Indian Rice — FOB Price Indication", productSchemaDescription: "Indicative FOB price bands for Indian basmati and non-basmati rice; firm same-day quote. FSSAI/APEDA registered.",
  },

  /* ─── RICE SUPPLIER IN INDIA — AUTHORITY HUB ───────────────── */
  "rice-supplier-india": {
    slug: "rice-supplier-in-india", folio: "№ 104", sectionLabel: "RICE SUPPLIER", category: "Rice", enrichedSchema: true,
    seoTitle: "Rice Supplier in India | Basmati & Non-Basmati Export — Vilora Impex",
    seoDescription: "Vilora Impex is a rice supplier in India exporting basmati and non-basmati rice worldwide from Gujarat — 1121, 1509, 1401, Sugandha, Sharbati, Sona Masoori, IR64. FSSAI/APEDA registered.",
    h1: "Rice Supplier in India",
    image: "/hero-rice.jpg", imageAlt: "Rice supplier in India — Vilora Impex, Gujarat",
    eyebrow: "Basmati & non-basmati — direct from Gujarat",
    standfirst: "Vilora Impex is a rice supplier in India exporting the full basmati and non-basmati range to buyers worldwide. From a counter in Rajkot to containers at Mundra, we supply graded, sortex-cleaned, documented rice — sourced direct from milling partners in Punjab, Haryana, Andhra Pradesh and Telangana, and shipped with a clean paper trail and live tracking.",
    introH2: "Why buyers choose Vilora as their India rice supplier",
    introBody: "A reliable rice supplier in India is judged on three things: consistent grade lot-to-lot, clean documentation that clears customs, and answers in the buyer's timezone. Vilora supplies every major variety and form, hand-grades at origin, lab-tests to your spec, and couriers the full document packet within 48 hours of sail — the discipline of a fifteen-year retail-and-wholesale house applied to export.",
    productsH2: "The rice range we supply", products: [
      { name: "1121 Basmati", spec: "Raw / Steam / Sella / Golden Sella — premium long grain" },
      { name: "1509 / 1401 Basmati", spec: "Value-premium aromatic, all forms" },
      { name: "Sugandha / Sharbati / Traditional", spec: "Economical to gourmet basmati" },
      { name: "Pusa & 1718 Basmati", spec: "IARI long-grain cultivars" },
      { name: "Sona Masoori, IR64, Parmal, PR11/PR14", spec: "Non-basmati staples, all forms" },
      { name: "Pesticide-free / residue-tested rice", spec: "Lab-tested to destination MRL" },
    ],
    packagingH2: "Supply terms, packing and private label", packagingBody: "FCL MOQ one 20ft container (~25 MT) per spec. 5/10/25/50 kg PP, BOPP-printed laminate, jute, cotton or buyer-branded private label (one FCL per SKU). Halal for Gulf retail. Incoterms: FOB Mundra/Kandla, CIF, CFR. Payment: 30% advance + 70% against BL scan, or irrevocable L/C at sight.",
    portsH2: "Where we supply rice from India", portsBody: PORTS_DEFAULT, process: PROCESS_DEFAULT,
    whyH2: "What makes Vilora a dependable rice supplier", why: WHY_DEFAULT,
    faqsH2: "Rice supplier in India — FAQs", faqs: [
      { q: "What rice varieties do you supply from India?", a: "The full range: basmati (1121, 1509, 1401, 1718, Pusa, Sugandha, Sharbati, Traditional) in Raw, Steam, Sella and Golden Sella forms, and non-basmati (Sona Masoori, IR64, Parmal, PR11, PR14). We also supply residue-tested pesticide-free lots." },
      { q: "What is your minimum order?", a: "One 20ft FCL (~25 MT) per specification. Private label is one FCL per SKU. Mixed-variety consolidation can be discussed for trial orders." },
      { q: "Which markets do you supply?", a: "The Gulf, wider Middle East, East and West Africa, the Maldives, and South-East Asia — through Jebel Ali, Dammam, Doha, Salalah, Mombasa, Dar es Salaam, Malé, Singapore, Port Klang and more." },
      { q: "Are you FSSAI and APEDA registered?", a: "Yes — FSSAI licensed, APEDA registered and ISO 22000, with full phyto/COO/BL documentation couriered within 48 hours of sail." },
      { q: "How fast do you quote?", a: "Same day in our timezone. Send variety, form, quantity, destination port, packing and payment terms to exports@viloraimpex.com." },
    ],
    related: ["rice-exporter", "1121-basmati", "1509-basmati", "1401-overview", "sugandha-overview", "non-basmati", "sona-masuri", "pesticide-free", "basmati-price"],
    productSchemaName: "Rice Supplier in India — Vilora Impex", productSchemaDescription: "India rice supplier exporting basmati and non-basmati rice worldwide from Gujarat. FSSAI/APEDA/ISO 22000.",
  },
};

/** Build the JSON-LD @graph for a single landing page.
 *
 *  Pages with `enrichedSchema: true` get the full 2026 schema stack:
 *  WebPage + Product + Service + ImageObject + BreadcrumbList + FAQPage +
 *  Speakable + founder/Person mentions. Older entries (added before this
 *  module learned the trick) get the original lean shape — change is
 *  deliberately additive so existing routes' prerendered output stays
 *  byte-identical until they opt in.
 */
function buildLandingJsonLd(key) {
  const cfg = LANDINGS[key];
  if (!cfg) return null;
  const url = `${SITE}/${cfg.slug}`;
  const imageUrl = `${SITE}${cfg.image || "/og.jpg"}`;

  const graph = [
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      "url": url,
      "name": cfg.seoTitle,
      "isPartOf": { "@id": `${SITE}/#website` },
      "about": { "@id": ORG_REF },
      "description": cfg.seoDescription,
      "inLanguage": "en",
      ...(cfg.enrichedSchema ? {
        "primaryImageOfPage": { "@id": `${url}#hero-image` },
        "speakable": {
          "@type": "SpeakableSpecification",
          "cssSelector": [".landing-h1", ".landing-standfirst", ".landing-faq-q", ".landing-faq-a"],
        },
        "mentions": { "@id": FOUNDER_REF },
        "datePublished": cfg.datePublished || "2026-05-20",
        "dateModified": cfg.dateModified || "2026-05-20",
      } : {}),
    },
    {
      "@type": "Product",
      "@id": `${url}#product`,
      "name": cfg.productSchemaName,
      "description": cfg.productSchemaDescription,
      "brand": { "@id": ORG_REF },
      "category": cfg.sectionLabel,
      "image": imageUrl,
      "offers": {
        "@type": "Offer",
        "url": url,
        "availability": "https://schema.org/InStock",
        "priceCurrency": "USD",
        "seller": { "@id": ORG_REF },
        ...(cfg.enrichedSchema ? {
          "priceSpecification": {
            "@type": "PriceSpecification",
            "priceCurrency": "USD",
            "valueAddedTaxIncluded": false,
          },
          "businessFunction": "https://purl.org/goodrelations/v1#Sell",
          "eligibleCustomerType": "http://purl.org/goodrelations/v1#Business",
          "areaServed": AREA_SERVED.map((c) => ({ "@type": "Country", "name": c })),
          "deliveryLeadTime": { "@type": "QuantitativeValue", "minValue": 10, "maxValue": 14, "unitCode": "DAY" },
        } : {}),
      },
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": cfg.enrichedSchema && cfg.category
        ? [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE}/` },
            { "@type": "ListItem", "position": 2, "name": cfg.category, "item": `${SITE}/catalogue` },
            { "@type": "ListItem", "position": 3, "name": cfg.h1, "item": url },
          ]
        : [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE}/` },
            { "@type": "ListItem", "position": 2, "name": cfg.h1, "item": url },
          ],
    },
    {
      "@type": "FAQPage",
      "@id": `${url}#faqs`,
      "mainEntity": cfg.faqs.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    },
  ];

  if (cfg.enrichedSchema) {
    // AggregateOffer — anchors the variants in cfg.products as a
    // priced offer band. priceRange is intentionally wide (B2B export
    // pricing varies by grade, season and L/C terms); Google uses the
    // entity for B2B rich results without requiring an exact figure.
    graph.push({
      "@type": "AggregateOffer",
      "@id": `${url}#aggregate-offer`,
      "offerCount": cfg.products.length,
      "priceCurrency": "USD",
      "lowPrice": cfg.aggregateLowPrice || "650",
      "highPrice": cfg.aggregateHighPrice || "2400",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "priceCurrency": "USD",
        "unitText": "MT",
        "referenceQuantity": { "@type": "QuantitativeValue", "value": 1, "unitCode": "TNE" },
      },
      "offeredBy": { "@id": ORG_REF },
      "availability": "https://schema.org/InStock",
      "areaServed": AREA_SERVED.map((c) => ({ "@type": "Country", "name": c })),
      "eligibleQuantity": { "@type": "QuantitativeValue", "minValue": 25, "unitCode": "TNE" },
    });

    // Service schema — high-leverage for B2B exporters. Anchored to the
    // Organization as provider, areaServed expanded to 15+ countries,
    // hasOfferCatalog pointing to the on-page Product.
    graph.push({
      "@type": "Service",
      "@id": `${url}#service`,
      "name": `${cfg.h1} — Service`,
      "serviceType": cfg.sectionLabel,
      "provider": { "@id": ORG_REF },
      "areaServed": AREA_SERVED.map((c) => ({ "@type": "Country", "name": c })),
      "audience": {
        "@type": "BusinessAudience",
        "audienceType": "Wholesale buyers, importers, distributors, HoReCa chains, private-label brands",
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": cfg.productSchemaName,
        "itemListElement": cfg.products.map((p, i) => ({
          "@type": "Offer",
          "position": i + 1,
          "itemOffered": { "@type": "Product", "name": p.name, "description": p.spec },
        })),
      },
      "termsOfService": `${SITE}/`,
      "description": cfg.productSchemaDescription,
    });

    // ImageObject for the hero — gives Google a concrete, location-tagged
    // primary image to surface in Image SERPs and rich results.
    graph.push({
      "@type": "ImageObject",
      "@id": `${url}#hero-image`,
      "contentUrl": imageUrl,
      "url": imageUrl,
      "caption": cfg.imageAlt || cfg.h1,
      "name": cfg.h1,
      "description": cfg.imageAlt || cfg.h1,
      "creator": { "@id": ORG_REF },
      "copyrightHolder": { "@id": ORG_REF },
      "contentLocation": {
        "@type": "Place",
        "name": "Rajkot, Gujarat, India",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Rajkot",
          "addressRegion": "Gujarat",
          "addressCountry": "IN",
        },
        "geo": { "@type": "GeoCoordinates", "latitude": 22.2587, "longitude": 71.1924 },
      },
      "license": `${SITE}/`,
      "acquireLicensePage": `${SITE}/`,
      "creditText": "Vilora Impex",
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

export { LANDINGS, buildLandingJsonLd };
